<template>
  <section
    class="case-prep-buddy"
    :class="{
      'case-prep-buddy--exposure-review': currentStep === 'addressExposure',
      'case-prep-buddy--validation': currentStep === 'validation'
    }"
  >
    <header class="page-header">
      <div>
        <p class="eyebrow">Guided workflow</p>
        <h1><button type="button" class="page-header__title-button" @click="goToRootIntake({ keepMessages: true })">Blockchain Case Builder</button></h1>
        <p class="lede">
          Assemble what you've got to begin your 
          <GlossaryTerm :entry="glossary.blockchainAnalysis" />.
        </p>
      </div>
     <!-- <div class="page-header__badge">Free To Use!</div>-->
    </header>

   <Teleport to="body">
  <transition-group
    name="notice-stack"
    tag="div"
    class="notice-stack"
    :style="{ top: `calc(${noticeTopOffset}px + env(safe-area-inset-top, 0px))` }"
  >
    <div
      v-if="flashMessage"
      key="flash"
      class="alert alert--success alert--floating"
      role="status"
      aria-live="polite"
    >
      <span class="alert__text">{{ flashMessage }}</span>
      <button
        type="button"
        class="alert__close"
        aria-label="Dismiss message"
        @click="dismissFlash"
      >
        ×
      </button>
    </div>

    <div
      v-if="errorMessage"
      key="error"
      class="alert alert--error alert--floating"
      role="alert"
      aria-live="assertive"
    >
      <span class="alert__text">{{ errorMessage }}</span>
      <button
        type="button"
        class="alert__close"
        aria-label="Dismiss error"
        @click="dismissError"
      >
        ×
      </button>
    </div>
  </transition-group>
</Teleport>
    <section v-if="loading" class="panel panel--centered">
      <h2>Loading Case Prep Buddy</h2>
      <p>Checking whether there is a prep run available to resume.</p>
    </section>
<section v-else-if="!prepRun" class="panel launch-panel">
  <div class="launch-panel__copy">
    <p class="eyebrow">Welcome</p>

    <template v-if="lastRunPreview">
      <h2>Resume your last prep run</h2>
      <p>
        Pick up where you left off
      </p>
    </template>

    <template v-else>
      <h2>Start a new run</h2>
      <p>
        This wizard will help you put together your case so it's ready for investigations.
      </p>
    </template>
  </div>

  <div v-if="lastRunPreview" class="resume-card resume-card--featured">
    <h3>Resumable local run found</h3>
    <p><strong>Title:</strong> {{ lastRunPreview.draftTitle || 'Untitled prep run' }}</p>
    <p><strong>Updated:</strong> {{ formatTimestamp(lastRunPreview.updatedAt) }}</p>
    <p>
      <strong>Contents:</strong>
      {{ lastRunPreview.sourceCount }} source item<span v-if="lastRunPreview.sourceCount !== 1">s</span>,
      {{ lastRunPreview.transactionCount }} staged transaction<span v-if="lastRunPreview.transactionCount !== 1">s</span>
    </p>
  </div>

  <div v-if="lastRunPreview && !confirmingFreshStart" class="launch-panel__actions launch-panel__actions--stacked">
    <button
      type="button"
      class="btn btn--primary"
      data-track="case_prep_resume_last"
      @click="resumeLastRun"
    >
      Resume last run
    </button>

    <button
      type="button"
      class="btn btn--ghost"
      data-track="case_prep_request_start_fresh"
      @click="requestStartNewRun"
    >
      Start a fresh run instead
    </button>
  </div>

  <div v-else-if="lastRunPreview && confirmingFreshStart" class="launch-warning">
    <h3>Start a fresh run instead?</h3>
    <p>
      A resumable local prep run already exists in this browser. Starting a fresh run will replace it
      as the active local run.
    </p>

    <div class="launch-panel__actions launch-panel__actions--stacked">
      <button
        type="button"
        class="btn btn--primary"
        data-track="case_prep_resume_last"
        @click="resumeLastRun"
      >
        Resume last run
      </button>

      <button
        type="button"
        class="btn btn--danger"
        data-track="case_prep_confirm_start_fresh"
        @click="confirmStartNewRun"
      >
        Yes, start a fresh run
      </button>

      <button
        type="button"
        class="btn btn--ghost"
        @click="cancelFreshStart"
      >
        Cancel
      </button>
    </div>
  </div>

  <div v-else class="launch-panel__actions">
    <button
      type="button"
      class="btn btn--primary"
      data-track="case_prep_start_new"
      @click="startNewRun"
    >
      Start a new run
    </button>
  </div>
</section>

    <div
      v-else
      class="workspace"
      :class="{
        'workspace--single-column': !showPrepSummaryTray,
        'workspace--exposure-review': currentStep === 'addressExposure',
        'workspace--validation': currentStep === 'validation'
      }"
    >
      <section ref="workspaceMain" class="workspace__main">
        <div class="workspace-toolbar">
          <div class="workspace-toolbar__left">
            <button v-if="canGoBack" type="button" class="btn btn--ghost" @click="goBack">
              Back
            </button>
            <button type="button" class="btn btn--ghost" @click="goToRootIntake">
              What do you have?
            </button>
            <button v-if="hasRunContent" type="button" class="btn btn--ghost" @click="openReview">
              Review prep run
            </button>
          </div>
          <span class="workspace-toolbar__meta">Run ID: {{ prepRun.id }}</span>
        </div>

        <section v-if="currentStep === 'intake'" class="panel">
          <div class="panel__header">
            <div>
              <p class="eyebrow">Root intake</p>
              <h2>What do you have?</h2>
              <p>
                Below are some common starting points for building your case.   You can come back here
                after adding something, and your case will stay intact.
              </p>
            </div>
          </div>

          <div v-if="hasPendingValidation" class="validation-warning validation-warning--strong">
            You still have {{ validationBatches.length }} candidate batch<span v-if="validationBatches.length !== 1">es</span> waiting in shared validation.
            <button type="button" class="btn btn--ghost btn--small" @click="resumeValidation">
              Resume validation
            </button>
          </div>

          <div v-if="shouldShowReturnToIntakePrompt" class="intake-banner">
            <div>
              <strong>Is this all of it?</strong>
              <p>
                You already have {{ prepRun.sourceItems.length }} source item<span v-if="prepRun.sourceItems.length !== 1">s</span>
                and {{ prepRun.stagedTransactions.length }} staged transaction<span v-if="prepRun.stagedTransactions.length !== 1">s</span>
                in this prep run.
              </p>
            </div>
            <div class="intake-banner__actions">
              <button type="button" class="btn btn--primary" @click="openReview">
                Review current prep run
              </button>
            <button type="button" class="btn btn--ghost" @click="handleKeepAddingSources">
              Keep adding sources
            </button> 
            </div>
          </div>

          <div ref="intakeSourceList" class="source-grid source-grid--intake">
            <button
              v-for="option in sourceOptions"
              :key="option.id"
              type="button"
              class="source-card"
              :data-track="`case_prep_select_${option.id}`"
              @click="selectSourceOption(option)"
            >
              <span class="source-card__badge">{{ option.badge }}</span>
              <h3>{{ option.title }}</h3>
              <p>{{ option.description }}</p>
            </button>
          </div>
        </section>

        <section v-else-if="currentStep === 'unsure'" class="panel">
          <div class="panel__header">
            <div>
              <p class="eyebrow">Layered help</p>
              <h2>I don't know yet</h2>
              <p>
               That's okay if you're not sure where to begin. Let's learn more about 
               
          <GlossaryTerm :entry="glossary.blockchainAnalysis" />.
              </p>
            </div>
          </div>

          <article class="education-card">
            <p class="education-card__level">
              Layer {{ educationLevel + 1 }} of {{ educationStages.length }}
            </p>
            <h3>{{ currentEducationStage.title }}</h3>
            <p>{{ currentEducationStage.summary }}</p>
            <ul>
              <li v-for="point in currentEducationStage.points" :key="point">{{ point }}</li>
            </ul>
          </article>

          <div class="inline-glossary">
            <GlossaryTerm :entry="glossary.address" />
            <GlossaryTerm :entry="glossary.addressHash" />
            <GlossaryTerm :entry="glossary.transaction" />
            <GlossaryTerm :entry="glossary.transactionHash" />
            <GlossaryTerm :entry="glossary.blockchain" />
          </div>

          <div class="panel__actions">
            <button
              type="button"
              class="btn btn--primary"
              :disabled="educationLevel >= educationStages.length - 1"
              @click="advanceEducation"
            >
              I still need help
            </button>
            <button type="button" class="btn btn--ghost" @click="recordEducationAndReturn">
              I understand enough to go back
            </button>
          </div>
        </section>

        <section v-else-if="currentStep === 'addressIntent'" class="panel">
          <div class="panel__header">
            <div>
              <p class="eyebrow">Address intake</p>
              <h2>I have an address</h2>
              <p>
                Choose whether you want to review the 
          <GlossaryTerm :entry="glossary.address" /> itself or use it as the start of a
                trace.
              </p>
            </div>
          </div>

          <div class="intent-grid">
            
            <button
              type="button"
              class="intent-card"
              data-track="case_prep_address_forward"
              @click="moveToStep('addressForward')"
            >
              <h3>Follow funds forward from this address</h3>
              <p>
                Use an address hash to figure out what to add to the case.
              </p>
            </button>
            <button
              type="button"
              class="intent-card"
              data-track="case_prep_address_exposure"
              @click="moveToStep('addressExposure')"
            >
        <h3>Review address / entity exposures</h3>
<p>
  Identify what an address or entity is associated with and who it interacts with.
</p>
            </button>

          </div>
        </section>
<section v-else-if="currentStep === 'document'" class="panel">
  <div class="panel__header">
    <div>
      <p class="eyebrow">Local document intake</p>
      <h2>I have a document</h2>
      <p>
        Add PDFs, DOCX files, or text-based documents. They are read locally in the browser and are not uploaded.
      </p>
    </div>
  </div>

  <form class="form-grid" @submit.prevent="handleDocumentSourceSubmit">
    <div class="field field--full">
      <span>Documents</span>

      <div
        class="screenshot-dropzone"
        :class="{ 'screenshot-dropzone--dragging': documentIntake.isDragging }"
        @dragenter.prevent="handleDocumentDragEnter"
        @dragover.prevent="handleDocumentDragOver"
        @dragleave.prevent="handleDocumentDragLeave"
        @drop.prevent="handleDocumentDrop"
      >
        <p>Drag PDF, DOCX, TXT, CSV, TSV, or JSON files here</p>
        <input
          ref="documentFileInput"
          type="file"
          multiple
          accept=".pdf,.docx,.txt,.md,.markdown,.json,.log,.csv,.tsv,text/*,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          @change="handleDocumentFileSelection"
        />
      </div>
    </div>

    <div v-if="documentIntake.files.length" class="field field--full">
      <div class="screenshot-file-grid">
        <article
          v-for="file in documentIntake.files"
          :key="file.id"
          class="screenshot-file-card"
        >
          <div class="screenshot-file-card__body">
            <strong>{{ file.name }}</strong>
            <span>{{ file.kindLabel }} • {{ formatBytes(file.size) }}</span>
            <span>{{ file.readStatusLabel }}</span>
          </div>

          <button
            type="button"
            class="btn btn--ghost btn--sm"
            @click="removeDocumentFile(file.id)"
          >
            Remove
          </button>
        </article>
      </div>
    </div>

    <div class="panel__actions">
      <button
        type="button"
        class="btn btn--secondary"
        :disabled="!documentIntake.files.length"
        @click="readDocumentsLocally"
      >
        Read documents locally
      </button>

      <button
        type="button"
        class="btn btn--ghost"
        :disabled="!documentIntake.files.length"
        @click="clearDocumentFiles"
      >
        Clear files
      </button>
    </div>

    <div
      v-if="documentIntake.readState !== 'idle' || documentIntake.engineStatus === 'loading'"
      class="ocr-progress-card field--full"
      :class="{ 'ocr-progress-card--error': documentIntake.readState === 'error' || documentIntake.engineStatus === 'error' }"
    >
      <div class="ocr-progress-card__header">
        <strong>{{ documentIntake.statusMessage || 'Preparing local document readers' }}</strong>
        <span>{{ documentIntake.readProgress }}%</span>
      </div>
      <progress max="100" :value="documentIntake.readProgress"></progress>
      <p v-if="documentIntake.currentFileName">
        Current file: {{ documentIntake.currentFileName }}
      </p>
    </div>

    <label class="field field--full">
      <span>Pasted document text</span>
      <textarea
        v-model="placeholderForms.document.manualExtractedText"
        rows="6"
        placeholder="Paste any text you already copied from a document. We will look for transaction hashes and addresses."
      ></textarea>
    </label>

    <label class="field field--full">
      <span>Transaction hashes found</span>
      <textarea
        v-model="placeholderForms.document.knownTransactionsText"
        rows="5"
        placeholder="Transaction hashes found from documents will appear here. Edit freely before continuing."
      ></textarea>
    </label>

    <label class="field field--full">
      <span>Addresses found</span>
      <textarea
        v-model="placeholderForms.document.extractedAddressesText"
        rows="5"
        placeholder="Addresses found from documents stay here as separate review context."
      ></textarea>
    </label>

    <div v-if="documentIntake.warnings.length" class="validation-summary field--full">
      <p><strong>Review notes:</strong> {{ documentIntake.warnings.join(' ') }}</p>
    </div>

    <details v-if="placeholderForms.document.rawExtractedText.trim()" class="ocr-raw-text field--full">
      <summary>Show extracted document text</summary>
      <textarea :value="placeholderForms.document.rawExtractedText" rows="10" readonly></textarea>
    </details>

    <label class="field field--full">
      <span>Source notes</span>
      <textarea
        v-model="placeholderForms.document.note"
        rows="5"
        placeholder="Describe what the document contains, what looked unclear, or anything else worth preserving."
      ></textarea>
    </label>

    <div class="panel__actions">
      <button type="submit" class="btn btn--primary">Save document source</button>
      <button type="button" class="btn btn--ghost" @click="goToRootIntake">Cancel</button>
    </div>
  </form>
</section>
    <CasePrepExposureReviewStep
  v-else-if="currentStep === 'addressExposure'"
  @save-source="handleExposureReviewSourceSave"
  @stage-transfers="handleExposureReviewTransferValidation"
  @cancel="goToRootIntake"
  @flash="setFlash"
  @error="setError"
/>

        <section v-else-if="currentStep === 'addressForward'" class="panel">
          <div class="panel__header">
            <div>
              <p class="eyebrow">Forward trace intake</p>
              <h2>Follow funds from one or more addresses</h2>
              <p>
                Capture the addresses, choose how broad the outgoing transaction set is, and use the lookup panel to review transaction candidates before adding them to shared validation.
              </p>
            </div>
          </div>

          <form class="form-grid" @submit.prevent="handleAddressForwardSubmit">
            <label class="field field--full">
              <span>Address or addresses</span>
              <textarea
                v-model="forms.addressForward.addressesText"
                rows="5"
                placeholder="Paste one address per line, or separate them with spaces or commas."
            
            
            
  data-1p-ignore    ></textarea>
            </label>

           <fieldset class="choice-group field--full">
  <legend>What outgoing transactions do you need?</legend>

  <label>
    <input v-model="forms.addressForward.selectionScope" type="radio" value="all" />
    All outgoing transactions
  </label>

  <label>
    <input v-model="forms.addressForward.selectionScope" type="radio" value="some" />
    Only some outgoing transactions
  </label>

  <p v-if="forms.addressForward.selectionScope === 'all'" class="field-help">
    You selected all outgoing transactions.
  </p>
</fieldset>

<div v-if="forms.addressForward.selectionScope === 'some'" class="filters-grid field--full">
  <label class="field">
    <span>From date (optional)</span>
    <input
      ref="addressForwardFromDateInput"
      v-model="forms.addressForward.dateFrom"
      type="text"
      class="datepicker-input"
      placeholder="YYYY-MM-DD"
      autocomplete="off"
      data-1p-ignore
    />
  </label>

  <label class="field">
    <span>To date (optional)</span>
    <input
      ref="addressForwardToDateInput"
      v-model="forms.addressForward.dateTo"
      type="text"
      class="datepicker-input"
      placeholder="YYYY-MM-DD"
      autocomplete="off"
      data-1p-ignore
    />
  </label>

  <label class="field">
    <span>Token type (optional)</span>
    <input
      v-model="forms.addressForward.tokenType"
      type="text"
      placeholder="ETH, USDT, BTC, etc."
      autocomplete="off"
      data-1p-ignore
    />
  </label>

  <label class="field">
    <span>Minimum amount (optional)</span>
    <input
      v-model="forms.addressForward.amountMin"
      type="text"
      placeholder="Optional"
      data-1p-ignore
    />
  </label>

  <label class="field">
    <span>Maximum amount (optional)</span>
    <input
      v-model="forms.addressForward.amountMax"
      type="text"
      placeholder="Optional"
      data-1p-ignore
    />
  </label>
</div>

<label v-if="forms.addressForward.selectionScope === 'some'" class="field field--full">
  <span>Known outgoing transaction hashes (optional)</span>
  <textarea
    v-model="forms.addressForward.candidateTransactionsText"
    rows="4"
    placeholder="Paste any hashes you already know so they can move into shared validation now."
    data-1p-ignore
  ></textarea>
</label>

<div class="address-lookup-panel field--full">
  <div class="selection-group__header">
    <div>
      <h3>Look up transactions from an address</h3>
      <p>
        Review up to the first {{ addressLookup.resultLimit }} matching transactions for one address at a time.
        Outgoing is the default starting point.
      </p>
    </div>
    <div class="panel__actions">
      <button
        type="button"
        class="btn btn--primary btn--small btn--lookup-emphasis"
        :disabled="!addressForwardParsedAddresses.length || addressLookup.state === 'loading'"
        @click="lookupTransactionsForActiveAddress"
      >
        {{ addressLookup.state === 'loading' ? 'Looking up...' : 'Look up transactions' }}
      </button>
      <button
        type="button"
        class="btn btn--ghost btn--small"
        :disabled="!addressLookupHasResults && !addressLookup.statusMessage"
        @click="resetAddressLookupState({ preserveOrigin: true, preserveDirection: true })"
      >
        Clear lookup
      </button>
    </div>
  </div>

  <div class="filters-grid field--full">
    <label class="field">
      <span>Address to inspect</span>
      <select v-model="addressLookup.activeAddress" :disabled="!addressForwardParsedAddresses.length">
        <option v-for="address in addressForwardParsedAddresses" :key="`addr_lookup_${address}`" :value="address">
          {{ address }}
        </option>
      </select>
    </label>

    <label class="field">
      <span>Direction</span>
      <select v-model="addressLookup.direction">
        <option value="outgoing">Outgoing</option>
        <option value="incoming">Incoming</option>
        <option value="all">All</option>
      </select>
    </label>
  </div>

  <div
    v-if="addressLookup.state === 'loading' || addressLookup.state === 'error' || addressLookup.statusMessage"
    class="ocr-progress-card"
    :class="{ 'ocr-progress-card--error': addressLookup.state === 'error' }"
  >
    <div class="ocr-progress-card__header">
      <strong>{{ addressLookup.errorMessage || addressLookup.statusMessage || 'Looking up address transactions...' }}</strong>
      <span v-if="addressLookup.lastLookupAddress">{{ addressLookup.direction }}</span>
    </div>
    <p v-if="addressLookup.lastLookupAddress">
      Active address: <code>{{ addressLookup.lastLookupAddress }}</code>
    </p>
  </div>

  <div v-if="addressLookupHasResults" class="address-lookup-results">
    <div class="selection-group__header">
      <div>
        <h3>Loaded transaction candidates</h3>
        <p>
          Showing {{ paginatedAddressLookupResults.length }} of {{ addressLookupResults.length }} loaded result{{ addressLookupResults.length === 1 ? '' : 's' }}
          for <code>{{ addressLookup.lastLookupAddress || addressLookup.activeAddress }}</code>.
        </p>
      </div>
      <div class="panel__actions">
        <button type="button" class="btn btn--ghost btn--small" @click="setAllAddressLookupResults(true)">
          Check all
        </button>
        <button type="button" class="btn btn--ghost btn--small" @click="setAllAddressLookupResults(false)">
          Clear all
        </button>
      </div>
    </div>

    <p v-if="addressLookup.truncatedResultSet" class="field-help">
      Only the first {{ addressLookup.resultLimit }} results were loaded. There may be more transactions for this address.
    </p>

    <div class="table-scroll">
      <table class="tx-table tx-table--lookup">
        <thead>
          <tr>
            <th>Select</th>
            <th>When</th>
            <th>Amount</th>
            <th>From</th>
            <th>To</th>
            <th>Hash</th>
            <th>Network</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="result in paginatedAddressLookupResults" :key="result.id">
            <td>
              <input v-model="result.selected" type="checkbox" />
            </td>
            <td>{{ formatLookupTimestamp(result.timestampIso || result.timestamp) }}</td>
            <td>{{ result.amount || '—' }}</td>
            <td><code>{{ result.from || 'unknown' }}</code></td>
            <td><code>{{ result.to || 'unknown' }}</code></td>
            <td><code>{{ result.hash }}</code></td>
            <td>{{ result.network || 'Unknown' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pager">
      <button
        type="button"
        class="btn btn--ghost"
        @click="addressLookup.currentPage = Math.max(1, addressLookup.currentPage - 1)"
        :disabled="addressLookup.currentPage <= 1"
      >
        ‹ Prev
      </button>
      <span class="muted tiny">{{ addressLookup.currentPage }} / {{ addressLookupTotalPages }}</span>
      <button
        type="button"
        class="btn btn--ghost"
        @click="addressLookup.currentPage = Math.min(addressLookupTotalPages, addressLookup.currentPage + 1)"
        :disabled="addressLookup.currentPage >= addressLookupTotalPages"
      >
        Next ›
      </button>
    </div>

    <div class="panel__actions address-lookup-panel__actions">
      <button
        type="button"
        class="btn btn--primary"
        :disabled="!selectedAddressLookupResultsCount"
        @click="addSelectedAddressLookupTransactions"
      >
        Add {{ selectedAddressLookupResultsCount }} selected transaction{{ selectedAddressLookupResultsCount === 1 ? '' : 's' }} to the prep run
      </button>
    </div>
  </div>
</div>

            <label class="field field--full">
              <span>Notes</span>
              <textarea
                v-model="forms.addressForward.note"
                rows="4"
                placeholder="Anything helpful about these addresses or the transaction selection goal."
          
          
  data-1p-ignore
                ></textarea>
            </label>

            <div class="panel__actions">
              <button type="submit" class="btn btn--primary">Save forward-trace source</button>
              <button type="button" class="btn btn--ghost" @click="goToRootIntake">Cancel</button>
            </div>
          </form>
        </section>

        <section v-else-if="currentStep === 'transaction'" class="panel">
          <div class="panel__header">
            <div>
              <p class="eyebrow">Transaction intake</p>
              <h2>I have a transaction</h2>
              <p>
                Paste one hash or many.
              </p>
            </div>
          </div>

          <form class="form-grid" @submit.prevent="handleTransactionSubmit">
            <fieldset class="choice-group field--full">
              <legend>How many transactions do you have?</legend>
              <label>
                <input v-model="forms.transaction.mode" type="radio" value="single" />
                Just one
              </label>
              <label>
                <input v-model="forms.transaction.mode" type="radio" value="bulk" />
                More than one
              </label>
            </fieldset>

            <label v-if="forms.transaction.mode === 'single'" class="field field--full">
              <span>Transaction hash</span>
              <input
                v-model="forms.transaction.singleHash"
                type="text"
                placeholder="0x..."
                autocomplete="off"
                
  data-1p-ignore
              />
            </label>

            <label v-else class="field field--full">
              <span>Transaction hashes</span>
              <textarea
                v-model="forms.transaction.bulkHashes"
                rows="6"
                placeholder="Paste one per line, or separate them with spaces or commas."
                
  data-1p-ignore
              ></textarea>
            </label>

            <label class="field">
              <span>Optional shared To Address</span>
              <input
                v-model="forms.transaction.commonToAddress"
                type="text"
                placeholder="0x..."
                autocomplete="off"
                
  data-1p-ignore
              />
            </label>

            <label class="field field--full">
              <span>Notes</span>
              <textarea
                v-model="forms.transaction.note"
                rows="4"
                placeholder="Why are these transactions relevant?"
                
  data-1p-ignore
              ></textarea>
            </label>

            <div class="panel__actions">
              <button type="submit" class="btn btn--primary">Stage candidate transactions</button>
              <button type="button" class="btn btn--ghost" @click="goToRootIntake">Cancel</button>
            </div>
          </form>
        </section>

        <section v-else-if="isTabularImportStep" class="panel panel--flush">
          <CasePrepTabularImportStep
            :source-type="currentStep"
            :source-title="currentSourceOption.title"
            :source-description="currentSourceOption.description"
            @stage-import="handleTabularImportStage"
            @cancel="goToRootIntake"
          />
        </section>

        <section v-else-if="currentStep === 'screenshots'" class="panel">
          <div class="panel__header">
            <div>
              <p class="eyebrow">Screenshot intake</p>
              <h2>I have screenshots</h2>
              <p>
                Add screenshots locally in this browser, try a best-effort local read, and review any
                transaction hashes or addresses.
              </p>
            </div>
          </div>

          <div class="local-processing-banner">
            <strong>Local only.</strong> Screenshot files stay in this browser tab and are not uploaded to
            the server. 
          </div>

          <form class="form-grid" @submit.prevent="handleScreenshotSourceSubmit">
            <div
              class="screenshot-dropzone field--full"
              :class="{ 'screenshot-dropzone--dragging': screenshotIntake.isDragging }"
              @dragenter.prevent="handleScreenshotDragEnter"
              @dragover.prevent="handleScreenshotDragOver"
              @dragleave.prevent="handleScreenshotDragLeave"
              @drop.prevent="handleScreenshotDrop"
            >
              <div class="screenshot-dropzone__content">
                <h3>Add screenshots or images</h3>
                <p>Drag image files here, or choose them from this device.</p>

                <div class="panel__actions">
                  <button type="button" class="btn btn--primary" @click="openScreenshotFilePicker">
                    Choose screenshots
                  </button>
                  <button
                    v-if="screenshotIntake.files.length"
                    type="button"
                    class="btn btn--ghost"
                    @click="clearScreenshotFiles"
                  >
                    Clear attached files
                  </button>
                </div>

                <p class="field-help">
                  PNG, JPG, WEBP, and any other browser-supported image type are fine.
                </p>
              </div>

              <input
                ref="screenshotFileInput"
                class="screenshot-file-input"
                type="file"
                accept="image/*"
                multiple
                @change="handleScreenshotFileSelection"
              />
            </div>

            <div v-if="screenshotIntake.files.length" class="screenshot-file-grid field--full">
              <article
                v-for="file in screenshotIntake.files"
                :key="file.id"
                class="screenshot-file-card"
              >
                <img
                  v-if="file.previewUrl"
                  class="screenshot-file-card__preview"
                  :src="file.previewUrl"
                  :alt="`Preview of ${file.name}`"
                />
                <div class="screenshot-file-card__meta">
                  <strong>{{ file.name }}</strong>
                  <p>{{ formatBytes(file.size) }} · {{ formatScreenshotScanStatus(file.scanStatus) }}</p>
                  <p v-if="file.lastExtractionSummary" class="screenshot-file-card__summary">
                    {{ file.lastExtractionSummary }}
                  </p>
                </div>
                <button
                  type="button"
                  class="btn btn--ghost btn--small"
                  @click="removeScreenshotFile(file.id)"
                >
                  Remove
                </button>
              </article>
            </div>

            <div class="panel__actions">
              <button
                type="button"
                class="btn btn--primary"
                :disabled="!screenshotIntake.files.length || isReadingScreenshots"
                @click="readScreenshotsLocally"
              >
                {{ screenshotReadButtonLabel }}
              </button>

              <button
                type="button"
                class="btn btn--ghost"
                :disabled="!placeholderForms.screenshots.manualExtractedText.trim() || isReadingScreenshots"
                @click="applyPastedScreenshotText"
              >
                Use pasted text
              </button>
            </div>

            <div
              v-if="screenshotIntake.engineStatus === 'loading' && screenshotIntake.scanState === 'idle'"
              class="local-processing-banner field--full"
            >
              Getting the local OCR reader ready in this browser. The first read on a device may take a little longer.
            </div>

            <div
              v-else-if="screenshotIntake.engineStatus === 'error' && screenshotIntake.scanState === 'idle'"
              class="validation-warning"
            >
              Local screenshot reading hit a setup problem in this browser. You can still paste copied text
              or save this source, then try the automatic reader again.
            </div>

            <div
              v-if="screenshotIntake.scanState !== 'idle'"
              class="ocr-progress-card field--full"
              :class="{ 'ocr-progress-card--error': screenshotIntake.scanState === 'error' }"
            >
              <div class="ocr-progress-card__header">
                <strong>{{ screenshotIntake.statusMessage || 'Preparing local screenshot read' }}</strong>
                <span>{{ screenshotIntake.scanProgress }}%</span>
              </div>
              <progress max="100" :value="screenshotIntake.scanProgress"></progress>
              <p v-if="screenshotIntake.currentFileName">
                Current file: {{ screenshotIntake.currentFileName }}
              </p>
            </div>

            <label class="field field--full">
              <span>Copied text from screenshots (optional)</span>
              <textarea
                v-model="placeholderForms.screenshots.manualExtractedText"
                rows="4"
                placeholder="Paste any text you already copied from the screenshots. We'll look for transaction hashes and addresses."
              ></textarea>
            </label>

            <label class="field field--full">
              <span>Transaction hashes to validate</span>
              <textarea
                v-model="placeholderForms.screenshots.knownTransactionsText"
                rows="5"
                placeholder="Any transaction hashes found from screenshots or pasted text will appear here."
              ></textarea>
              <p class="field-help">
                Only transaction hashes move into shared validation. Review these before confirming them.
              </p>
            </label>

            <label class="field field--full">
              <span>Extracted addresses</span>
              <textarea
                v-model="placeholderForms.screenshots.extractedAddressesText"
                rows="4"
                placeholder="Addresses found in the screenshots stay here as separate review context."
              ></textarea>
              <p class="field-help">
                Extracted addresses show here if any are found...
              </p>
            </label>

            <div v-if="screenshotIntake.warnings.length" class="validation-summary field--full">
              <p><strong>Review notes:</strong> {{ screenshotIntake.warnings.join(' ') }}</p>
            </div>

            <div v-if="screenshotIntake.unusableFragments.length" class="validation-warning field--full">
              Some values looked truncated or too ambiguous to trust automatically:
              {{ screenshotIntake.unusableFragments.join(', ') }}
            </div>

            <details v-if="placeholderForms.screenshots.rawExtractedText.trim()" class="ocr-raw-text field--full">
              <summary>Show raw extracted text</summary>
              <textarea :value="placeholderForms.screenshots.rawExtractedText" rows="8" readonly></textarea>
            </details>

            <label class="field field--full">
              <span>Source notes</span>
              <textarea
                v-model="placeholderForms.screenshots.note"
                rows="5"
                placeholder="Add any notes you have about this image."
              ></textarea>
            </label>

            <div class="panel__actions">
              <button type="submit" class="btn btn--primary">
                Save screenshot source
              </button>
              <button type="button" class="btn btn--ghost" @click="goToRootIntake">
                Cancel
              </button>
            </div>
          </form>
        </section>

        <section v-else-if="isPlaceholderStep" class="panel">
          <div class="panel__header">
            <div>
              <p class="eyebrow">Placeholder source path</p>
              <h2>{{ currentSourceOption.title }}</h2>
              <p>{{ currentSourceOption.description }}</p>
            </div>
          </div>

          <form class="form-grid" @submit.prevent="handlePlaceholderSourceSubmit">
            <label class="field field--full">
              <span>Source notes</span>
              <textarea
                v-model="placeholderForms[currentStep].note"
                rows="5"
                placeholder="Describe what this file set or evidence source contains."
              ></textarea>
            </label>

            <label class="field field--full">
              <span>Known transaction hashes</span>
              <textarea
                v-model="placeholderForms[currentStep].knownTransactionsText"
                rows="4"
                placeholder="Optional. Paste any hashes you already extracted so they can move into shared validation."
              ></textarea>
            </label>

            <div class="panel__actions">
              <button type="submit" class="btn btn--primary">Record this source</button>
              <button type="button" class="btn btn--ghost" @click="goToRootIntake">Cancel</button>
            </div>
          </form>
        </section>


        <section v-else-if="currentStep === 'extractedSelection'" class="panel">
          <div class="panel__header">
            <div>
              <p class="eyebrow">Review extracted items</p>
              <h2>Choose what to add from this source</h2>
              <p>
                The source item has already been saved in the prep run. Select which extracted transaction hashes
                should move into shared validation and which extracted addresses should be kept for follow-up.
              </p>
            </div>
          </div>

          <div v-if="extractionSelectionDraft" class="form-grid">
            <div class="validation-summary field--full">
              <p><strong>Source:</strong> {{ extractionSelectionDraft.sourceLabel }}</p>
              <p v-if="extractionSelectionDraft.warnings.length">
                <strong>Review notes:</strong> {{ extractionSelectionDraft.warnings.join(' ') }}
              </p>
            </div>

            <section v-if="extractionSelectionDraft.transactions.length" class="selection-group field--full">
              <div class="selection-group__header">
                <div>
                  <h3>Transaction hashes</h3>
                  <p>Selected transaction hashes will move into shared validation before they count as staged prep output.</p>
                </div>
                <div class="panel__actions">
                  <button type="button" class="btn btn--ghost btn--small" @click="setExtractionSelectionAll('transaction', true)">
                    Check all
                  </button>
                  <button type="button" class="btn btn--ghost btn--small" @click="setExtractionSelectionAll('transaction', false)">
                    Clear all
                  </button>
                </div>
              </div>

              <ul class="selection-list">
                <li v-for="item in extractionSelectionDraft.transactions" :key="item.id">
                  <label class="selection-list__label">
                    <input v-model="item.selected" type="checkbox" />
                    <code>{{ item.value }}</code>
                  </label>
                </li>
              </ul>
            </section>

            <section v-if="extractionSelectionDraft.addresses.length" class="selection-group field--full">
              <div class="selection-group__header">
                <div>
                  <h3>Addresses</h3>
                  <p>Selected addresses will be kept with this source item as follow-up leads while you unpack addresses into transactions.</p>
                </div>
                <div class="panel__actions">
                  <button type="button" class="btn btn--ghost btn--small" @click="setExtractionSelectionAll('address', true)">
                    Check all
                  </button>
                  <button type="button" class="btn btn--ghost btn--small" @click="setExtractionSelectionAll('address', false)">
                    Clear all
                  </button>
                </div>
              </div>

              <ul class="selection-list">
                <li v-for="item in extractionSelectionDraft.addresses" :key="item.id">
                  <label class="selection-list__label">
                    <input v-model="item.selected" type="checkbox" />
                    <code>{{ item.value }}</code>
                  </label>
                </li>
              </ul>
            </section>

            <div class="panel__actions">
              <button type="button" class="btn btn--primary" @click="confirmExtractionSelection">
                Confirm selections
              </button>
              <button type="button" class="btn btn--ghost" @click="skipExtractionSelection">
                Keep source only for now
              </button>
            </div>
          </div>

          <div v-else class="empty-state">
            <p>There are no extracted items ready to review.</p>
            <div class="panel__actions">
              <button type="button" class="btn btn--primary" @click="goToRootIntake">Return to intake</button>
            </div>
          </div>
        </section>

        <section v-else-if="currentStep === 'validation'" class="panel panel--validation">
          <div class="panel__header">
            <div>
              <p class="eyebrow">Shared validation</p>
              <h2>Validate and confirm candidate transactions</h2>
              <p>
                Different intake paths converge here. Check whether transactions exist on supported networks,
                identify cases that need a To Address, and keep warnings non-blocking when a lookup is unavailable.
              </p>
            </div>
          </div>

          <div v-if="hasMultipleValidationBatches" class="validation-batches">
            <p class="validation-batches__label">Pending batches</p>
            <div class="validation-batches__list">
              <button
                v-for="(batch, batchIndex) in validationBatches"
                :key="batch.id"
                type="button"
                class="validation-batch-chip"
                :class="{ 'validation-batch-chip--active': validationDraft && batch.id === validationDraft.id }"
                @click="setActiveValidationBatch(batch.id)"
              >
                <span>{{ batch.sourceLabel || `Batch ${batchIndex + 1}` }}</span>
                <strong>{{ batch.candidates.length }}</strong>
              </button>
            </div>
          </div>

          <div v-if="validationDraft" class="validation-summary">
            <p><strong>Source:</strong> {{ validationDraft.sourceLabel }}</p>
            <p v-if="hasMultipleValidationBatches">
              <strong>Batch:</strong> {{ activeValidationBatchIndex + 1 }} of {{ validationBatches.length }}
            </p>
            <p>
              <strong>Candidates:</strong>
              {{ validationSummary.candidateCount }}
              &nbsp;|&nbsp;
              <strong>Unparsed entries:</strong> {{ validationSummary.invalidCount }}
              &nbsp;|&nbsp;
              <strong>With To Address:</strong> {{ validationSummary.withToAddressCount }}
            </p>
            <p v-if="validationDraft.importSummary">
              <strong>Ready from file:</strong> {{ validationDraft.importSummary.readyCount || 0 }}
              &nbsp;|&nbsp;
              <strong>Missing tx hash:</strong> {{ validationDraft.importSummary.missingHashCount || 0 }}
            </p>
          </div>

          <div v-if="validationDraft && validationDraft.invalidEntries.length" class="validation-warning">
            These entries were not recognized as transaction hashes yet:
            {{ validationDraft.invalidEntries.join(', ') }}
          </div>

          <div v-if="validationDraft && validationDraft.candidates.length" class="validation-toolbar">
            <div>
              <strong>Transaction checks</strong>
              <p>Validate the active batch, then use the status controls to quickly mark trusted rows as ready.</p>
            </div>
            <div class="validation-toolbar__actions">
              <button
                type="button"
                class="btn btn--primary"
                :disabled="validationCheck.state === 'running'"
                @click="validateActiveBatchTransactions"
              >
                {{ validationCheck.state === 'running' ? 'Checking transactions…' : 'Check active batch' }}
              </button>
              <button type="button" class="btn btn--ghost" @click="setAllValidationStates('ready')">
                Set all as ready
              </button>
              <button type="button" class="btn btn--ghost" @click="setAllValidationStates('unverified')">
                Set all as unverified
              </button>
            </div>
          </div>

          <div
            v-if="validationCheck.state === 'running' || validationCheck.statusMessage || validationCheck.errorMessage"
            class="ocr-progress-card"
            :class="{ 'ocr-progress-card--error': validationCheck.state === 'error' }"
          >
            <div class="ocr-progress-card__header">
              <strong>{{ validationCheck.errorMessage || validationCheck.statusMessage || 'Checking candidate transactions…' }}</strong>
              <span v-if="validationCheck.totalCount">{{ validationCheck.checkedCount }} / {{ validationCheck.totalCount }}</span>
            </div>
            <progress
              v-if="validationCheck.totalCount"
              max="100"
              :value="Math.round((validationCheck.checkedCount / Math.max(validationCheck.totalCount, 1)) * 100)"
            ></progress>
          </div>

          <div v-if="!validationDraft || !validationDraft.candidates.length" class="empty-state">
            <h3>No candidate transactions are waiting for confirmation.</h3>
            <p>Return to the intake root and add a transaction-bearing source.</p>
          </div>

          <div v-else class="table-scroll table-scroll--validation">
            <table class="tx-table tx-table--validation">
              <colgroup>
                <col class="validation-col validation-col--hash" />
                <col class="validation-col validation-col--to-address" />
                <col class="validation-col validation-col--network" />
                <col class="validation-col validation-col--status" />
                <col class="validation-col validation-col--notes" />
                <col class="validation-col validation-col--actions" />
              </colgroup>
              <thead>
                <tr>
                  <th>Transaction hash</th>
                  <th>To Address</th>
                  <th>Network</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(candidate, index) in validationDraft.candidates" :key="candidate.id">
                  <td data-label="Transaction hash" class="validation-cell validation-cell--hash">
                    <code>{{ candidate.txHash }}</code>
                    <button
                      type="button"
                      class="btn btn--ghost btn--small validation-row-action"
                      :disabled="validationCheck.state === 'running'"
                      @click="validateSingleValidationCandidate(candidate)"
                    >
                      Check
                    </button>
                  </td>
                  <td data-label="To Address" class="validation-cell validation-cell--to-address">
                    <input
                      v-model="candidate.toAddress"
                      type="text"
                      placeholder="Optional"
                      autocomplete="off"
                      
  data-1p-ignore
                    />
                    <button
                      v-if="candidate.toAddressSuggestions && candidate.toAddressSuggestions.length"
                      type="button"
                      class="btn btn--ghost btn--small validation-row-action"
                      @click="openToAddressPicker(candidate)"
                    >
                      Pick To Address
                    </button>
                    <p v-if="candidate.requiresToAddress === 'yes' && !candidate.toAddress" class="field-help field-help--warning">
                      This transaction appears to need a To Address.
                    </p>
                  </td>
                  <td data-label="Network" class="validation-cell validation-cell--network">
                    <input
                      v-model="candidate.network"
                      type="text"
                      class="validation-network-input"
                      placeholder="Unknown"
                      autocomplete="off"
  data-1p-ignore
                    />
                  </td>
                  <td data-label="Status" class="validation-cell validation-cell--status">
                    <select v-model="candidate.validationState" class="validation-status-select">
                      <option value="needs_review">Needs review</option>
                      <option value="unverified">Unverified but usable</option>
                      <option value="ready">Ready</option>
                    </select>
                    <div class="validation-state-hints">
                      <span v-if="candidate.lookupStatus">Lookup: {{ prettyLookupStatus(candidate.lookupStatus) }}</span>
                      <span v-if="candidate.requiresToAddress && candidate.requiresToAddress !== 'unknown'">
                        To Address: {{ candidate.requiresToAddress === 'yes' ? 'needed' : 'not needed' }}
                      </span>
                    </div>
                  </td>
                  <td data-label="Notes" class="validation-cell validation-cell--notes">
                    <input
                      v-model="candidate.notes"
                      type="text"
                      placeholder="Optional note"
                      autocomplete="off"
  data-1p-ignore
                    />
                    <p v-if="candidateRowOutcomeLabel(candidate)" class="field-help field-help--success">
                      ✓ {{ candidateRowOutcomeLabel(candidate) }}
                    </p>
                    <ul v-if="candidate.warnings && candidate.warnings.length" class="warning-list">
                      <li
                        v-for="warning in candidate.warnings"
                        :key="warning"
                        :class="candidateMessageClass(warning)"
                      >{{ warning }}</li>
                    </ul>
                  </td>
                  <td data-label="Actions" class="validation-cell validation-cell--actions">
                    <button type="button" class="btn btn--ghost btn--small" @click="removeValidationCandidate(index)">
                      Remove
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="panel__actions panel__actions--validation-footer">
            <button type="button" class="btn btn--primary" @click="confirmValidationDraft">
              Confirm active batch and add to prep run
            </button>
            <button type="button" class="btn btn--ghost" @click="discardValidationDraft">
              Keep source, discard active batch
            </button>
          </div>
        </section>

        <div v-if="validationConfirmGuard.open" class="case-modal-backdrop" @click.self="closeValidationConfirmGuard">
          <div class="case-modal case-modal--compact">
            <div class="case-modal__head">
              <div>
                <p class="eyebrow">Recommended validation step</p>
                <h3>You have unchecked transactions in this batch</h3>
              </div>
              <button type="button" class="btn btn--ghost btn--small" @click="closeValidationConfirmGuard">Close</button>
            </div>

            <div class="case-modal__body">
              <p>
                {{ uncheckedValidationCandidateCount }} candidate transaction<span v-if="uncheckedValidationCandidateCount !== 1">s</span>
                have not been checked yet. Running <strong>Check active batch</strong> is recommended because it can save time by spotting
                missing <GlossaryTerm :entry="glossary.toAddress" />, unsupported transactions, or transactions that still need a destination picked.
              </p>

              <div class="panel__actions">
                <button
                  type="button"
                  class="btn btn--primary"
                  :disabled="validationCheck.state === 'running'"
                  @click="runChecksFromValidationConfirmGuard"
                >
                  {{ validationCheck.state === 'running' ? 'Checking transactions…' : 'Run checks now' }}
                </button>
                <button type="button" class="btn btn--ghost" @click="confirmValidationDraft({ skipUncheckedWarning: true })">
                  Add anyway
                </button>
                <button type="button" class="btn btn--ghost" @click="closeValidationConfirmGuard">
                  Keep reviewing
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="toAddressPicker.open" class="case-modal-backdrop" @click.self="closeToAddressPicker">
          <div class="case-modal">
            <div class="case-modal__head">
              <div>
                <p class="eyebrow">To Address selection</p>
                <h3>Select a valid To Address</h3>
              </div>
              <button type="button" class="btn btn--ghost btn--small" @click="closeToAddressPicker">Close</button>
            </div>

            <div class="case-modal__body">
              <p>
                This transaction has multiple possible destinations. Pick the address that matches the funds you need to investigate.
              </p>

              <div v-if="toAddressPicker.suggestions.length" class="to-address-choice-list">
                <button
                  v-for="(entry, index) in toAddressPicker.suggestions"
                  :key="`${entry.address}_${index}`"
                  type="button"
                  class="to-address-choice"
                  :class="{ 'to-address-choice--known': entry.seenInSources }"
                  @click="chooseToAddressSuggestion(entry)"
                >
                  <code>{{ entry.address }}</code>
                  <span>{{ formatToAddressSuggestionMeta(entry) }}</span>
                  <strong v-if="entry.seenInSources">Found in this prep run</strong>
                </button>
              </div>

              <p v-else class="empty-state">No To Address suggestions were returned for this transaction.</p>
            </div>
          </div>
        </div>

        <section v-else-if="currentStep === 'review'" class="panel review-panel">
          <div class="panel__header">
            <div>
              <p class="eyebrow">Prep run review</p>
              <h2>Review what is staged so far</h2>
              <p>
                This is the prep summary layer. It stays separate from the canonical Case Buddy case,
                but it can already produce handoff-ready transaction lists and TSV output.
              </p>
            </div>
          </div>

          <div v-if="hasPendingValidation" class="validation-warning validation-warning--strong">
            You still have {{ validationBatches.length }} candidate batch<span v-if="validationBatches.length !== 1">es</span> waiting in shared validation.
            <button type="button" class="btn btn--ghost btn--small" @click="resumeValidation">
              Resume validation
            </button>
          </div>

          <div class="review-stack">
            <article class="review-card review-card--transactions">
              <div class="review-card__header review-card__header--split">
                <div>
                  <h3>Staged starting transactions</h3>
                  <p v-if="stagedTransactionsNeedReview.length">
                    {{ stagedTransactionsNeedReview.length }} staged transaction<span v-if="stagedTransactionsNeedReview.length !== 1">s</span> still show as needing review.
                  </p>
                </div>
                <button
                  v-if="stagedTransactionsNeedReview.length"
                  type="button"
                  class="btn btn--primary btn--small"
                  @click="resumeStagedTransactionReview()"
                >
                  Review unresolved staged transactions
                </button>
              </div>
              <p v-if="!stagedTransactions.length">No transactions are staged yet.</p>
              <div v-else class="table-scroll">
                <table class="tx-table">
                  <thead>
                    <tr>
                      <th>Transaction hash</th>
                      <th>To Address</th>
                      <th>Network</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="transaction in stagedTransactions" :key="transaction.id">
                      <td><code>{{ transaction.txHash }}</code></td>
                      <td>{{ transaction.toAddress || '—' }}</td>
                      <td>{{ transaction.network || 'Unknown' }}</td>
                      <td>
                        <span class="status-pill" :class="`status-pill--${transaction.validationState}`">
                          {{ prettyValidationState(transaction.validationState) }}
                        </span>
                      </td>
                      <td>
                        <div class="table-actions">
                          <button
                            v-if="transaction.validationState === 'needs_review' || (transaction.requiresToAddress === 'yes' && !transaction.toAddress)"
                            type="button"
                            class="btn btn--primary btn--small"
                            @click="resumeStagedTransactionReview(transaction.id)"
                          >
                            Review / validate
                          </button>
                          <button
                            type="button"
                            class="btn btn--ghost btn--small"
                            :data-track="`case_prep_send_tx_${transaction.id}`"
                            @click="sendTransactionToPathTracer(transaction)"
                          >
                            Send to PathTracer
                          </button>
                          <button
                            type="button"
                            class="btn btn--ghost btn--small"
                            @click="removeStagedTransaction(transaction.id)"
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </article>

            <article class="review-card review-card--sources">
              <h3>Source items</h3>
              <p v-if="!sourceItems.length">No source items have been saved yet.</p>
              <ul v-else class="source-item-list">
                <li v-for="source in sourceItems" :key="source.id">
                  <div class="source-item-list__content">
                    <strong>{{ source.title }}</strong>
                    <p>{{ formatSourceType(source.type) }} · {{ prettySourceStatus(source.status) }}</p>
                    <p v-if="getSourceItemExtractedTransactions(source).length">
                      <strong>Transactions found:</strong> {{ getSourceItemExtractedTransactions(source).length }}
                    </p>
                    <div v-if="getSourceItemExtractedTransactions(source).length" class="source-item-inline-values">
                      <code v-for="value in getSourceItemExtractedTransactions(source).slice(0, 2)" :key="`${source.id}_tx_${value}`">{{ value }}</code>
                      <span v-if="getSourceItemExtractedTransactions(source).length > 2">
                        +{{ getSourceItemExtractedTransactions(source).length - 2 }} more
                      </span>
                    </div>
                    <p v-if="getSourceItemExtractedAddresses(source).length">
                      <strong>Addresses found:</strong> {{ getSourceItemExtractedAddresses(source).length }}
                    </p>
                    <div v-if="getSourceItemExtractedAddresses(source).length" class="source-item-inline-values">
                      <code v-for="value in getSourceItemExtractedAddresses(source).slice(0, 2)" :key="`${source.id}_addr_${value}`">{{ value }}</code>
                      <span v-if="getSourceItemExtractedAddresses(source).length > 2">
                        +{{ getSourceItemExtractedAddresses(source).length - 2 }} more
                      </span>
                    </div>
                    <p v-if="getSourceItemSelectedAddresses(source).length">
                      <strong>Addresses selected for follow-up:</strong> {{ getSourceItemSelectedAddresses(source).length }}
                    </p>
                    <div v-if="getSourceItemSelectedAddresses(source).length" class="source-item-inline-values">
                      <code v-for="value in getSourceItemSelectedAddresses(source).slice(0, 2)" :key="`${source.id}_selected_addr_${value}`">{{ value }}</code>
                      <span v-if="getSourceItemSelectedAddresses(source).length > 2">
                        +{{ getSourceItemSelectedAddresses(source).length - 2 }} more
                      </span>
                    </div>
                    <div class="source-item-actions">
                      <button
                        v-if="getSourceItemExtractedTransactions(source).length || getSourceItemExtractedAddresses(source).length"
                        type="button"
                        class="btn btn--ghost btn--small"
                        @click="reopenSourcePromotion(source)"
                      >
                        Review extracted items
                      </button>
                      <button
                        v-if="getSourceItemSelectedAddresses(source).length"
                        type="button"
                        class="btn btn--primary btn--small btn--lookup-emphasis"
                        @click="startAddressLookupFromSource(source)"
                      >
                        Look up transactions
                      </button>
                    </div>
                  </div>
                  <div class="source-item-list__meta"><span>{{ formatTimestamp(source.updatedAt || source.createdAt) }}</span><button
                      type="button"
                      class="btn btn--ghost btn--small"
                      @click="removeSourceItem(source.id)"
                    >
                      Remove source
                    </button></div>
                </li>
              </ul>
            </article>
          </div>

          <article class="review-card handoff-card">
            <h3>Handoff previews</h3>
            <p>
              These previews stay centered on the staged starting transactions that are ready to hand off.
            </p>

            <div class="handoff-grid">
              <label class="field">
                <span>Transaction hashes (newline list)</span>
                <textarea :value="handoffNewlineList" rows="5" readonly></textarea>
                <button type="button" class="btn btn--ghost btn--small" @click="copyText(handoffNewlineList, 'hash list')">
                  Copy hash list
                </button>
              </label>

              <label class="field">
                <span>Transaction hashes with optional To Address</span>
                <textarea :value="handoffHashAndToAddressList" rows="5" readonly></textarea>
                <button type="button" class="btn btn--ghost btn--small" @click="copyText(handoffHashAndToAddressList, 'hash and To Address list')">
                  Copy hash + To Address list
                </button>
              </label>

              <label class="field field--full">
                <span>Case Buddy-compatible TSV (hash in Column B, To Address in Column D)</span>
                <textarea :value="handoffTsv" rows="6" readonly></textarea>
                <button type="button" class="btn btn--ghost btn--small" @click="copyText(handoffTsv, 'Case Buddy TSV')">
                  Copy TSV
                </button>
              </label>

            </div>
          </article>

          <div class="panel__actions">
            <button type="button" class="btn btn--primary" @click="goToRootIntake">Add more sources</button>
            <button type="button" class="btn btn--ghost" @click="sendPrepRunToCaseBuddy">
              Open final handoff view
            </button>
          </div>
        </section>

        <section v-else-if="currentStep === 'caseBuddyPreview'" class="panel review-panel case-ready-panel">
          <div class="panel__header">
            <div>
              <p class="eyebrow">Final prep handoff</p>
              <h2>Your case-ready transaction sheet</h2>
              <p>
                This preview keeps the Case Buddy-compatible sheet shape: transaction hashes in Column B and
                To Addresses in Column D when they are present. The actions beside it are product options,
                not extra spreadsheet columns.
              </p>
            </div>
          </div>

          <div class="case-ready-banner">
            <div class="case-ready-banner__copy">
              <p class="eyebrow">Finished prep run</p>
              <h3>Your starting transactions are assembled</h3>
              <p>
                This is the final handoff surface for the prep flow. From here, you can send a specific row to
                PathTracer, request a manual expert investigation, or keep the Case Buddy TSV handy for the
                future Case Buddy import flow.
              </p>
            </div>
            <div class="case-ready-banner__stats">
              <span class="case-ready-banner__chip">{{ stagedTransactions.length }} staged transaction<span v-if="stagedTransactions.length !== 1">s</span></span>
              <span class="case-ready-banner__chip" v-if="stagedTransactionsWithToAddressCount">{{ stagedTransactionsWithToAddressCount }} with To Address</span>
              <span class="case-ready-banner__chip" v-if="stagedTransactionsReadyCount">{{ stagedTransactionsReadyCount }} marked ready</span>
            </div>
          </div>

          <div class="validation-summary validation-summary--stacked">
            <p><strong>How to use this today:</strong> Use the row buttons in the spreadsheet preview to open a selected transaction in PathTracer, or copy your TSV before continuing to the manual investigation request page.</p>
            <p><strong>Rows shown:</strong> {{ caseBuddyPreviewRows.length }} visible row<span v-if="caseBuddyPreviewRows.length !== 1">s</span> based on {{ stagedTransactions.length }} staged transaction<span v-if="stagedTransactions.length !== 1">s</span>.</p>
          </div>

          <section class="handoff-options" aria-label="Final handoff options">
            <article class="handoff-option handoff-option--pathtracer">
              <p class="eyebrow">PathTracer</p>
              <h3>Send a row to PathTracer</h3>
              <p>
                Open any populated spreadsheet row in PathTracer using the row action beside the sheet.
                Validation warnings stay in Case Prep Buddy, and PathTracer can still handle the analysis request.
              </p>
              <button
                type="button"
                class="btn btn--primary btn--small"
                :disabled="!stagedTransactions.length"
                @click="sendTransactionToPathTracer(stagedTransactions[0])"
              >
                Open first row in PathTracer
              </button>
            </article>

            <article class="handoff-option handoff-option--investigation">
              <p class="eyebrow">Manual investigation</p>
              <h3>Request an expert investigation</h3>
              <p>
                We will show your prepared TSV first, then send you to the external CoinStructive intake form.
                Because that form is not on PathTracer, you will copy and paste the transaction data yourself.
              </p>
              <button type="button" class="btn btn--yellow" @click="openInvestigationRequestFromCasePrep">
                Request investigation
              </button>
            </article>

            <article class="handoff-option handoff-option--disabled" aria-disabled="true">
              <p class="eyebrow">Case Buddy</p>
              <h3>Send to Case Buddy</h3>
              <p>
                Direct Case Buddy import is planned, but not wired yet. The TSV below keeps the expected
                Column B / Column D shape ready for that future handoff.
              </p>
              <button type="button" class="btn btn--ghost" disabled>
                Coming soon
              </button>
            </article>
          </section>

          <article class="review-card handoff-card">
            <div class="review-card__header review-card__header--split">
              <div>
                <h3>Spreadsheet preview</h3>
                <p class="field-help">Dragging across the sheet and copying will place the clean Case Buddy TSV on your clipboard. Row and column headers are preview-only; the PathTracer action gutter is not part of the TSV.</p>
              </div>
              <button type="button" class="btn btn--primary btn--small" @click="copyText(handoffTsv, 'Case Buddy TSV')">
                Copy TSV
              </button>
            </div>
            <div class="table-scroll spreadsheet-preview-wrap" @copy="copyCaseBuddyPreviewSelection">
              <table class="spreadsheet-preview-table spreadsheet-preview-table--with-actions">
                <thead>
                  <tr>
                    <th class="spreadsheet-preview-table__rowhead spreadsheet-preview-table__noselect" @mousedown.prevent></th>
                    <th class="spreadsheet-preview-table__noselect" @mousedown.prevent>A</th>
                    <th class="spreadsheet-preview-table__noselect" @mousedown.prevent>B</th>
                    <th class="spreadsheet-preview-table__noselect" @mousedown.prevent>C</th>
                    <th class="spreadsheet-preview-table__noselect" @mousedown.prevent>D</th>
                    <th class="spreadsheet-preview-table__noselect spreadsheet-preview-table__action-head" @mousedown.prevent>PathTracer</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in caseBuddyPreviewRows" :key="`casebuddy_row_${row.rowNumber}`">
                    <th class="spreadsheet-preview-table__rowhead spreadsheet-preview-table__noselect" @mousedown.prevent>{{ row.rowNumber }}</th>
                    <td><span class="spreadsheet-preview-table__celltext">{{ row.colA }}</span></td>
                    <td><code v-if="row.colB" class="spreadsheet-preview-table__celltext">{{ row.colB }}</code><span v-else class="spreadsheet-preview-table__celltext"></span></td>
                    <td><span class="spreadsheet-preview-table__celltext">{{ row.colC }}</span></td>
                    <td><code v-if="row.colD" class="spreadsheet-preview-table__celltext">{{ row.colD }}</code><span v-else class="spreadsheet-preview-table__celltext"></span></td>
                    <td class="spreadsheet-preview-table__action-cell spreadsheet-preview-table__noselect" @mousedown.prevent>
                      <button
                        v-if="row.transaction"
                        type="button"
                        class="btn btn--ghost btn--tiny spreadsheet-preview-table__action-button"
                        :aria-label="`Send row ${row.rowNumber} to PathTracer`"
                        @click.stop="sendTransactionToPathTracer(row.transaction)"
                      >
                        Send
                      </button>
                      <span v-else class="spreadsheet-preview-table__empty-action">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </article>

          <article class="review-card handoff-card">
            <h3>Copy-ready transaction data</h3>
            <p class="field-help">
              Use this when requesting a manual investigation. The next form is hosted outside PathTracer, so
              copy this data and keep it handy before you continue.
            </p>
            <label class="field field--full">
              <span>Case Buddy-compatible TSV — hash in Column B, To Address in Column D</span>
              <textarea :value="handoffTsv" rows="8" readonly></textarea>
            </label>

            <div class="panel__actions">
              <button type="button" class="btn btn--primary" @click="copyText(handoffTsv, 'Case Buddy TSV')">
                Copy TSV
              </button>
              <button type="button" class="btn btn--ghost" @click="copyText(handoffHashAndToAddressList, 'hash and To Address list')">
                Copy hash + To Address list
              </button>
            </div>
          </article>

          <div class="panel__actions panel__actions--final-handoff">
            <button type="button" class="btn btn--ghost" @click="openReview">Back to prep review</button>
            <button type="button" class="btn btn--yellow" @click="openInvestigationRequestFromCasePrep">
              Request investigation
            </button>
            <button type="button" class="btn btn--ghost" disabled @click="confirmCaseBuddyPlaceholderHandoff">
              Send to Case Buddy — coming soon
            </button>
          </div>
        </section>
      </section>

      <PrepSummaryTray
        v-if="showPrepSummaryTray"
        :prep-run="prepRunForTray"
        :save-state="saveState"
        @update-title="updateDraftTitle"
        @open-review="openReview"
        @send-casebuddy="sendPrepRunToCaseBuddy"
        @reset-run="resetRun"
      />
    </div>
  </section>
</template>

<script>
import { logEvent } from '@/analytics';
import GlossaryTerm from '@/components/case-prep-buddy/GlossaryTerm.vue';
import PrepSummaryTray from '@/components/case-prep-buddy/PrepSummaryTray.vue';
import CasePrepTabularImportStep from '@/components/case-prep-buddy/CasePrepTabularImportStep.vue';
import CasePrepExposureReviewStep from '@/components/case-prep-buddy/CasePrepExposureReviewStep.vue';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';

import {
  buildCaseBuddyCreateCaseTsv,
  buildHashWithOptionalToAddressList,
  buildNewlineHashList
} from '@/services/casePrepBuddyHandoff';
import {
  createClientId,
  createEmptyPrepRun,
  deletePrepRun,
  loadLastPrepRun,
  savePrepRun
} from '@/services/casePrepBuddyStorage';
import {
  createStagedTransactionCandidates,
  mergeStagedTransactions,
  normalizeAddress,
  normalizeTransactionHash,
  parseAddressInput,
  parseTransactionInput,
  summarizeValidationDraft
} from '@/services/casePrepBuddyValidation';
import { buildCasePrepCandidatesFromImport } from '@/services/txnImport/buildCasePrepCandidatesFromImport';
import { casePrepBuddyGlossary } from '@/utils/casePrepBuddyGlossary';
import {
  casePrepBuddySourceOptions,
  getCasePrepBuddySourceOptionByStep
} from '@/utils/casePrepBuddySourceOptions';
import {
  warmupCasePrepOcr,
  recognizeCasePrepImage,
  terminateCasePrepOcr
} from '@/services/casePrepOcrService';


import {
  extractCasePrepDocument,
  getCasePrepDocumentKind,
  isCasePrepDocumentSupported,
  warmupCasePrepDocumentReaders
} from '@/services/casePrepDocumentService';


const EDUCATION_STAGES = [
  {
    title: 'Blockchain Basics',
    summary:
      'Let\'s start with the basics:',
    points: [
      'An address is where funds can appear, move from, or move to.',
      'A transaction is one recorded movement or action on-chain.',
      'A transaction hash is the unique identifier for that transaction.',
    ]
  },
  {
    title: 'Understand why the starting transaction matters',
    summary:
      'Blockchain investigations begin with a clean, confirmed set of starting transactions. Prep runs exist to help users find and review those.',
    points: [
      'Users often begin with messy evidence rather than neat hashes.',
      'Different source types can contribute to one prep run.',
      'You can add more sources without losing staged work.'
    ]
  },
  {
    title: 'So what am I looking for?',
    summary:
      'Dig up everything you can find about the addresses and transactions, and add them when you are ready.',
    points: [
      'Hint: most investigations start with an address hash or transaction hash',
      'Learn how to identify them, and locate the ones related to your scenario',
      'When you are ready, load them into the case builder'
    ]
  }
];

const SCREENSHOT_TRANSACTION_LABEL_PREFIXES = [
  /^(?:transaction\s*(?:id|hash)|tx\s*(?:id|hash)|txid)\s*[:#-]?\s*/i,
  /^(?:hash)\s*[:#-]?\s*/i
];

const SCREENSHOT_ADDRESS_LABEL_PREFIXES = [
  /^(?:wallet\s+address(?:\s+details)?|recipient\s+address|to\s+address|destination\s+address|deposit\s+address|withdraw(?:al)?\s+to|withdraw(?:al)?\s+address|address)\s*[:#-]?\s*/i
];

const SCREENSHOT_ELLIPSIS_PATTERN = /(?:\.\.\.|…|⋯)/;
const SCREENSHOT_MAX_CONTINUATION_LINES = 4;

function dedupePlainStrings(values = []) {
  return Array.from(new Set((values || []).map((value) => String(value || '').trim()).filter(Boolean)));
}

function mergeUniqueLines(existingText, newValues, normalizer = null) {
  const existingLines = String(existingText || '')
    .split(/\r?\n/)
    .map((line) => String(line || '').trim())
    .filter(Boolean);

  const incomingValues = Array.isArray(newValues)
    ? newValues
    : String(newValues || '')
        .split(/\r?\n/)
        .map((value) => String(value || '').trim())
        .filter(Boolean);

  const seen = new Set();
  const output = [];

  for (const line of existingLines) {
    const key = normalizer ? (normalizer(line) || line) : line;
    if (seen.has(key)) continue;
    seen.add(key);
    output.push(line);
  }

  for (const value of incomingValues) {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) continue;
    const key = normalizer ? (normalizer(normalizedValue) || normalizedValue) : normalizedValue;
    if (seen.has(key)) continue;
    seen.add(key);
    output.push(normalizedValue);
  }

  return output.join('\n');
}
function appendRawTextBlock(existingText, nextText) {
  const current = String(existingText || '').trim();
  const addition = String(nextText || '').trim();

  if (!addition) return current;
  if (!current) return addition;
  if (current.includes(addition)) return current;

  return `${current}

────────

${addition}`;
}

function capitalizeFirstLetter(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function humanizeScreenshotOcrStatus(value) {
  const normalized = String(value || '')
    .replace(/_/g, ' ')
    .trim()
    .toLowerCase();

  const map = {
    'loading tesseract core': 'Loading local OCR engine',
    'loading language traineddata': 'Loading local OCR language data',
    'initializing api': 'Starting the local OCR reader',
    'initializing tesseract': 'Starting the local OCR reader',
    'recognizing text': 'Reading screenshot text',
    'loading image': 'Preparing screenshot image'
  };

  return map[normalized] || capitalizeFirstLetter(normalized);
}

function normalizeOcrWhitespace(value) {
  return String(value || '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeOcrCandidateText(value) {
  return normalizeOcrWhitespace(value)
    .replace(/[|]/g, '')
    .replace(/\s+/g, '')
    .replace(/^[^A-Za-z0-9.]+/, '')
    .replace(/[^A-Za-z0-9.…⋯]+$/, '');
}

function isTruncatedScreenshotValue(value) {
  return SCREENSHOT_ELLIPSIS_PATTERN.test(String(value || ''));
}

function getScreenshotLabelPrefixPatterns(kind) {
  return kind === 'transaction'
    ? SCREENSHOT_TRANSACTION_LABEL_PREFIXES
    : SCREENSHOT_ADDRESS_LABEL_PREFIXES;
}

function detectScreenshotLabelKind(text) {
  const normalized = normalizeOcrWhitespace(text);
  if (!normalized) return null;

  if (SCREENSHOT_TRANSACTION_LABEL_PREFIXES.some((pattern) => pattern.test(normalized))) {
    return 'transaction';
  }

  if (SCREENSHOT_ADDRESS_LABEL_PREFIXES.some((pattern) => pattern.test(normalized))) {
    return 'address';
  }

  return null;
}

function stripKnownScreenshotLabelPrefix(text, kind) {
  const normalized = normalizeOcrWhitespace(text);
  const patterns = getScreenshotLabelPrefixPatterns(kind);

  for (const pattern of patterns) {
    const stripped = normalized.replace(pattern, '');
    if (stripped !== normalized) {
      return stripped.trim();
    }
  }

  return '';
}

function parseTesseractTsvLines(tsv) {
  const raw = String(tsv || '').trim();
  if (!raw) return [];

  const rows = raw.split(/\r?\n/);
  if (rows.length <= 1) return [];

  const lineMap = new Map();

  for (const row of rows.slice(1)) {
    const parts = row.split('	');
    if (parts.length < 12) continue;

    const [level, pageNum, blockNum, parNum, lineNum, wordNum, left, top, width, height, conf, ...textParts] = parts;
    if (String(level) !== '5') continue;

    const text = normalizeOcrWhitespace(textParts.join('	'));
    if (!text) continue;

    const key = [pageNum, blockNum, parNum, lineNum].join(':');
    const word = {
      text,
      left: Number(left) || 0,
      top: Number(top) || 0,
      width: Number(width) || 0,
      height: Number(height) || 0,
      conf: Number(conf) || 0,
      wordNum: Number(wordNum) || 0
    };

    if (!lineMap.has(key)) {
      lineMap.set(key, {
        words: [],
        left: word.left,
        top: word.top,
        right: word.left + word.width,
        bottom: word.top + word.height
      });
    }

    const line = lineMap.get(key);
    line.words.push(word);
    line.left = Math.min(line.left, word.left);
    line.top = Math.min(line.top, word.top);
    line.right = Math.max(line.right, word.left + word.width);
    line.bottom = Math.max(line.bottom, word.top + word.height);
  }

  return Array.from(lineMap.values())
    .map((line, index) => {
      const words = line.words.sort((a, b) => a.left - b.left || a.wordNum - b.wordNum);
      return {
        index,
        text: words.map((word) => word.text).join(' '),
        left: line.left,
        top: line.top,
        right: line.right,
        bottom: line.bottom,
        height: Math.max(1, line.bottom - line.top)
      };
    })
    .sort((a, b) => a.top - b.top || a.left - b.left)
    .map((line, index) => ({ ...line, index }));
}

function fallbackOcrLinesFromText(text) {
  return String(text || '')
    .split(/\r?\n/)
    .map((line) => normalizeOcrWhitespace(line))
    .filter(Boolean)
    .map((line, index) => ({
      index,
      text: line,
      left: 0,
      top: index * 24,
      right: Math.max(40, line.length * 9),
      bottom: (index * 24) + 20,
      height: 20
    }));
}

function buildStructuredOcrLines(result) {
  const tsvLines = parseTesseractTsvLines(result?.tsv || '');
  if (tsvLines.length) {
    return tsvLines;
  }

  return fallbackOcrLinesFromText(result?.text || '');
}

function looksLikeStructuredValueFragment(text) {
  const compact = normalizeOcrCandidateText(text);
  if (!compact || compact.length < 6) return false;
  if (isTruncatedScreenshotValue(compact)) return true;
  if (/[^A-Za-z0-9.…⋯]/.test(compact)) return false;
  if (compact.startsWith('0x') || /^bc1/i.test(compact) || /^tb1/i.test(compact) || /^ltc1/i.test(compact)) {
    return true;
  }

  const digits = (compact.match(/\d/g) || []).length;
  const letters = (compact.match(/[A-Za-z]/g) || []).length;
  return digits >= 4 && (compact.length >= 12 || letters >= 2);
}

function looksLikeShortAddressContinuationFragment(text) {
  const compact = normalizeOcrCandidateText(text);
  if (!compact || compact.length < 2 || compact.length > 16) return false;
  return /^[A-Fa-f0-9]+$/.test(compact) || /^[ac-hj-np-z02-9]+$/i.test(compact);
}

function previousLineSuggestsAddressContinuation(text) {
  const compact = normalizeOcrCandidateText(text);
  if (!compact) return false;

  if (compact.startsWith('0x')) {
    return compact.length >= 10 && compact.length < 42 && /^0x[A-Fa-f0-9]+$/.test(compact);
  }

  if (/^(bc1|tb1|ltc1)/i.test(compact)) {
    return compact.length >= 8;
  }

  return false;
}

function isLikelyContinuationLine(previousLine, currentLine) {
  if (!currentLine) {
    return false;
  }

  if (detectScreenshotLabelKind(currentLine.text)) {
    return false;
  }

  const previousCompact = normalizeOcrCandidateText(previousLine?.text || '');
  const currentCompact = normalizeOcrCandidateText(currentLine.text);

  const looksStructured = looksLikeStructuredValueFragment(currentLine.text);
  const looksShortAddressContinuation = (
    previousLineSuggestsAddressContinuation(previousLine?.text || '')
    && looksLikeShortAddressContinuationFragment(currentLine.text)
    && currentCompact.length <= Math.max(16, 42 - Math.max(previousCompact.length, 0))
  );

  if (!looksStructured && !looksShortAddressContinuation) {
    return false;
  }

  const previousBottom = Number(previousLine?.bottom || 0);
  const currentTop = Number(currentLine?.top || 0);
  const previousLeft = Number(previousLine?.left || 0);
  const currentLeft = Number(currentLine?.left || 0);
  const previousHeight = Number(previousLine?.height || 20);
  const currentHeight = Number(currentLine?.height || 20);

  const verticalGap = currentTop - previousBottom;
  const horizontalDelta = Math.abs(currentLeft - previousLeft);

  if (verticalGap > Math.max(40, Math.max(previousHeight, currentHeight) * 1.75)) {
    return false;
  }

  if (horizontalDelta > Math.max(180, Math.max(previousHeight, currentHeight) * 6)) {
    return false;
  }

  return true;
}

function normalizeScreenshotCandidateByKind(value, kind) {
  const compact = normalizeOcrCandidateText(value);
  if (!compact || isTruncatedScreenshotValue(compact)) {
    return '';
  }

  try {
    if (kind === 'transaction') {
      const normalized = normalizeTransactionHash(compact) || '';
      if (normalized) {
        return normalized;
      }

      if (/^0x[A-Fa-f0-9]{64}$/.test(compact)) {
        return compact.toLowerCase();
      }

      return '';
    }

    if (kind === 'address') {
      const normalized = normalizeAddress(compact) || '';
      if (normalized) {
        return normalized;
      }

      if (/^0x[A-Fa-f0-9]{40}$/.test(compact)) {
        return compact;
      }

      if (/^(bc1|tb1|ltc1)[ac-hj-np-z02-9]{14,120}$/i.test(compact)) {
        return compact.toLowerCase();
      }

      return '';
    }
  } catch (error) {
    console.debug('CasePrepBuddy screenshot candidate normalization failed', error);
  }

  return '';
}

function chooseBestCandidateFromFragments(fragments, kind) {
  const cleaned = (fragments || [])
    .map((fragment) => normalizeOcrCandidateText(fragment?.text || fragment))
    .filter(Boolean);

  let longestRaw = '';
  let prefixCount = 0;

  for (let count = 1; count <= cleaned.length; count += 1) {
    const raw = cleaned.slice(0, count).join('');
    if (!raw) continue;
    longestRaw = raw;
    prefixCount = count;

    if (isTruncatedScreenshotValue(raw)) {
      return { kind, raw, normalized: '', prefixCount: count, truncated: true };
    }

    const normalized = normalizeScreenshotCandidateByKind(raw, kind);
    if (normalized) {
      return { kind, raw, normalized, prefixCount: count, truncated: false };
    }
  }

  return {
    kind,
    raw: longestRaw,
    normalized: '',
    prefixCount,
    truncated: isTruncatedScreenshotValue(longestRaw)
  };
}

function chooseBestAnyCandidateFromFragments(fragments) {
  const cleaned = (fragments || [])
    .map((fragment) => normalizeOcrCandidateText(fragment?.text || fragment))
    .filter(Boolean);

  let longestRaw = '';
  let prefixCount = 0;

  for (let count = 1; count <= cleaned.length; count += 1) {
    const raw = cleaned.slice(0, count).join('');
    if (!raw) continue;
    longestRaw = raw;
    prefixCount = count;

    if (isTruncatedScreenshotValue(raw)) {
      return { kind: null, raw, normalized: '', prefixCount: count, truncated: true };
    }

    const normalizedTransactionHash = normalizeScreenshotCandidateByKind(raw, 'transaction');
    if (normalizedTransactionHash) {
      return {
        kind: 'transaction',
        raw,
        normalized: normalizedTransactionHash,
        prefixCount: count,
        truncated: false
      };
    }

    const normalizedAddress = normalizeScreenshotCandidateByKind(raw, 'address');
    if (normalizedAddress) {
      return {
        kind: 'address',
        raw,
        normalized: normalizedAddress,
        prefixCount: count,
        truncated: false
      };
    }
  }

  return {
    kind: null,
    raw: longestRaw,
    normalized: '',
    prefixCount,
    truncated: isTruncatedScreenshotValue(longestRaw)
  };
}

function truncateWarningLabel(value, maxLength = 36) {
  const text = normalizeOcrWhitespace(value);
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1)}…`;
}

function extractStructuredCandidatesFromOcrResult(result) {
  const lines = buildStructuredOcrLines(result);
  const txHashes = [];
  const addresses = [];
  const unusableFragments = [];
  const warnings = [];
  const consumed = new Set();

  const addValue = (collection, value) => {
    if (value && !collection.includes(value)) {
      collection.push(value);
    }
  };

  for (let index = 0; index < lines.length; index += 1) {
    if (consumed.has(index)) continue;

    const labelKind = detectScreenshotLabelKind(lines[index].text);
    if (!labelKind) continue;

    const labelLine = lines[index];
    const fragments = [];
    const inlineValue = stripKnownScreenshotLabelPrefix(labelLine.text, labelKind);

    if (inlineValue) {
      fragments.push({ index, text: inlineValue, line: labelLine });
    }

    let previousLine = labelLine;
    for (let nextIndex = index + 1; nextIndex < lines.length && fragments.length < SCREENSHOT_MAX_CONTINUATION_LINES; nextIndex += 1) {
      if (detectScreenshotLabelKind(lines[nextIndex].text)) break;
      if (!isLikelyContinuationLine(previousLine, lines[nextIndex])) {
        if (fragments.length) break;
        continue;
      }

      fragments.push({ index: nextIndex, text: lines[nextIndex].text, line: lines[nextIndex] });
      previousLine = lines[nextIndex];
    }

    if (!fragments.length) {
      consumed.add(index);
      continue;
    }

    const best = chooseBestCandidateFromFragments(fragments, labelKind);
    if (best.normalized) {
      if (labelKind === 'transaction') {
        addValue(txHashes, best.normalized);
      } else {
        addValue(addresses, best.normalized);
      }
      consumed.add(index);
      fragments.slice(0, best.prefixCount).forEach((fragment) => consumed.add(fragment.index));
      continue;
    }

    if (best.truncated && best.raw) {
      addValue(unusableFragments, best.raw);
      consumed.add(index);
      fragments.slice(0, best.prefixCount).forEach((fragment) => consumed.add(fragment.index));
      continue;
    }

    if (best.raw && best.raw.length >= 10) {
      addValue(
        warnings,
        `Could not confidently reconstruct one ${labelKind === 'transaction' ? 'transaction hash' : 'address'} near “${truncateWarningLabel(labelLine.text)}”.`
      );
      consumed.add(index);
      fragments.slice(0, best.prefixCount).forEach((fragment) => consumed.add(fragment.index));
    }
  }

  for (let index = 0; index < lines.length; index += 1) {
    if (consumed.has(index)) continue;
    if (!looksLikeStructuredValueFragment(lines[index].text)) continue;

    const fragments = [{ index, text: lines[index].text, line: lines[index] }];
    let previousLine = lines[index];

    for (let nextIndex = index + 1; nextIndex < lines.length && fragments.length < SCREENSHOT_MAX_CONTINUATION_LINES; nextIndex += 1) {
      if (consumed.has(nextIndex) || detectScreenshotLabelKind(lines[nextIndex].text)) break;
      if (!isLikelyContinuationLine(previousLine, lines[nextIndex])) break;

      fragments.push({ index: nextIndex, text: lines[nextIndex].text, line: lines[nextIndex] });
      previousLine = lines[nextIndex];
    }

    const best = chooseBestAnyCandidateFromFragments(fragments);
    if (best.normalized) {
      if (best.kind === 'transaction') {
        addValue(txHashes, best.normalized);
      } else if (best.kind === 'address') {
        addValue(addresses, best.normalized);
      }
      fragments.slice(0, best.prefixCount).forEach((fragment) => consumed.add(fragment.index));
      continue;
    }

    if (best.truncated && best.raw) {
      addValue(unusableFragments, best.raw);
      fragments.slice(0, best.prefixCount).forEach((fragment) => consumed.add(fragment.index));
    }
  }

  return {
    txHashes,
    addresses,
    unusableFragments: dedupePlainStrings(unusableFragments),
    warnings: dedupePlainStrings(warnings),
    rawText: String(result?.text || '').trim()
  };
}

function buildScreenshotExtractionSummary(result) {
  const txCount = Array.isArray(result?.txHashes) ? result.txHashes.length : 0;
  const addressCount = Array.isArray(result?.addresses) ? result.addresses.length : 0;
  const truncatedCount = Array.isArray(result?.unusableFragments) ? result.unusableFragments.length : 0;

  const parts = [];
  if (txCount) parts.push(`${txCount} transaction hash${txCount === 1 ? '' : 'es'}`);
  if (addressCount) parts.push(`${addressCount} address${addressCount === 1 ? '' : 'es'}`);
  if (truncatedCount) parts.push(`${truncatedCount} flagged fragment${truncatedCount === 1 ? '' : 's'}`);

  return parts.join(' · ');
}

function calculateScreenshotUpscale(width, height) {
  const smallestSide = Math.max(1, Math.min(Number(width) || 0, Number(height) || 0));
  if (smallestSide >= 1400) return 1;
  if (smallestSide >= 900) return 1.35;
  return 2;
}

function preprocessScreenshotImageData(imageData) {
  const data = imageData?.data;
  if (!data || !data.length) return imageData;

  let luminanceTotal = 0;
  const pixelCount = data.length / 4;

  for (let index = 0; index < data.length; index += 4) {
    const luminance = (data[index] * 0.299) + (data[index + 1] * 0.587) + (data[index + 2] * 0.114);
    luminanceTotal += luminance;
  }

  const averageLuminance = luminanceTotal / Math.max(pixelCount, 1);
  const shouldInvert = averageLuminance < 115;

  for (let index = 0; index < data.length; index += 4) {
    let gray = (data[index] * 0.299) + (data[index + 1] * 0.587) + (data[index + 2] * 0.114);
    if (shouldInvert) {
      gray = 255 - gray;
    }

    gray = ((gray - 128) * 1.45) + 128;
    gray = Math.max(0, Math.min(255, gray));

    if (gray > 214) gray = 255;
    if (gray < 36) gray = 0;

    data[index] = gray;
    data[index + 1] = gray;
    data[index + 2] = gray;
    data[index + 3] = 255;
  }

  return imageData;
}

export default {
  name: 'CasePrepBuddy',
  components: {
    GlossaryTerm,
    PrepSummaryTray,
    CasePrepTabularImportStep,
    CasePrepExposureReviewStep
  },
 data() {
  return {
    loading: true,
    prepRun: null,
    screenshotOcrReady: false,
    screenshotOcrPreparing: false,
    screenshotOcrReading: false,
    screenshotOcrProgress: 0,
    screenshotOcrStatus: '',
    screenshotOcrError: '',
    screenshotExtractedText: '',
    screenshotExtractionResults: [],
    extractionSelectionDraft: null,
    lastRunPreview: null,
    currentStep: 'launch',
    stepHistory: [],
    saveState: 'idle',
    saveTimer: null,
    flashMessage: '',
    errorMessage: '',
    noticeTopOffset: 76,
    glossary: casePrepBuddyGlossary,
    sourceOptions: casePrepBuddySourceOptions,
    educationStages: EDUCATION_STAGES,
    educationLevel: 0,
    activeValidationBatchId: null,
    dismissedReturnToIntakePrompt: false,
    addressForwardFromDatePicker: null,
    addressForwardToDatePicker: null,
    isApplyingBrowserPopstate: false,
    confirmingFreshStart: false,
    browserWorkflowHistoryReady: false,

    forms: {
      addressExposure: {
        address: '',
        label: '',
        note: ''
      },
      addressForward: {
        addressesText: '',
        selectionScope: 'all',
        dateFrom: '',
        dateTo: '',
        tokenType: '',
        amountMin: '',
        amountMax: '',
        candidateTransactionsText: '',
        note: ''
      },
      transaction: {
        mode: 'single',
        singleHash: '',
        bulkHashes: '',
        commonToAddress: '',
        note: ''
      }
    },

    placeholderForms: {
      csv: { note: '', knownTransactionsText: '' },
      spreadsheet: { note: '', knownTransactionsText: '' },
      screenshots: {
        note: '',
        knownTransactionsText: '',
        extractedAddressesText: '',
        manualExtractedText: '',
        rawExtractedText: ''
      },
      document: {
        note: '',
        knownTransactionsText: '',
        extractedAddressesText: '',
        manualExtractedText: '',
        rawExtractedText: ''
      }
    },

    screenshotIntake: {
      files: [],
      isDragging: false,
      scanState: 'idle',
      scanProgress: 0,
      currentFileName: '',
      statusMessage: '',
      unusableFragments: [],
      warnings: [],
      engineStatus: 'unknown'
    },

    documentIntake: {
      files: [],
      isDragging: false,
      engineStatus: 'unknown',
      readState: 'idle',
      readProgress: 0,
      currentFileName: '',
      statusMessage: '',
      warnings: [],
      unreadableFiles: []
    },

    addressLookup: {
      originSourceItemId: '',
      originSourceLabel: '',
      activeAddress: '',
      direction: 'outgoing',
      state: 'idle',
      statusMessage: '',
      errorMessage: '',
      results: [],
      currentPage: 1,
      resultsPerPage: 10,
      resultLimit: 50,
      lastLookupAddress: '',
      lastLookupDirection: '',
      lookupTriggeredAt: '',
      truncatedResultSet: false
    },

    validationCheck: {
      state: 'idle',
      statusMessage: '',
      errorMessage: '',
      checkedCount: 0,
      totalCount: 0,
      currentCandidateId: ''
    },

    toAddressPicker: {
      open: false,
      candidateId: '',
      txHash: '',
      suggestions: []
    },

    validationConfirmGuard: {
      open: false
    }
  };
},
  computed: {
shouldShowReturnToIntakePrompt() {
  const sourceCount = this.prepRun?.sourceItems?.length || 0;
  const stagedCount = this.prepRun?.stagedTransactions?.length || 0;

  return this.currentStep === 'intake'
    && (sourceCount > 0 || stagedCount > 0)
    && !this.dismissedReturnToIntakePrompt;
},
    canGoBack() {
      return this.stepHistory.length > 0;
    },
    hasPendingValidation() {
      return this.validationBatches.length > 0;
    },
    validationBatches() {
      const batches = Array.isArray(this.prepRun?.pendingValidationBatches)
        ? this.prepRun.pendingValidationBatches
        : [];

      return [...batches].sort((a, b) =>
        String(a.createdAt || '').localeCompare(String(b.createdAt || ''))
      );
    },
    validationDraft() {
      if (!this.validationBatches.length) return null;

      if (this.activeValidationBatchId) {
        const match = this.validationBatches.find((batch) => batch.id === this.activeValidationBatchId);
        if (match) return match;
      }

      return this.validationBatches[0] || null;
    },
    hasMultipleValidationBatches() {
      return this.validationBatches.length > 1;
    },
    activeValidationBatchIndex() {
      if (!this.validationDraft) return 0;
      return Math.max(0, this.validationBatches.findIndex((batch) => batch.id === this.validationDraft.id));
    },
    hasRunContent() {
      if (!this.prepRun) return false;

      return (
        (this.prepRun.sourceItems || []).length > 0 ||
        (this.prepRun.stagedTransactions || []).length > 0
      );
    },
    showPrepSummaryTray() {
      return this.hasRunContent && !['caseBuddyPreview', 'addressExposure', 'validation'].includes(this.currentStep);
    },
    prepRunForTray() {
      if (!this.prepRun) return null;

      const { suggestedLabels, ...rest } = this.prepRun;
      console.log(suggestedLabels);
      return {
        ...rest,
        hideSuggestedLabels: true
      };
    },
    isReadingScreenshots() {
      return this.screenshotIntake.scanState === 'preparing' || this.screenshotIntake.scanState === 'running';
    },
    screenshotReadButtonLabel() {
      if (this.isReadingScreenshots) {
        return 'Reading screenshots locally…';
      }

      if (this.screenshotIntake.engineStatus === 'loading') {
        return 'Preparing local reader…';
      }

      return 'Read screenshots locally';
    },
    isTabularImportStep() {
      return ['csv', 'spreadsheet'].includes(this.currentStep);
    },
  isPlaceholderStep() {
  return ['csv', 'spreadsheet'].includes(this.currentStep);
},
isDocumentStep() {
  return this.currentStep === 'document';
},
    currentSourceOption() {
      return getCasePrepBuddySourceOptionByStep(this.currentStep) || {
        title: 'Placeholder source',
        description: 'Record this source in the prep run.'
      };
    },
    currentEducationStage() {
      return this.educationStages[Math.min(this.educationLevel, this.educationStages.length - 1)];
    },
    validationSummary() {
      if (!this.validationDraft) {
        return summarizeValidationDraft([], []);
      }

      return summarizeValidationDraft(
        this.validationDraft.candidates,
        this.validationDraft.invalidEntries
      );
    },
    stagedTransactions() {
      return [...(this.prepRun?.stagedTransactions || [])].sort((a, b) =>
        String(a.createdAt).localeCompare(String(b.createdAt))
      );
    },
    stagedTransactionsNeedReview() {
      return this.stagedTransactions.filter((transaction) =>
        transaction.validationState === 'needs_review'
        || (transaction.requiresToAddress === 'yes' && !String(transaction.toAddress || '').trim())
      );
    },
    stagedTransactionsWithToAddressCount() {
      return this.stagedTransactions.filter((transaction) => Boolean(String(transaction.toAddress || '').trim())).length;
    },
    stagedTransactionsReadyCount() {
      return this.stagedTransactions.filter((transaction) => transaction.validationState === 'ready').length;
    },
    uncheckedValidationCandidateCount() {
      const candidates = Array.isArray(this.validationDraft?.candidates) ? this.validationDraft.candidates : [];
      return candidates.filter((candidate) => !candidate.validationCheckedAt && (!candidate.lookupStatus || candidate.lookupStatus === 'not_attempted')).length;
    },
    suggestedLabels() {
      return [];
    },
    sourceItems() {
      return [...(this.prepRun?.sourceItems || [])].sort((a, b) =>
        String(b.updatedAt || b.createdAt).localeCompare(String(a.updatedAt || a.createdAt))
      );
    },
    handoffNewlineList() {
      return buildNewlineHashList(this.stagedTransactions);
    },
    handoffHashAndToAddressList() {
      return buildHashWithOptionalToAddressList(this.stagedTransactions);
    },
    handoffTsv() {
      return buildCaseBuddyCreateCaseTsv(this.stagedTransactions);
    },
    caseBuddyPreviewRows() {
      const baseRows = this.stagedTransactions.map((transaction, index) => ({
        rowNumber: index + 1,
        colA: '',
        colB: transaction.txHash || '',
        colC: '',
        colD: transaction.toAddress || '',
        transaction
      }));

      const minimumRows = 12;
      const rows = [...baseRows];
      while (rows.length < minimumRows) {
        rows.push({
          rowNumber: rows.length + 1,
          colA: '',
          colB: '',
          colC: '',
          colD: '',
          transaction: null
        });
      }

      return rows;
    },
    labelsExport() {
      return '';
    },
    addressForwardParsedAddresses() {
      return parseAddressInput(this.forms.addressForward.addressesText).valid;
    },
    addressLookupResults() {
      return Array.isArray(this.addressLookup?.results) ? this.addressLookup.results : [];
    },
    addressLookupTotalPages() {
      const total = this.addressLookupResults.length;
      const size = Math.max(1, Number(this.addressLookup.resultsPerPage || 10));
      return Math.max(1, Math.ceil(total / size));
    },
    paginatedAddressLookupResults() {
      const page = Math.max(1, Number(this.addressLookup.currentPage || 1));
      const size = Math.max(1, Number(this.addressLookup.resultsPerPage || 10));
      const start = (page - 1) * size;
      return this.addressLookupResults.slice(start, start + size);
    },
    selectedAddressLookupResultsCount() {
      return this.addressLookupResults.filter((result) => result.selected).length;
    },
    addressLookupHasResults() {
      return this.addressLookupResults.length > 0;
    },
    addressExposurePreviewTitle() {
      const label = String(this.forms.addressExposure.label || '').trim();
      const address = String(this.forms.addressExposure.address || '').trim();

      if (label) return label;
      if (address) return 'Unlabeled address under review';
      return 'Address analysis preview';
    },
    addressExposurePreviewAddress() {
      return String(this.forms.addressExposure.address || '').trim()
        || 'Enter an address to preview the future analysis layout.';
    },
    addressExposurePreviewAddressCompact() {
      const value = String(this.forms.addressExposure.address || '').trim();

      if (!value) return 'Address profile';
      if (value.length <= 22) return value;

      return `${value.slice(0, 10)}…${value.slice(-8)}`;
    },
    addressExposurePreviewNote() {
      return String(this.forms.addressExposure.note || '').trim();
    }
  },

  watch: {
 currentStep(newStep, oldStep) {
  if (newStep === 'addressForward') {
    this.$nextTick(() => {
      if (this.forms.addressForward.selectionScope === 'some') {
        this.initAddressForwardDatePickers();
      }
    });
  }

  if (oldStep === 'addressForward' && newStep !== 'addressForward') {
    this.destroyAddressForwardDatePickers();
  }

  if (newStep === 'document') {
    this.warmupDocumentReaders();
  }

  if (newStep !== oldStep) {
    this.scheduleWorkflowStepScroll();
  }
},

  'forms.addressForward.selectionScope'(newValue) {
    if (newValue === 'some') {
      this.$nextTick(() => {
        this.initAddressForwardDatePickers();
      });
      return;
    }

    if (newValue === 'all') {
      this.destroyAddressForwardDatePickers();
      this.clearAddressForwardNarrowingFields();
    }
  },
  'forms.addressForward.addressesText'() {
    this.syncAddressLookupActiveAddress();

    if (!this.addressForwardParsedAddresses.length) {
      this.resetAddressLookupState({ preserveOrigin: true, preserveDirection: true });
    }
  }
},

  async mounted() {
    await this.bootstrap();

      this.initializeBrowserWorkflowHistory();
  window.addEventListener('popstate', this.handleBrowserPopstate);
  },


async beforeUnmount() {
 window.clearTimeout(this.saveTimer);
  this.destroyAddressForwardDatePickers();
  this.resetScreenshotIntakeSession({ clearForm: false });
  window.removeEventListener('popstate', this.handleBrowserPopstate);

  try {
    await terminateCasePrepOcr();
  } catch (error) {
    console.warn('CasePrepBuddy OCR cleanup failed', error);
  }
},
  methods: {

    async bootstrap() {
      this.loading = true;

      try {
        const lastRun = await loadLastPrepRun();
        this.lastRunPreview = lastRun ? this.buildLastRunPreview(lastRun) : null;
      } catch (error) {
        console.debug('CasePrepBuddy bootstrap failed', error);
        this.errorMessage = 'Could not check for a resumable prep run.';
      } finally {
        this.loading = false;
      }
    },
    buildLastRunPreview(run) {
      return {
        id: run.id,
        draftTitle: run.draftTitle || '',
        updatedAt: run.updatedAt,
        sourceCount: Array.isArray(run.sourceItems) ? run.sourceItems.length : 0,
        transactionCount: Array.isArray(run.stagedTransactions) ? run.stagedTransactions.length : 0
      };
    },
    ensurePrepRunCollections(run = this.prepRun) {
      if (!run) return run;

      if (!Array.isArray(run.sourceItems)) run.sourceItems = [];
      if (!Array.isArray(run.stagedTransactions)) run.stagedTransactions = [];
      if (!Array.isArray(run.suggestedLabels)) run.suggestedLabels = [];
      if (!Array.isArray(run.pendingValidationBatches)) run.pendingValidationBatches = [];

      return run;
    },
    normalizeValidationCandidate(candidate = {}) {
      return {
        id: candidate.id || createClientId('candidate'),
        txHash: String(candidate.txHash || '').trim(),
        toAddress: String(candidate.toAddress || '').trim(),
        network: String(candidate.network || '').trim(),
        validationState: candidate.validationState || (candidate.txHash ? 'unverified' : 'needs_review'),
        notes: String(candidate.notes || '').trim(),
        warnings: Array.isArray(candidate.warnings) ? candidate.warnings.filter(Boolean) : [],
        lookupStatus: candidate.lookupStatus || 'not_attempted',
        requiresToAddress: candidate.requiresToAddress || 'unknown',
        toAddressSuggestions: Array.isArray(candidate.toAddressSuggestions) ? candidate.toAddressSuggestions : [],
        validationCheckedAt: candidate.validationCheckedAt || '',
        sourceItemId: candidate.sourceItemId || '',
        provenanceSourceIds: Array.isArray(candidate.provenanceSourceIds) ? candidate.provenanceSourceIds.filter(Boolean) : [],
        provenance: Array.isArray(candidate.provenance) ? candidate.provenance : []
      };
    },
    normalizeValidationBatch(batch = {}) {
      const createdAt = batch.createdAt || new Date().toISOString();
      const normalizedCandidates = (Array.isArray(batch.candidates) ? batch.candidates : [])
        .map((candidate) => this.normalizeValidationCandidate(candidate))
        .filter(Boolean);

      return {
        id: batch.id || createClientId('validation_batch'),
        sourceItemId: batch.sourceItemId || '',
        sourceLabel: batch.sourceLabel || 'Imported candidates',
        sourceType: batch.sourceType || '',
        invalidEntries: Array.isArray(batch.invalidEntries) ? batch.invalidEntries : [],
        candidates: normalizedCandidates,
        importSummary: batch.importSummary || null,
        createdAt,
        updatedAt: batch.updatedAt || createdAt
      };
    },
    hydratePrepRun(run) {
      if (!run) return null;

      const hydratedRun = { ...run };
      this.ensurePrepRunCollections(hydratedRun);
      hydratedRun.pendingValidationBatches = hydratedRun.pendingValidationBatches
        .map((batch) => this.normalizeValidationBatch(batch))
        .filter((batch) => batch.candidates.length);

      return hydratedRun;
    },
    setActiveValidationBatch(batchId) {
      this.activeValidationBatchId = batchId || (this.validationBatches[0]?.id || null);
    },
    createPendingValidationBatch({ sourceItemId, sourceLabel, sourceType = '', invalidEntries = [], candidates = [], importSummary = null }) {
      if (!this.prepRun) return null;

      this.ensurePrepRunCollections(this.prepRun);
      const batch = this.normalizeValidationBatch({
        id: createClientId('validation_batch'),
        sourceItemId,
        sourceLabel,
        sourceType,
        invalidEntries,
        candidates,
        importSummary,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      this.prepRun.pendingValidationBatches.push(batch);
      this.activeValidationBatchId = batch.id;
      return batch;
    },
    removePendingValidationBatch(batchId) {
      if (!this.prepRun) return;

      this.prepRun.pendingValidationBatches = (this.prepRun.pendingValidationBatches || []).filter(
        (batch) => batch.id !== batchId
      );

      const nextBatch = this.prepRun.pendingValidationBatches[0] || null;
      this.activeValidationBatchId = nextBatch ? nextBatch.id : null;
    },
    clearMessages() {
      this.flashMessage = '';
      this.errorMessage = '';
    },
    setFlash(message) {
      this.flashMessage = message;
      this.errorMessage = '';
    },
    setError(message) {
      this.errorMessage = message;
      this.flashMessage = '';
    },
 
  moveToStep(step, { keepMessages = false } = {}) {
  if (this.currentStep === step) {
    if (!keepMessages) {
      this.clearMessages();
    }
    return;
  }

  if (this.currentStep) {
    this.stepHistory.push(this.currentStep);
  }

  this.currentStep = step;

  if (!keepMessages) {
    this.clearMessages();
  }

  this.pushBrowserWorkflowStep(step);
},

replaceStep(step, { keepMessages = false } = {}) {
  this.currentStep = step;
  this.stepHistory = [];

  if (!keepMessages) {
    this.clearMessages();
  }

  this.replaceBrowserWorkflowStep(step);
},

goBack() {
  if (!this.canGoBack) return;
  window.history.back();
},

goToRootIntake(options = {}) {
  const { keepMessages = false } = options;

  if (this.currentStep === 'intake') {
    if (!keepMessages) {
      this.clearMessages();
    }
    return;
  }

  this.moveToStep('intake', { keepMessages });
},

openReview(options = {}) {
  this.moveToStep('review', options);
},

resumeValidation() {
  if (!this.validationBatches.length) {
    this.setError('There are no pending validation batches to resume.');
    return;
  }

  if (!this.activeValidationBatchId) {
    this.activeValidationBatchId = this.validationBatches[0].id;
  }

  this.moveToStep('validation');
},
    buildSourceItem({ type, title, payload = {}, placeholder = false, status = 'captured' }) {
      const timestamp = new Date().toISOString();

      return {
        id: createClientId('source'),
        type,
        title,
        payload,
        placeholder,
        status,
        createdAt: timestamp,
        updatedAt: timestamp
      };
    },
    buildSuggestedLabel({ address, label, sourceItemId }) {
      return {
        id: createClientId('label'),
        address,
        label: String(label || '').trim(),
        sourceItemId,
        createdAt: new Date().toISOString()
      };
    },
    queueSave() {
      if (!this.prepRun) return;
      this.saveState = 'saving';
      window.clearTimeout(this.saveTimer);
      this.saveTimer = window.setTimeout(() => {
        this.persistRun();
      }, 250);
    },
    async persistRun() {
      if (!this.prepRun) return;

      this.ensurePrepRunCollections(this.prepRun);
      this.saveState = 'saving';

      try {
        const saved = await savePrepRun(this.prepRun);
        this.prepRun.updatedAt = saved.updatedAt;
        this.lastRunPreview = this.buildLastRunPreview(this.prepRun);
        this.saveState = 'saved';
        window.setTimeout(() => {
          if (this.saveState === 'saved') {
            this.saveState = 'idle';
          }
        }, 1500);
      } catch (error) {
        console.debug('CasePrepBuddy save failed', error);
        this.saveState = 'error';
        this.setError('Could not save the prep run locally.');
      }
    },
 async startNewRun() {
  this.clearMessages();
  this.confirmingFreshStart = false;
  this.prepRun = this.hydratePrepRun(createEmptyPrepRun());
  this.currentStep = 'intake';
  this.stepHistory = [];
  this.educationLevel = 0;
  this.activeValidationBatchId = null;
  this.dismissedReturnToIntakePrompt = false;
  this.resetForms();
  this.replaceBrowserWorkflowStep('intake');
  await this.persistRun();

  try {
    await logEvent('case_prep_run_started', { mode: 'new' });
  } catch (error) {
    console.debug('CasePrepBuddy analytics failed for startNewRun', error);
  }
},
   async resumeLastRun() {
  this.clearMessages();
this.confirmingFreshStart = false;
  try {
    const run = await loadLastPrepRun();

    if (!run) {
      this.setError('There is no local prep run available to resume.');
      return;
    }

    this.prepRun = this.hydratePrepRun(run);
    this.currentStep = 'intake';
    this.stepHistory = [];
    this.educationLevel = 0;
    this.activeValidationBatchId = this.validationBatches[0]?.id || null;
    this.dismissedReturnToIntakePrompt = false;
    this.resetForms();
    this.replaceBrowserWorkflowStep('intake');

    try {
      await logEvent('case_prep_run_started', { mode: 'resume', run_id: run.id });
    } catch (error) {
      console.debug('CasePrepBuddy analytics failed for resumeLastRun', error);
    }
  } catch (error) {
    console.debug('CasePrepBuddy resume failed', error);
    this.setError('Could not resume the last prep run.');
  }
},
    resetForms() {
      this.forms.addressExposure = { address: '', label: '', note: '' };
      this.forms.addressForward = {
        addressesText: '',
        selectionScope: 'all',

  dateFrom: '',
  dateTo: '',
        tokenType: '',
        amountMin: '',
        amountMax: '',
        candidateTransactionsText: '',
        note: ''
      };
      this.forms.transaction = {
        mode: 'single',
        singleHash: '',
        bulkHashes: '',
        commonToAddress: '',
        note: ''
      };
    this.placeholderForms = {
  csv: { note: '', knownTransactionsText: '' },
  spreadsheet: { note: '', knownTransactionsText: '' },
  screenshots: {
    note: '',
    knownTransactionsText: '',
    extractedAddressesText: '',
    manualExtractedText: '',
    rawExtractedText: ''
  },
  document: {
    note: '',
    knownTransactionsText: '',
    extractedAddressesText: '',
    manualExtractedText: '',
    rawExtractedText: ''
  }
};

this.documentIntake = {
  files: [],
  isDragging: false,
  engineStatus: 'unknown',
  readState: 'idle',
  readProgress: 0,
  currentFileName: '',
  statusMessage: '',
  warnings: [],
  unreadableFiles: []
};

this.resetScreenshotIntakeSession({ clearForm: false });
this.resetAddressLookupState();
this.extractionSelectionDraft = null;
    },
    async warmupScreenshotOcr() {
      if (this.screenshotOcrReady || this.screenshotOcrPreparing) {
        return;
      }

      this.screenshotOcrPreparing = true;
      this.screenshotOcrError = '';
      this.screenshotOcrStatus = 'Preparing local reader...';
      this.screenshotOcrProgress = 0;
      this.screenshotIntake.engineStatus = 'loading';

      try {
        await warmupCasePrepOcr((progress) => {
          const statusText = humanizeScreenshotOcrStatus(progress?.status) || 'Preparing local reader...';
          const percent = Math.max(0, Math.min(100, Math.round((progress?.progress || 0) * 100)));

          this.screenshotOcrStatus = statusText;
          this.screenshotOcrProgress = percent;
          this.screenshotIntake.statusMessage = statusText;
          this.screenshotIntake.scanProgress = percent;
        });

        this.screenshotOcrReady = true;
        this.screenshotOcrStatus = 'Local reader ready.';
        this.screenshotIntake.engineStatus = 'ready';
        this.screenshotIntake.statusMessage = '';
        this.screenshotIntake.scanProgress = 0;
      } catch (error) {
        console.error('CasePrepBuddy warmupScreenshotOcr failed', error);
        this.screenshotOcrError = 'Could not start the local OCR reader.';
        this.screenshotOcrStatus = '';
        this.screenshotIntake.engineStatus = 'error';
      } finally {
        this.screenshotOcrPreparing = false;
      }
    },
    openScreenshotFilePicker() {
      const input = this.$refs.screenshotFileInput;
      const element = Array.isArray(input) ? input[0] : input;
      if (element && typeof element.click === 'function') {
        element.click();
      }
    },
    handleScreenshotDragEnter() {
      this.screenshotIntake.isDragging = true;
    },
    handleScreenshotDragOver() {
      this.screenshotIntake.isDragging = true;
    },
    handleScreenshotDragLeave(event) {
      if (event?.currentTarget && event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) {
        return;
      }

      this.screenshotIntake.isDragging = false;
    },
    handleScreenshotDrop(event) {
      this.screenshotIntake.isDragging = false;
      const files = Array.from(event?.dataTransfer?.files || []);
      this.appendScreenshotFiles(files);
    },
    handleScreenshotFileSelection(event) {
      const files = Array.from(event?.target?.files || []);
      this.appendScreenshotFiles(files);

      if (event?.target) {
        event.target.value = '';
      }
    },
    appendScreenshotFiles(files) {
      if (!Array.isArray(files) || !files.length) {
        return;
      }

      const imageFiles = files.filter((file) => String(file?.type || '').startsWith('image/'));

      if (!imageFiles.length) {
        this.setError('Only image files can be added in the screenshots path.');
        return;
      }

      const existingKeys = new Set(
        (this.screenshotIntake.files || []).map((item) => `${item.name}::${item.size}::${item.lastModified}`)
      );

      const additions = [];
      for (const file of imageFiles) {
        const dedupeKey = `${file.name}::${file.size}::${file.lastModified}`;
        if (existingKeys.has(dedupeKey)) {
          continue;
        }

        existingKeys.add(dedupeKey);
        additions.push({
          id: createClientId('screenshot_file'),
          file,
          name: file.name,
          size: Number(file.size || 0),
          type: file.type || 'image/*',
          lastModified: file.lastModified || null,
          previewUrl: this.buildScreenshotPreviewUrl(file),
          scanStatus: 'queued',
          lastExtractionSummary: ''
        });
      }

      if (!additions.length) {
        this.setFlash('Those screenshots were already attached in this browser tab.');
        return;
      }

      this.screenshotIntake.files = [...this.screenshotIntake.files, ...additions];
      this.screenshotIntake.scanState = 'idle';
      this.screenshotIntake.scanProgress = 0;
      this.screenshotIntake.currentFileName = '';
      this.screenshotIntake.statusMessage = '';
      this.setFlash(`${additions.length} screenshot${additions.length === 1 ? '' : 's'} attached locally.`);
    },
    buildScreenshotPreviewUrl(file) {
      if (typeof window === 'undefined' || typeof window.URL?.createObjectURL !== 'function') {
        return '';
      }

      try {
        return window.URL.createObjectURL(file);
      } catch (error) {
        console.debug('CasePrepBuddy screenshot preview URL failed', error);
        return '';
      }
    },
    revokeScreenshotPreviewUrl(url) {
      if (!url || typeof window === 'undefined' || typeof window.URL?.revokeObjectURL !== 'function') {
        return;
      }

      try {
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.debug('CasePrepBuddy screenshot preview revoke failed', error);
      }
    },
    removeScreenshotFile(fileId) {
      const match = (this.screenshotIntake.files || []).find((file) => file.id === fileId);
      if (match?.previewUrl) {
        this.revokeScreenshotPreviewUrl(match.previewUrl);
      }

      this.screenshotIntake.files = (this.screenshotIntake.files || []).filter((file) => file.id !== fileId);

      if (match?.lastExtractionSummary) {
        this.setFlash('Screenshot removed. Review the extracted text below and delete anything that only came from that image.');
      }
    },
    clearScreenshotFiles() {
      for (const file of this.screenshotIntake.files || []) {
        if (file.previewUrl) {
          this.revokeScreenshotPreviewUrl(file.previewUrl);
        }
      }

      this.screenshotIntake.files = [];
      this.screenshotIntake.isDragging = false;
      this.screenshotIntake.scanState = 'idle';
      this.screenshotIntake.scanProgress = 0;
      this.screenshotIntake.currentFileName = '';
      this.screenshotIntake.statusMessage = '';

      const input = this.$refs.screenshotFileInput;
      const element = Array.isArray(input) ? input[0] : input;
      if (element) {
        element.value = '';
      }
    },
    resetScreenshotIntakeSession({ clearForm = false } = {}) {
      this.clearScreenshotFiles();
      this.screenshotIntake.unusableFragments = [];
      this.screenshotIntake.warnings = [];
      this.screenshotIntake.engineStatus = this.screenshotOcrReady ? 'ready' : 'unknown';
      this.screenshotIntake.scanState = 'idle';
      this.screenshotIntake.scanProgress = 0;
      this.screenshotIntake.currentFileName = '';
      this.screenshotIntake.statusMessage = '';

      if (clearForm) {
        this.placeholderForms.screenshots = {
          note: '',
          knownTransactionsText: '',
          extractedAddressesText: '',
          manualExtractedText: '',
          rawExtractedText: ''
        };
      }
    },
    formatBytes(value) {
      const size = Number(value || 0);
      if (!Number.isFinite(size) || size <= 0) {
        return '0 B';
      }

      const units = ['B', 'KB', 'MB', 'GB'];
      const index = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
      const amount = size / (1024 ** index);
      return `${amount >= 10 || index === 0 ? Math.round(amount) : amount.toFixed(1)} ${units[index]}`;
    },
    formatScreenshotScanStatus(status) {
      const map = {
        queued: 'Queued',
        running: 'Reading locally',
        scanned: 'Read complete',
        scanned_empty: 'No clear values found',
        failed: 'Could not read'
      };

      return map[status] || 'Queued';
    },
    mergeScreenshotExtraction(result, options = {}) {
      if (!result) return;

      const { includeRawText = false } = options;
      const txHashes = Array.isArray(result.txHashes) ? result.txHashes : [];
      const addresses = Array.isArray(result.addresses) ? result.addresses : [];
      const unusableFragments = Array.isArray(result.unusableFragments) ? result.unusableFragments : [];
      const warnings = Array.isArray(result.warnings) ? result.warnings : [];

      this.placeholderForms.screenshots.knownTransactionsText = mergeUniqueLines(
        this.placeholderForms.screenshots.knownTransactionsText,
        txHashes,
        normalizeTransactionHash
      );
      this.placeholderForms.screenshots.extractedAddressesText = mergeUniqueLines(
        this.placeholderForms.screenshots.extractedAddressesText,
        addresses,
        normalizeAddress
      );
      this.screenshotIntake.unusableFragments = dedupePlainStrings([
        ...this.screenshotIntake.unusableFragments,
        ...unusableFragments
      ]);
      this.screenshotIntake.warnings = dedupePlainStrings([
        ...this.screenshotIntake.warnings,
        ...warnings
      ]);

      if (includeRawText && result.rawText) {
        this.placeholderForms.screenshots.rawExtractedText = appendRawTextBlock(
          this.placeholderForms.screenshots.rawExtractedText,
          result.rawText
        );
      }
    },
    applyPastedScreenshotText(options = {}) {
      const { quiet = false } = options;
      const manualText = String(this.placeholderForms.screenshots.manualExtractedText || '').trim();

      if (!manualText) {
        if (!quiet) {
          this.setError('Paste some copied text from the screenshot before using this helper.');
        }
        return null;
      }

      const parsed = extractStructuredCandidatesFromOcrResult({ text: manualText, tsv: '' });
      this.mergeScreenshotExtraction(parsed, { includeRawText: false });

      if (!quiet) {
        const txCount = parsed.txHashes.length;
        const addressCount = parsed.addresses.length;
        this.setFlash(
          `Pasted text added ${txCount} transaction hash${txCount === 1 ? '' : 'es'} and ${addressCount} address${addressCount === 1 ? '' : 'es'} for review.`
        );
      }

      return parsed;
    },
    async waitForNextFrame() {
      await new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
    },
    async prepareScreenshotCanvas(file) {
      const bitmap = await this.loadImageBitmapForScreenshot(file);
      const scale = calculateScreenshotUpscale(bitmap.width, bitmap.height);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));

      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) {
        throw new Error('Could not get a 2D context for the screenshot canvas.');
      }

      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

      try {
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        preprocessScreenshotImageData(imageData);
        context.putImageData(imageData, 0, 0);
      } catch (error) {
        console.debug('CasePrepBuddy screenshot preprocessing failed', error);
      }

      if (bitmap && typeof bitmap.close === 'function') {
        bitmap.close();
      }

      return canvas;
    },
    async loadImageBitmapForScreenshot(file) {
      if (typeof window.createImageBitmap === 'function') {
        return window.createImageBitmap(file);
      }

      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Could not read the image file.'));
        reader.onload = () => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = () => reject(new Error('Could not decode the image file.'));
          image.src = reader.result;
        };
        reader.readAsDataURL(file);
      });
    },
    async readScreenshotsLocally() {
      const files = this.screenshotIntake.files || [];

      if (!files.length) {
        this.setError('Add at least one screenshot first.');
        return;
      }

      await this.warmupScreenshotOcr();
      if (!this.screenshotOcrReady) {
        this.screenshotIntake.engineStatus = 'error';
        this.setError('Could not start the local screenshot reader. You can still paste copied text or save this source for later review.');
        return;
      }

      this.screenshotIntake.scanState = 'preparing';
      this.screenshotIntake.scanProgress = 0;
      this.screenshotIntake.currentFileName = '';
      this.screenshotIntake.statusMessage = 'Preparing local screenshot read';
      this.screenshotIntake.engineStatus = 'ready';
      this.screenshotIntake.unusableFragments = [];
      this.screenshotIntake.warnings = [];

      this.screenshotOcrReading = true;
      this.screenshotOcrError = '';
      this.screenshotOcrStatus = 'Reading screenshots locally...';
      this.screenshotOcrProgress = 0;
      this.screenshotExtractedText = '';
      this.screenshotExtractionResults = [];

      const txSet = new Set();
      const addressSet = new Set();
      let fragmentCount = 0;

      try {
        const allResults = [];
        await this.waitForNextFrame();

        for (let index = 0; index < files.length; index += 1) {
          const fileRecord = files[index];
          const sourceFile = fileRecord?.file || fileRecord;

          fileRecord.scanStatus = 'running';
          fileRecord.lastExtractionSummary = '';
          this.screenshotIntake.scanState = 'running';
          this.screenshotIntake.currentFileName = fileRecord.name || sourceFile?.name || `Screenshot ${index + 1}`;

          await this.waitForNextFrame();
          const preparedImage = await this.prepareScreenshotCanvas(sourceFile);
          const data = await recognizeCasePrepImage(preparedImage, (progress) => {
            const normalizedProgress = Math.max(0, Math.min(1, Number(progress?.progress) || 0));
            const overallProgress = ((index + normalizedProgress) / Math.max(files.length, 1)) * 100;
            const statusText = humanizeScreenshotOcrStatus(progress?.status)
              || `Reading screenshot ${index + 1} of ${files.length}`;

            this.screenshotIntake.scanState = 'running';
            this.screenshotIntake.scanProgress = Math.max(0, Math.min(100, Math.round(overallProgress)));
            this.screenshotIntake.statusMessage = statusText;
            this.screenshotIntake.currentFileName = fileRecord.name || sourceFile?.name || '';

            this.screenshotOcrStatus = statusText;
            this.screenshotOcrProgress = this.screenshotIntake.scanProgress;
          });

          const parsed = extractStructuredCandidatesFromOcrResult(data || { text: '', tsv: '' });
          this.mergeScreenshotExtraction(parsed, { includeRawText: true });

          parsed.txHashes.forEach((hash) => txSet.add(hash));
          parsed.addresses.forEach((address) => addressSet.add(address));
          fragmentCount += parsed.unusableFragments.length;

          const summary = buildScreenshotExtractionSummary(parsed);
          fileRecord.lastExtractionSummary = summary || 'No clear transaction hashes or addresses found';
          fileRecord.scanStatus = summary ? 'scanned' : 'scanned_empty';

          allResults.push({
            fileId: fileRecord.id,
            fileName: fileRecord.name || sourceFile?.name || `Screenshot ${index + 1}`,
            text: String(data?.text || ''),
            tsv: String(data?.tsv || ''),
            parsed
          });
        }

        this.screenshotExtractionResults = allResults;
        this.screenshotExtractedText = allResults
          .map((entry) => `# ${entry.fileName}
${entry.text}`.trim())
          .filter(Boolean)
          .join('\n\n');

        this.screenshotIntake.scanState = 'complete';
        this.screenshotIntake.scanProgress = 100;
        this.screenshotIntake.currentFileName = '';
        this.screenshotIntake.statusMessage = 'Screenshot reading complete.';

        this.screenshotOcrStatus = 'Screenshot reading complete.';
        this.screenshotOcrProgress = 100;
        this.screenshotIntake.engineStatus = 'ready';

        this.setFlash(
          `Local screenshot read found ${txSet.size} transaction hash${txSet.size === 1 ? '' : 'es'}, ${addressSet.size} address${addressSet.size === 1 ? '' : 'es'}, and flagged ${fragmentCount} truncated or unclear fragment${fragmentCount === 1 ? '' : 's'} for review.`
        );
      } catch (error) {
        console.error('CasePrepBuddy readScreenshotsLocally failed', error);
        this.screenshotIntake.scanState = 'error';
        this.screenshotIntake.statusMessage = 'Could not read the screenshots locally.';
        this.screenshotIntake.engineStatus = 'error';
        this.screenshotOcrError = 'Could not read the screenshots locally.';
        this.screenshotOcrStatus = '';
        const currentName = this.screenshotIntake.currentFileName;
        if (currentName) {
          const currentRecord = files.find((entry) => entry.name === currentName);
          if (currentRecord) {
            currentRecord.scanStatus = 'failed';
          }
        }
        this.setError('Could not read the screenshots locally. You can still paste copied text or save this source for later review.');
      } finally {
        this.screenshotOcrReading = false;
      }
    },
    async handleScreenshotSourceSubmit() {
      if (!this.prepRun) return;

      this.applyPastedScreenshotText({ quiet: true });

      const form = this.placeholderForms.screenshots;
      const hasMeaningfulInput = Boolean(
        form.note.trim()
        || form.knownTransactionsText.trim()
        || form.extractedAddressesText.trim()
        || form.manualExtractedText.trim()
        || this.screenshotIntake.files.length
      );

      if (!hasMeaningfulInput) {
        this.setError('Add a note, attach a screenshot, or paste extracted text before saving this source.');
        return;
      }

      const parsedTransactions = parseTransactionInput(form.knownTransactionsText);
      const parsedAddresses = parseAddressInput(form.extractedAddressesText);
      const screenshotCount = this.screenshotIntake.files.length;

      const sourceItem = this.buildSourceItem({
        type: 'screenshots',
        title: screenshotCount
          ? `Screenshot intake: ${screenshotCount} image${screenshotCount === 1 ? '' : 's'}`
          : 'Screenshot intake',
        payload: {
          note: form.note.trim(),
          screenshotCount,
          screenshotFiles: (this.screenshotIntake.files || []).map((file) => ({
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified,
            scanStatus: file.scanStatus,
            extractionSummary: file.lastExtractionSummary || ''
          })),
          extractionStatus: this.screenshotIntake.scanState === 'complete' ? 'local_read_complete' : 'captured_only',
          extractedTransactionHashes: parsedTransactions.valid,
          invalidTransactionEntries: parsedTransactions.invalid,
          extractedAddresses: parsedAddresses.valid,
          invalidAddressEntries: parsedAddresses.invalid,
          warnings: this.screenshotIntake.warnings,
          unusableFragments: this.screenshotIntake.unusableFragments
        },
        placeholder: !parsedTransactions.valid.length,
        status: 'captured'
      });

      this.appendSourceItem(sourceItem);

      if (parsedTransactions.valid.length || parsedAddresses.valid.length) {
        const screenshotWarnings = dedupePlainStrings([
          'Extracted from screenshots with local review. Confirm carefully before adding to the prep run.',
          ...this.screenshotIntake.warnings,
          ...(this.screenshotIntake.unusableFragments.length
            ? ['Some nearby values looked truncated or ambiguous and were not auto-added.']
            : [])
        ]);

        await this.persistRun();

        this.extractionSelectionDraft = this.buildExtractionSelectionDraft({
          sourceItemId: sourceItem.id,
          sourceLabel: sourceItem.title,
          sourceType: 'screenshots',
          transactions: parsedTransactions.valid,
          invalidTransactionEntries: parsedTransactions.invalid,
          addresses: parsedAddresses.valid,
          invalidAddressEntries: parsedAddresses.invalid,
          notes: form.note.trim(),
          warnings: screenshotWarnings
        });

        this.resetScreenshotIntakeSession({ clearForm: true });
        this.moveToStep('extractedSelection', { keepMessages: true });
        return;
      }

      await this.persistRun();
      const savedCount = screenshotCount;
      this.resetScreenshotIntakeSession({ clearForm: true });
      this.setFlash(
        savedCount
          ? 'Screenshot source saved. Raw image files were kept local to this tab and were not stored in the prep run.'
          : 'Screenshot source saved in the prep run.'
      );
      this.goToRootIntake({ keepMessages: true });
    },
    selectSourceOption(option) {
      this.moveToStep(option.step);
    },
    advanceEducation() {
      this.educationLevel = Math.min(this.educationLevel + 1, this.educationStages.length - 1);
    },
   async recordEducationAndReturn() {
  if (!this.prepRun) return;

  if (!this.prepRun.metadata) {
    this.prepRun.metadata = {};
  }

  this.prepRun.metadata.helpPathViewed = true;
  this.prepRun.metadata.highestEducationLevelReached = this.educationLevel + 1;
  this.prepRun.metadata.lastHelpCompletedAt = new Date().toISOString();

  await this.persistRun();
  this.educationLevel = 0;
  this.setFlash('You’ve reviewed the basics. Try looking for a transaction hash or address hash to get started.');
  this.goToRootIntake({ keepMessages: true });
},
   appendSourceItem(sourceItem) {
  if (!this.prepRun) return;
  this.ensurePrepRunCollections(this.prepRun);
  this.prepRun.sourceItems.push(sourceItem);
  this.dismissedReturnToIntakePrompt = false;
},
    appendSuggestedLabel(suggestedLabel) {
      if (!this.prepRun) return;

      const exists = (this.prepRun.suggestedLabels || []).some(
        (entry) => entry.address === suggestedLabel.address && entry.label === suggestedLabel.label
      );

      if (!exists) {
        this.prepRun.suggestedLabels.push(suggestedLabel);
      }
    },
    updateSourceItemStatus(sourceItemId, status) {
      if (!this.prepRun) return;

      this.prepRun.sourceItems = (this.prepRun.sourceItems || []).map((sourceItem) => {
        if (sourceItem.id !== sourceItemId) return sourceItem;

        return {
          ...sourceItem,
          status,
          updatedAt: new Date().toISOString()
        };
      });
    },

buildExposureReviewSnapshot(result = {}) {
  const outgoing = Array.isArray(result?.counterparties?.outgoing)
    ? result.counterparties.outgoing
    : [];
  const incoming = Array.isArray(result?.counterparties?.incoming)
    ? result.counterparties.incoming
    : [];

  return {
    provider: 'arkham',
    status: result?.status || 'captured_only',
    fetchedAt: result?.fetchedAt || '',
    cache: {
      hit: Boolean(result?.cache?.hit),
      cacheKey: result?.cache?.cacheKey || '',
      expiresAt: result?.cache?.expiresAt || ''
    },
    target: result?.target || null,
    identity: result?.identity
      ? {
          targetKind: result.identity.targetKind || '',
          displayName: result.identity.displayName || '',
          entityId: result.identity.entityId || '',
          entityName: result.identity.entityName || '',
          entityType: result.identity.entityType || '',
          addressCount: result.identity.addressCount || null,
          totalBalanceUsd: result.identity.totalBalanceUsd || null,
          totalVolumeUsd: result.identity.totalVolumeUsd || null,
          firstTransferAt: result.identity.firstTransferAt || '',
          lastTransferAt: result.identity.lastTransferAt || '',
          tags: Array.isArray(result.identity.tags) ? result.identity.tags : [],
          chains: Array.isArray(result.identity.chains) ? result.identity.chains : []
        }
      : null,
    counterpartyCounts: {
      outgoing: outgoing.length,
      incoming: incoming.length
    },
    counterpartyPreview: {
      outgoing: outgoing.slice(0, 10).map(this.summarizeExposureCounterpartyRow),
      incoming: incoming.slice(0, 10).map(this.summarizeExposureCounterpartyRow)
    },
    warnings: Array.isArray(result?.warnings) ? result.warnings.filter(Boolean) : []
  };
},

summarizeExposureCounterpartyRow(row = {}) {
  return {
    id: row.id || '',
    displayName: row.displayName || '',
    address: row.address || '',
    entityId: row.entityId || '',
    type: row.type || '',
    tags: Array.isArray(row.tags) ? row.tags.slice(0, 8) : [],
    direction: row.direction || '',
    usd: row.usd ?? null,
    transactionCount: row.transactionCount ?? null,
    chains: Array.isArray(row.chains) ? row.chains.slice(0, 8) : []
  };
},

buildExposureReviewSourceItem(payload, status = 'captured') {
  const target = payload?.target || {};
  const targetLabel = target.displayName || target.value || 'Unknown target';
  const targetKindLabel = target.kind === 'entity' ? 'Entity' : 'Address';

  return this.buildSourceItem({
    type: 'exposure_review',
    title: `${targetKindLabel} exposure review: ${targetLabel}`,
    payload: {
      target,
      note: String(payload?.note || '').trim(),
      filters: payload?.filters || {},
      lookupSnapshot: this.buildExposureReviewSnapshot(payload?.lookupResult || {}),
      savedAt: payload?.savedAt || new Date().toISOString()
    },
    placeholder: !payload?.lookupResult,
    status
  });
},

appendExposureReviewSuggestedLabel(payload, sourceItemId) {
  const target = payload?.target || {};
  const label = String(payload?.label || '').trim();
  if (!label || target.kind !== 'address' || !target.address) return;

  this.appendSuggestedLabel(
    this.buildSuggestedLabel({
      address: normalizeAddress(target.address) || target.address,
      label,
      sourceItemId
    })
  );
},

async handleExposureReviewSourceSave(payload) {
  if (!this.prepRun) return;

  const targetValue = payload?.target?.value || payload?.target?.address || payload?.target?.entityId;
  if (!targetValue) {
    this.setError('Choose an address or entity before saving the exposure review.');
    return;
  }

  const sourceItem = this.buildExposureReviewSourceItem(payload, 'captured');
  this.appendSourceItem(sourceItem);
  this.appendExposureReviewSuggestedLabel(payload, sourceItem.id);

  await this.persistRun();
  this.setFlash('Exposure review source saved. No starting transactions were staged automatically.');
  this.goToRootIntake({ keepMessages: true });
},

extractExposureTransferTxHash(transfer = {}) {
  return String(
    transfer.txHash
      || transfer.transactionHash
      || transfer.hash
      || transfer?.raw?.txHash
      || transfer?.raw?.transactionHash
      || transfer?.raw?.hash
      || ''
  ).trim();
},

extractExposureTransferToAddress(transfer = {}) {
  const rawTo = String(
    transfer.toAddress
      || transfer.to
      || transfer?.raw?.toAddress
      || transfer?.raw?.to
      || ''
  ).trim();

  return normalizeAddress(rawTo) || rawTo;
},

extractExposureTransferNetwork(transfer = {}) {
  return String(transfer.chain || transfer.network || transfer?.raw?.chain || '').trim();
},

async handleExposureReviewTransferValidation(payload) {
  if (!this.prepRun) return;

  const selectedTransfers = Array.isArray(payload?.selectedTransfers)
    ? payload.selectedTransfers
    : [];

  if (!selectedTransfers.length) {
    this.setError('Select at least one transfer before sending it to validation.');
    return;
  }

  const sourceItem = this.buildExposureReviewSourceItem(payload, 'pending_validation');
  this.appendSourceItem(sourceItem);
  this.appendExposureReviewSuggestedLabel(payload, sourceItem.id);

  const invalidEntries = [];
  const candidates = selectedTransfers
    .map((transfer) => {
      const rawHash = this.extractExposureTransferTxHash(transfer);
      const txHash = normalizeTransactionHash(rawHash) || rawHash;

      if (!txHash) {
        invalidEntries.push({
          raw: JSON.stringify(transfer),
          reason: 'Transfer did not include a transaction hash.'
        });
        return null;
      }

      return {
        id: createClientId('candidate'),
        txHash,
        toAddress: this.extractExposureTransferToAddress(transfer),
        network: this.extractExposureTransferNetwork(transfer),
        validationState: 'unverified',
        notes: String(payload?.note || '').trim(),
        warnings: [
          'Selected from an Arkham exposure review transfer drilldown. Confirm the transfer before adding it to the prep-run output.',
          ...(Array.isArray(payload?.lookupResult?.warnings) ? payload.lookupResult.warnings : [])
        ].filter(Boolean),
        lookupStatus: 'provider_suggested',
        sourceItemId: sourceItem.id,
        provenance: [
          {
            type: 'exposure_review_transfer_drilldown',
            provider: 'arkham',
            sourceItemId: sourceItem.id,
            target: payload?.target || null,
            counterparty: payload?.selectedCounterparty || null,
            flow: payload?.selectedFlow || ''
          }
        ]
      };
    })
    .filter(Boolean);

  if (!candidates.length) {
    await this.persistRun();
    this.setError('The selected transfer rows did not include usable transaction hashes. The exposure source was saved, but no validation batch was created.');
    return;
  }

  this.createPendingValidationBatch({
    sourceItemId: sourceItem.id,
    sourceLabel: sourceItem.title,
    sourceType: 'exposure_review',
    invalidEntries,
    candidates,
    importSummary: {
      source: 'arkham_exposure_review',
      selectedTransferCount: selectedTransfers.length,
      candidateCount: candidates.length
    }
  });

  await this.persistRun();
  this.setFlash(`${candidates.length} exposure-review transfer candidate${candidates.length === 1 ? '' : 's'} sent to shared validation.`);
  this.moveToStep('validation', { keepMessages: true });
},








    async saveAddressExposureSource() {
      if (!this.prepRun) return;

      const normalizedAddress = normalizeAddress(this.forms.addressExposure.address);
      if (!normalizedAddress) {
        this.setError('Enter a valid address before saving this source.');
        return;
      }

      const sourceItem = this.buildSourceItem({
        type: 'address_exposure',
        title: `Address analysis review: ${normalizedAddress}`,
        payload: {
          address: normalizedAddress,
          note: this.forms.addressExposure.note.trim()
        },
        placeholder: true,
        status: 'captured'
      });

      this.appendSourceItem(sourceItem);

      if (this.forms.addressExposure.label.trim()) {
        this.appendSuggestedLabel(
          this.buildSuggestedLabel({
            address: normalizedAddress,
            label: this.forms.addressExposure.label,
            sourceItemId: sourceItem.id
          })
        );
      }

      await this.persistRun();
      this.forms.addressExposure = { address: '', label: '', note: '' };
      this.setFlash('Address review source saved. Live exposure lookup and key stats are still placeholder, so no starting transactions were staged yet.');
      this.goToRootIntake({ keepMessages: true });
    },
    resetAddressLookupState({ preserveOrigin = false, preserveDirection = false } = {}) {
      const originSourceItemId = preserveOrigin ? this.addressLookup.originSourceItemId : '';
      const originSourceLabel = preserveOrigin ? this.addressLookup.originSourceLabel : '';
      const direction = preserveDirection ? this.addressLookup.direction : 'outgoing';

      this.addressLookup = {
        originSourceItemId,
        originSourceLabel,
        activeAddress: '',
        direction,
        state: 'idle',
        statusMessage: '',
        errorMessage: '',
        results: [],
        currentPage: 1,
        resultsPerPage: 10,
        resultLimit: 50,
        lastLookupAddress: '',
        lastLookupDirection: '',
        lookupTriggeredAt: '',
        truncatedResultSet: false
      };

      this.syncAddressLookupActiveAddress();
    },
    syncAddressLookupActiveAddress() {
      const addresses = this.addressForwardParsedAddresses || [];

      if (!addresses.length) {
        this.addressLookup.activeAddress = '';
        return;
      }

      if (!this.addressLookup.activeAddress || !addresses.includes(this.addressLookup.activeAddress)) {
        this.addressLookup.activeAddress = addresses[0];
      }
    },
    normalizeLookupResultTimestamp(value) {
      if (value === null || value === undefined || value === '') {
        return '';
      }

      const raw = String(value).trim();
      if (!raw) {
        return '';
      }

      if (!/^-?\d+(?:\.\d+)?$/.test(raw)) {
        const parsed = Date.parse(raw);
        return Number.isFinite(parsed) ? new Date(parsed).toISOString() : '';
      }

      const numericValue = Number(raw);
      if (!Number.isFinite(numericValue) || numericValue <= 0) {
        return '';
      }

      if (numericValue > 1000000000000) {
        return new Date(numericValue).toISOString();
      }

      return new Date(numericValue * 1000).toISOString();
    },
    formatLookupTimestamp(value) {
      const normalized = this.normalizeLookupResultTimestamp(value);
      return normalized ? this.formatTimestamp(normalized) : 'Unknown';
    },
    parseLookupAmountValue(value) {
      const raw = String(value || '').replace(/,/g, '').trim();
      if (!raw) {
        return NaN;
      }

      const match = raw.match(/-?\d+(?:\.\d+)?/);
      return match ? Number(match[0]) : NaN;
    },
    parseLookupBoundaryDate(value, endOfDay = false) {
      const raw = String(value || '').trim();
      if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        return '';
      }

      return endOfDay ? `${raw}T23:59:59.999Z` : `${raw}T00:00:00.000Z`;
    },
    applyAddressLookupClientFilters(results = []) {
      const tokenFilter = String(this.forms.addressForward.tokenType || '').trim().toUpperCase();
      const minRaw = String(this.forms.addressForward.amountMin || '').trim();
      const maxRaw = String(this.forms.addressForward.amountMax || '').trim();
      const minAmount = minRaw ? Number(minRaw) : NaN;
      const maxAmount = maxRaw ? Number(maxRaw) : NaN;
      const fromBoundary = this.parseLookupBoundaryDate(this.forms.addressForward.dateFrom, false);
      const toBoundary = this.parseLookupBoundaryDate(this.forms.addressForward.dateTo, true);
      const fromMs = fromBoundary ? Date.parse(fromBoundary) : NaN;
      const toMs = toBoundary ? Date.parse(toBoundary) : NaN;

      return (Array.isArray(results) ? results : []).filter((entry) => {
        if (tokenFilter && String(entry?.token || '').trim().toUpperCase() !== tokenFilter) {
          return false;
        }

        const amountValue = Number(entry?.amountValue);
        if (Number.isFinite(minAmount) && (!Number.isFinite(amountValue) || amountValue < minAmount)) {
          return false;
        }
        if (Number.isFinite(maxAmount) && (!Number.isFinite(amountValue) || amountValue > maxAmount)) {
          return false;
        }

        const timestampMs = entry?.timestampIso ? Date.parse(entry.timestampIso) : NaN;
        if (Number.isFinite(fromMs) && (!Number.isFinite(timestampMs) || timestampMs < fromMs)) {
          return false;
        }
        if (Number.isFinite(toMs) && (!Number.isFinite(timestampMs) || timestampMs > toMs)) {
          return false;
        }

        return true;
      });
    },
    async lookupTransactionsForActiveAddress({ quiet = false } = {}) {
      const addresses = this.addressForwardParsedAddresses || [];
      if (!addresses.length) {
        this.setError('Enter at least one valid address before looking up transactions.');
        return;
      }

      this.syncAddressLookupActiveAddress();
      const address = this.addressLookup.activeAddress || addresses[0];
      if (!address) {
        this.setError('Choose an address to inspect first.');
        return;
      }

      this.addressLookup.state = 'loading';
      this.addressLookup.errorMessage = '';
      this.addressLookup.statusMessage = 'Looking up matching transactions...';
      this.addressLookup.currentPage = 1;

      try {
        const response = await fetch('/api/etherscan/lookup_address_for_transaction_candidates_casebuddy.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            address,
            limit: this.addressLookup.resultLimit,
            direction: this.addressLookup.direction || 'outgoing',
            tokenType: String(this.forms.addressForward.tokenType || '').trim(),
            amountMin: String(this.forms.addressForward.amountMin || '').trim(),
            amountMax: String(this.forms.addressForward.amountMax || '').trim(),
            dateFrom: String(this.forms.addressForward.dateFrom || '').trim(),
            dateTo: String(this.forms.addressForward.dateTo || '').trim()
          })
        });

        let data = null;
        try {
          data = await response.json();
        } catch (parseError) {
          throw new Error('The address lookup response could not be read.');
        }

        if (!response.ok) {
          throw new Error(data?.error || 'The address lookup request failed.');
        }

        if (data && !Array.isArray(data) && data.error) {
          throw new Error(data.error);
        }

        const rawResults = Array.isArray(data)
          ? data
          : (Array.isArray(data?.results) ? data.results : []);

        const dedupe = new Set();
        const normalizedResults = rawResults
          .map((entry) => ({
            id: createClientId('addr_lookup_tx'),
            selected: true,
            hash: String(entry?.hash || '').trim(),
            timestamp: entry?.timestamp ?? '',
            timestampIso: this.normalizeLookupResultTimestamp(entry?.timestampIso || entry?.timestamp),
            amount: String(entry?.amount || '').trim(),
            amountValue: Number.isFinite(Number(entry?.amountValue))
              ? Number(entry.amountValue)
              : this.parseLookupAmountValue(entry?.amount),
            token: String(entry?.token || '').trim(),
            from: String(entry?.from || '').trim(),
            to: String(entry?.to || '').trim(),
            network: String(entry?.network || '').trim(),
            direction: String(entry?.direction || this.addressLookup.direction || 'outgoing').trim()
          }))
          .filter((entry) => {
            if (!entry.hash) {
              return false;
            }

            const key = `${entry.network}|${entry.direction}|${entry.hash}`.toLowerCase();
            if (dedupe.has(key)) {
              return false;
            }

            dedupe.add(key);
            return true;
          });

        const results = this.applyAddressLookupClientFilters(normalizedResults);

        this.addressLookup.results = results;
        this.addressLookup.state = 'loaded';
        this.addressLookup.statusMessage = results.length
          ? `Loaded ${results.length} matching transaction${results.length === 1 ? '' : 's'}.`
          : 'No matching transactions were returned for that address and filters.';
        this.addressLookup.lastLookupAddress = address;
        this.addressLookup.lastLookupDirection = this.addressLookup.direction || 'outgoing';
        this.addressLookup.lookupTriggeredAt = new Date().toISOString();
        this.addressLookup.truncatedResultSet = rawResults.length >= this.addressLookup.resultLimit;
        this.addressLookup.currentPage = 1;

        if (!results.length && !quiet) {
          this.setFlash('No matching transactions were returned for that address with the current direction.');
        }
      } catch (error) {
        const message = error?.message || 'Could not look up transactions for that address.';
        console.error('CasePrepBuddy address lookup failed', message, error);
        this.addressLookup.state = 'error';
        this.addressLookup.errorMessage = message;
        this.addressLookup.statusMessage = '';
        this.addressLookup.results = [];
        if (!quiet) {
          this.setError(message);
        }
      }
    },
    setAllAddressLookupResults(selected) {
      (this.addressLookup.results || []).forEach((result) => {
        result.selected = Boolean(selected);
      });
    },
    buildAddressLookupCandidates({ selectedResults = [], sourceItemId = '', notes = '' } = {}) {
      const timestamp = new Date().toISOString();

      return selectedResults.map((result) => {
        const rawToAddress = String(result.to || '').trim();
        const normalizedToAddress = normalizeAddress(rawToAddress);
        const warnings = rawToAddress && !normalizedToAddress
          ? ['To Address format looks unusual; kept for manual review.']
          : [];

        return {
          id: createClientId('stagedTx'),
          txHash: String(result.hash || '').trim(),
          toAddress: normalizedToAddress || rawToAddress,
          network: String(result.network || '').trim(),
          validationState: 'needs_review',
          lookupStatus: 'not_attempted',
          requiresToAddress: 'unknown',
          provenanceSourceIds: sourceItemId ? [sourceItemId] : [],
          userConfirmed: false,
          notes: String(notes || '').trim(),
          warnings,
          createdAt: timestamp,
          updatedAt: timestamp
        };
      });
    },
    async addSelectedAddressLookupTransactions() {
      if (!this.prepRun) return;

      const selectedResults = (this.addressLookup.results || []).filter((result) => result.selected);
      if (!selectedResults.length) {
        this.setError('Select at least one transaction before adding them to the prep run.');
        return;
      }

      const lookupAddress = this.addressLookup.lastLookupAddress || this.addressLookup.activeAddress;
      if (!lookupAddress) {
        this.setError('Choose an address to inspect first.');
        return;
      }

      const note = String(this.forms.addressForward.note || '').trim();
      const sourceItem = this.buildSourceItem({
        type: 'address_forward',
        title: `Forward trace from ${lookupAddress}`,
        payload: {
          addresses: [lookupAddress],
          invalidEntries: [],
          selectionScope: this.forms.addressForward.selectionScope,
          direction: this.addressLookup.lastLookupDirection || this.addressLookup.direction || 'outgoing',
          filters: this.forms.addressForward.selectionScope === 'some'
            ? {
                dateFrom: this.forms.addressForward.dateFrom.trim(),
                dateTo: this.forms.addressForward.dateTo.trim(),
                tokenType: this.forms.addressForward.tokenType.trim(),
                amountMin: this.forms.addressForward.amountMin.trim(),
                amountMax: this.forms.addressForward.amountMax.trim()
              }
            : null,
          selectedTransactionHashes: selectedResults.map((result) => result.hash),
          selectedAddresses: [lookupAddress],
          lookupCap: this.addressLookup.resultLimit,
          displayedResultCount: this.addressLookup.results.length,
          mayHaveMore: this.addressLookup.truncatedResultSet,
          originSourceItemId: this.addressLookup.originSourceItemId || '',
          note
        },
        placeholder: false,
        status: 'pending_validation'
      });

      this.appendSourceItem(sourceItem);

      if (this.addressLookup.originSourceItemId) {
        const originSource = (this.prepRun.sourceItems || []).find((entry) => entry.id === this.addressLookup.originSourceItemId);
        if (originSource) {
          originSource.payload = {
            ...(originSource.payload || {}),
            selectedAddresses: dedupePlainStrings([
              ...((originSource.payload?.selectedAddresses) || []),
              lookupAddress
            ]),
            lastAddressLookupAt: new Date().toISOString(),
            lastAddressLookupAddress: lookupAddress,
            lastAddressLookupDirection: this.addressLookup.lastLookupDirection || this.addressLookup.direction || 'outgoing'
          };
        }
      }

      const batch = this.createPendingValidationBatch({
        sourceItemId: sourceItem.id,
        sourceLabel: sourceItem.title,
        sourceType: 'address_forward',
        invalidEntries: [],
        candidates: this.buildAddressLookupCandidates({
          selectedResults,
          sourceItemId: sourceItem.id,
          notes: note
        })
      });

      await this.persistRun();

      this.setFlash(
        `Added ${selectedResults.length} transaction${selectedResults.length === 1 ? '' : 's'} from ${lookupAddress} into shared validation.`
      );

      if (batch) {
        this.moveToStep('validation', { keepMessages: true });
        return;
      }

      this.openReview({ keepMessages: true });
    },
    async handleAddressForwardSubmit() {
      if (!this.prepRun) return;

      const parsedAddresses = parseAddressInput(this.forms.addressForward.addressesText);
      if (!parsedAddresses.valid.length) {
        this.setError('Enter at least one valid address for the forward-trace intake.');
        return;
      }


      const usingNarrowingFilters = this.forms.addressForward.selectionScope === 'some';
const dateFrom = this.forms.addressForward.dateFrom.trim();
const dateTo = this.forms.addressForward.dateTo.trim();

if (dateFrom && dateTo && dateFrom > dateTo) {
  this.setError('The "To date" must be on or after the "From date".');
  return;
}

      const sourceItem = this.buildSourceItem({
        type: 'address_forward',
        title:
          parsedAddresses.valid.length === 1
            ? `Forward trace from ${parsedAddresses.valid[0]}`
            : `Forward trace from ${parsedAddresses.valid.length} addresses`,
        payload: {
          addresses: parsedAddresses.valid,
          invalidEntries: parsedAddresses.invalid,
          selectionScope: this.forms.addressForward.selectionScope,
 filters: usingNarrowingFilters
  ? {
      dateFrom,
      dateTo,
      tokenType: this.forms.addressForward.tokenType.trim(),
      amountMin: this.forms.addressForward.amountMin.trim(),
      amountMax: this.forms.addressForward.amountMax.trim()
    }
  : null,
          note: this.forms.addressForward.note.trim()
        },
        placeholder: true,
        status: 'captured'
      });

      this.appendSourceItem(sourceItem);

const parsedTransactions = usingNarrowingFilters
  ? parseTransactionInput(this.forms.addressForward.candidateTransactionsText)
  : { valid: [], invalid: [] };

      if (parsedTransactions.valid.length) {
        this.updateSourceItemStatus(sourceItem.id, 'pending_validation');
        this.createPendingValidationBatch({
          sourceItemId: sourceItem.id,
          sourceLabel: sourceItem.title,
          sourceType: 'address_forward',
          invalidEntries: parsedTransactions.invalid,
          candidates: createStagedTransactionCandidates({
            hashes: parsedTransactions.valid,
            sourceItemId: sourceItem.id,
            notes: this.forms.addressForward.note.trim()
          })
        });
        await this.persistRun();
        this.forms.addressForward = {
          addressesText: '',
          selectionScope: 'all',
  dateFrom: '',
  dateTo: '',
          tokenType: '',
          amountMin: '',
          amountMax: '',
          candidateTransactionsText: '',
          note: ''
        };
        this.moveToStep('validation');
        return;
      }

      await this.persistRun();
      this.forms.addressForward = {
        addressesText: '',
        selectionScope: 'all',

  dateFrom: '',
  dateTo: '',
        tokenType: '',
        amountMin: '',
        amountMax: '',
        candidateTransactionsText: '',
        note: ''
      };
      this.setFlash(
        'Forward-trace source saved. Use the lookup panel to review transactions from these addresses when you are ready.'
      );
      this.goToRootIntake({ keepMessages: true });
    },
    async handleTransactionSubmit() {
      if (!this.prepRun) return;

      const rawInput = this.forms.transaction.mode === 'single'
        ? this.forms.transaction.singleHash
        : this.forms.transaction.bulkHashes;

      const parsedTransactions = parseTransactionInput(rawInput);
      if (!parsedTransactions.valid.length) {
        this.setError('Enter at least one valid transaction hash before continuing.');
        return;
      }

      const rawToAddress = this.forms.transaction.commonToAddress.trim();

      const sourceItem = this.buildSourceItem({
        type: 'transaction_hashes',
        title:
          parsedTransactions.valid.length === 1
            ? `Transaction intake: ${parsedTransactions.valid[0]}`
            : `Transaction intake: ${parsedTransactions.valid.length} hashes`,
        payload: {
          rawInput,
          invalidEntries: parsedTransactions.invalid,
          mode: this.forms.transaction.mode,
          commonToAddress: rawToAddress,
          note: this.forms.transaction.note.trim()
        },
        placeholder: false,
        status: 'pending_validation'
      });

      this.appendSourceItem(sourceItem);
      this.createPendingValidationBatch({
        sourceItemId: sourceItem.id,
        sourceLabel: sourceItem.title,
        sourceType: 'transaction_hashes',
        invalidEntries: parsedTransactions.invalid,
        candidates: createStagedTransactionCandidates({
          hashes: parsedTransactions.valid,
          sourceItemId: sourceItem.id,
          commonToAddress: rawToAddress,
          notes: this.forms.transaction.note.trim()
        })
      });

      await this.persistRun();
      this.forms.transaction = {
        mode: 'single',
        singleHash: '',
        bulkHashes: '',
        commonToAddress: '',
        note: ''
      };
      this.moveToStep('validation');
    },
    async handlePlaceholderSourceSubmit() {
      if (!this.prepRun) return;

      const form = this.placeholderForms[this.currentStep];
      const sourceItem = this.buildSourceItem({
        type: this.currentStep,
        title: this.currentSourceOption.title,
        payload: {
          note: form.note.trim()
        },
        placeholder: true,
        status: 'captured'
      });

      this.appendSourceItem(sourceItem);

      const parsedTransactions = parseTransactionInput(form.knownTransactionsText);
      if (parsedTransactions.valid.length) {
        this.updateSourceItemStatus(sourceItem.id, 'pending_validation');
        this.createPendingValidationBatch({
          sourceItemId: sourceItem.id,
          sourceLabel: sourceItem.title,
          sourceType: this.currentStep,
          invalidEntries: parsedTransactions.invalid,
          candidates: createStagedTransactionCandidates({
            hashes: parsedTransactions.valid,
            sourceItemId: sourceItem.id,
            notes: form.note.trim()
          })
        });
        await this.persistRun();
        this.placeholderForms[this.currentStep] = { note: '', knownTransactionsText: '' };
        this.moveToStep('validation');
        return;
      }

      await this.persistRun();
      this.placeholderForms[this.currentStep] = { note: '', knownTransactionsText: '' };
      this.setFlash(`${this.currentSourceOption.title} was recorded as a source item in this prep run.`);
      this.goToRootIntake({ keepMessages: true });
    },
    async handleTabularImportStage(payload) {
      if (!this.prepRun) return;

      const fileName = String(payload?.fileName || '').trim();
      const sheetName = String(payload?.sheetName || '').trim();
      const sourceTitleBase = payload?.sourceType === 'spreadsheet' ? 'Spreadsheet import' : 'CSV / TSV import';
      const sourceTitle = fileName
        ? `${sourceTitleBase}: ${fileName}${sheetName ? ` — ${sheetName}` : ''}`
        : sourceTitleBase;

      const sourceItem = this.buildSourceItem({
        type: payload?.sourceType || this.currentStep,
        title: sourceTitle,
        payload: {
          fileName,
          fileSize: payload?.fileSize || 0,
          mimeType: payload?.mimeType || '',
          extension: payload?.extension || '',
          sheetName,
          note: String(payload?.note || '').trim(),
          analysis: {
            formatLabel: payload?.analysis?.format?.label || '',
            columns: payload?.analysis?.columns || null,
            candidateCount: payload?.analysis?.importSummary?.candidateCount || 0,
            readyCount: payload?.analysis?.importSummary?.readyCount || 0,
            missingHashCount: payload?.analysis?.importSummary?.missingHashCount || 0,
            withToAddressCount: payload?.analysis?.importSummary?.withToAddressCount || 0
          }
        },
        placeholder: false,
        status: 'captured'
      });

      this.appendSourceItem(sourceItem);

      const importedCandidates = buildCasePrepCandidatesFromImport({
        entries: payload?.selectedEntries || [],
        sourceItemId: sourceItem.id,
        sharedNotes: payload?.note || '',
        fileName,
        sheetName
      });

      if (importedCandidates.length) {
        this.updateSourceItemStatus(sourceItem.id, 'pending_validation');
        this.createPendingValidationBatch({
          sourceItemId: sourceItem.id,
          sourceLabel: sourceItem.title,
          sourceType: payload?.sourceType || this.currentStep,
          invalidEntries: [],
          candidates: importedCandidates,
          importSummary: payload?.analysis?.importSummary || null
        });
        await this.persistRun();
        this.moveToStep('validation');
        return;
      }

      await this.persistRun();
      this.setFlash(`${sourceItem.title} was recorded in the prep run, but no ready transaction hashes were selected yet.`);
      this.goToRootIntake({ keepMessages: true });
    },
    prettyLookupStatus(value) {
      const normalized = String(value || '').trim();
      if (normalized === 'not_attempted') return 'not checked';
      if (normalized === 'checking') return 'checking';
      if (normalized === 'validated') return 'validated';
      if (normalized === 'failed') return 'not validated';
      return normalized.replace(/_/g, ' ');
    },
    setAllValidationStates(state = 'ready') {
      if (!this.validationDraft || !Array.isArray(this.validationDraft.candidates)) return;

      this.validationDraft.candidates.forEach((candidate) => {
        candidate.validationState = state;
        candidate.updatedAt = new Date().toISOString();
      });

      this.validationDraft.updatedAt = new Date().toISOString();
      this.queueSave();
      this.setFlash(`All candidates in the active batch were marked ${state === 'ready' ? 'ready' : 'unverified but usable'}.`);
    },
    inferValidationNetwork(candidate = {}) {
      const existing = String(candidate.network || '').trim().toLowerCase();
      if (existing.includes('eth') || existing === 'evm') return 'ethereum';
      if (existing.includes('btc') || existing.includes('bitcoin')) return 'bitcoin';
      if (existing.includes('tron') || existing === 'trx') return 'tron';

      const hash = String(candidate.txHash || '').trim();
      if (/^0x[a-fA-F0-9]{64}$/.test(hash)) return 'ethereum';

      return existing || 'unknown';
    },
    getKnownPrepRunAddressSet() {
      const known = new Set();
      const addAddress = (value) => {
        const normalized = normalizeAddress(value);
        if (normalized) {
          known.add(normalized.toLowerCase());
          return;
        }

        const trimmed = String(value || '').trim();
        if (trimmed) known.add(trimmed.toLowerCase());
      };

      const sourceItems = Array.isArray(this.prepRun?.sourceItems) ? this.prepRun.sourceItems : [];
      sourceItems.forEach((source) => {
        const payload = source?.payload || {};
        addAddress(payload.address);
        (payload.addresses || []).forEach(addAddress);
        (payload.extractedAddresses || []).forEach(addAddress);
        (payload.selectedAddresses || []).forEach(addAddress);
      });

      const labels = Array.isArray(this.prepRun?.suggestedLabels) ? this.prepRun.suggestedLabels : [];
      labels.forEach((label) => addAddress(label.address));

      return known;
    },
    normalizeToAddressSuggestions(addressesFound = []) {
      const knownAddresses = this.getKnownPrepRunAddressSet();
      const seen = new Set();

      return (Array.isArray(addressesFound) ? addressesFound : [])
        .map((entry) => {
          const address = String(entry?.address || '').trim();
          if (!address) return null;

          const normalizedAddress = normalizeAddress(address) || address;
          const key = `${String(normalizedAddress).toLowerCase()}|${String(entry?.token || '')}|${String(entry?.amount || entry?.value || '')}`;
          if (seen.has(key)) return null;
          seen.add(key);

          const compareKey = String(normalizedAddress || address).toLowerCase();
          return {
            address: normalizedAddress || address,
            amount: entry?.amount ?? '',
            value: entry?.value ?? '',
            token: entry?.token || '',
            network: entry?.network || '',
            kind: entry?.kind || '',
            seenInSources: knownAddresses.has(compareKey)
          };
        })
        .filter(Boolean);
    },
    appendCandidateWarning(candidate, warning) {
      if (!candidate || !warning) return;
      candidate.warnings = Array.from(new Set([...(candidate.warnings || []), warning]));
    },
    clearCandidateWarnings(candidate, prefixes = []) {
      if (!candidate || !Array.isArray(candidate.warnings) || !prefixes.length) return;
      candidate.warnings = candidate.warnings.filter((warning) => {
        const text = String(warning || '');
        return !prefixes.some((prefix) => text.startsWith(prefix));
      });
    },
    async validateSingleValidationCandidate(candidate, { quiet = false, openPickerOnNeed = true } = {}) {
      if (!candidate) return null;

      const normalizedHash = normalizeTransactionHash(candidate.txHash);
      if (!normalizedHash) {
        this.appendCandidateWarning(candidate, 'Validation check: transaction hash format was not recognized.');
        candidate.lookupStatus = 'failed';
        candidate.validationState = 'needs_review';
        this.queueSave();
        return null;
      }

      const network = this.inferValidationNetwork(candidate);
      candidate.lookupStatus = 'checking';
      candidate.updatedAt = new Date().toISOString();
      this.validationCheck.currentCandidateId = candidate.id;

      try {
        const response = await fetch('/api/etherscan/check_to_address_needed_casebuddy.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            transaction_hash: normalizedHash,
            network,
            to_address: String(candidate.toAddress || '').trim() || null
          })
        });

        let data = null;
        try {
          data = await response.json();
        } catch (parseError) {
          throw new Error('The transaction validation response could not be read.');
        }

        if (!response.ok) {
          throw new Error(data?.error || 'The transaction validation request failed.');
        }

        this.applyValidationCheckResult(candidate, data, { openPickerOnNeed, quiet });
        this.queueSave();
        return data;
      } catch (error) {
        const message = error?.message || 'Could not validate this transaction right now.';
        candidate.lookupStatus = 'failed';
        candidate.validationState = 'unverified';
        candidate.requiresToAddress = candidate.requiresToAddress || 'unknown';
        candidate.network = candidate.network || (network === 'unknown' ? '' : network);
        candidate.updatedAt = new Date().toISOString();
        this.appendCandidateWarning(candidate, `Validation check failed: ${message}`);
        if (!quiet) this.setError(message);
        this.queueSave();
        return null;
      }
    },
    applyValidationCheckResult(candidate, data = {}, { openPickerOnNeed = true, quiet = false } = {}) {
      const normalizedNetwork = String(data.network || '').trim();
      const suggestions = this.normalizeToAddressSuggestions(data.addressesFound || []);
      const providedTo = String(candidate.toAddress || '').trim();

      this.clearCandidateWarnings(candidate, [
        'Validation check:',
        'To Address required:',
        'Provided To Address was not found',
        'Transaction was not validated'
      ]);

      candidate.toAddressSuggestions = suggestions;
      candidate.validationCheckedAt = new Date().toISOString();
      candidate.lookupStatus = data.txFound === false ? 'failed' : 'validated';
      candidate.network = normalizedNetwork && normalizedNetwork !== 'unknown'
        ? normalizedNetwork
        : (candidate.network || '');

      if (data.txFound === false) {
        candidate.validationState = 'unverified';
        candidate.requiresToAddress = 'unknown';
        this.appendCandidateWarning(
          candidate,
          `Transaction was not validated${data.reason ? `: ${data.reason}` : ' on the selected network.'}`
        );
        return;
      }

      candidate.requiresToAddress = data.requiresToAddress ? 'yes' : 'no';

      if (data.requiresToAddress) {
        if (data.matchedTo) {
          candidate.validationState = 'ready';
          this.appendCandidateWarning(candidate, 'Validation check: provided To Address was found among the transaction destinations.');
          return;
        }

        candidate.validationState = 'needs_review';
        this.appendCandidateWarning(
          candidate,
          `To Address required: ${data.reason || 'multiple possible destinations were found.'}`
        );

        if (providedTo && !data.matchedTo) {
          this.appendCandidateWarning(candidate, 'Provided To Address was not found among the returned destination options.');
        }

        if (openPickerOnNeed && suggestions.length && !quiet) {
          this.openToAddressPicker(candidate);
        }
        return;
      }

      candidate.validationState = 'ready';
      if (!providedTo) {
        candidate.toAddress = '';
      }
      if (data.reason) {
        this.appendCandidateWarning(candidate, `Validation check: ${data.reason}`);
      }
    },
    async validateActiveBatchTransactions() {
      if (!this.validationDraft || !Array.isArray(this.validationDraft.candidates) || !this.validationDraft.candidates.length) {
        this.setError('There are no candidate transactions in the active batch.');
        return;
      }

      this.validationCheck = {
        state: 'running',
        statusMessage: 'Checking candidate transactions...',
        errorMessage: '',
        checkedCount: 0,
        totalCount: this.validationDraft.candidates.length,
        currentCandidateId: ''
      };

      let failedCount = 0;
      let needsToAddressCount = 0;

      for (const candidate of this.validationDraft.candidates) {
        this.validationCheck.statusMessage = `Checking ${candidate.txHash}`;
        const result = await this.validateSingleValidationCandidate(candidate, {
          quiet: true,
          openPickerOnNeed: false
        });

        if (!result || result.txFound === false) failedCount += 1;
        if (candidate.requiresToAddress === 'yes' && !candidate.toAddress) needsToAddressCount += 1;
        this.validationCheck.checkedCount += 1;
      }

      this.validationCheck.state = failedCount ? 'complete' : 'complete';
      this.validationCheck.currentCandidateId = '';
      this.validationCheck.statusMessage = needsToAddressCount
        ? `Checked ${this.validationCheck.checkedCount} transaction${this.validationCheck.checkedCount === 1 ? '' : 's'}. ${needsToAddressCount} still need a To Address.`
        : `Checked ${this.validationCheck.checkedCount} transaction${this.validationCheck.checkedCount === 1 ? '' : 's'}.`;

      await this.persistRun();
      this.setFlash(this.validationCheck.statusMessage);
    },
    closeValidationConfirmGuard() {
      this.validationConfirmGuard.open = false;
    },
    async runChecksFromValidationConfirmGuard() {
      this.closeValidationConfirmGuard();
      await this.validateActiveBatchTransactions();
    },
    candidateRowOutcomeLabel(candidate) {
      if (!candidate || candidate.lookupStatus !== 'validated' || candidate.validationState !== 'ready') {
        return '';
      }

      if (candidate.requiresToAddress === 'no') {
        return 'Checked — no To Address needed.';
      }

      if (candidate.requiresToAddress === 'yes' && String(candidate.toAddress || '').trim()) {
        return 'Checked — To Address confirmed.';
      }

      if (String(candidate.toAddress || '').trim()) {
        return 'Checked — ready to stage.';
      }

      return 'Checked — ready to stage.';
    },
    candidateMessageClass(message = '') {
      const value = String(message || '').toLowerCase();
      if (!value) return 'candidate-message--warning';

      if (
        value.includes('provided to address was found')
        || value.includes('to address selected from transaction destinations')
        || (value.startsWith('validation check:') && !value.includes('failed') && !value.includes('not found') && !value.includes('required') && !value.includes('unusual'))
      ) {
        return 'candidate-message--positive';
      }

      if (
        value.includes('failed')
        || value.includes('not validated')
        || value.includes('not found')
      ) {
        return 'candidate-message--negative';
      }

      return 'candidate-message--warning';
    },
    copyCaseBuddyPreviewSelection(event) {
      if (!this.handoffTsv) {
        return;
      }

      if (event?.clipboardData) {
        event.preventDefault();
        event.clipboardData.setData('text/plain', this.handoffTsv);
        this.setFlash('Copied the Case Buddy TSV from the spreadsheet preview.');
        return;
      }

      this.copyText(this.handoffTsv, 'Case Buddy TSV');
    },
    sanitizeAnalysisPrefillValue(value, maxLength = 66) {
      return String(value || '')
        .replace(/[^a-zA-Z0-9]/g, '')
        .slice(0, Math.max(0, maxLength));
    },

    findValidationCandidateById(candidateId) {
      const candidates = Array.isArray(this.validationDraft?.candidates) ? this.validationDraft.candidates : [];
      return candidates.find((candidate) => candidate.id === candidateId) || null;
    },
    openToAddressPicker(candidate) {
      if (!candidate) return;
      this.toAddressPicker = {
        open: true,
        candidateId: candidate.id,
        txHash: candidate.txHash,
        suggestions: this.normalizeToAddressSuggestions(candidate.toAddressSuggestions || [])
      };
    },
    closeToAddressPicker() {
      this.toAddressPicker = {
        open: false,
        candidateId: '',
        txHash: '',
        suggestions: []
      };
    },
    chooseToAddressSuggestion(entry) {
      const candidate = this.findValidationCandidateById(this.toAddressPicker.candidateId);
      if (!candidate || !entry?.address) return;

      candidate.toAddress = entry.address;
      candidate.requiresToAddress = candidate.requiresToAddress || 'yes';
      candidate.validationState = 'ready';
      candidate.lookupStatus = candidate.lookupStatus || 'validated';
      this.clearCandidateWarnings(candidate, [
        'To Address required:',
        'Provided To Address was not found'
      ]);
      this.appendCandidateWarning(candidate, 'Validation check: To Address selected from transaction destinations.');
      candidate.updatedAt = new Date().toISOString();
      this.closeToAddressPicker();
      this.queueSave();
    },
    formatToAddressSuggestionMeta(entry = {}) {
      const parts = [];
      const amount = entry.amount !== undefined && entry.amount !== null && entry.amount !== ''
        ? entry.amount
        : entry.value;
      if (amount !== undefined && amount !== null && amount !== '') {
        parts.push(String(amount));
      }
      if (entry.token) parts.push(String(entry.token));
      if (entry.network) parts.push(String(entry.network));
      if (entry.kind) parts.push(String(entry.kind));
      return parts.join(' · ') || 'Destination option';
    },
    removeValidationCandidate(index) {
      if (!this.validationDraft) return;
      this.validationDraft.candidates.splice(index, 1);
      this.validationDraft.updatedAt = new Date().toISOString();

      if (!this.validationDraft.candidates.length) {
        if (this.validationDraft.sourceItemId) {
          this.updateSourceItemStatus(this.validationDraft.sourceItemId, 'captured');
        }
        this.removePendingValidationBatch(this.validationDraft.id);
        this.queueSave();
        return;
      }

      this.queueSave();
    },
    async discardValidationDraft() {
      if (!this.validationDraft) return;

      const discardedBatch = this.validationDraft;
      if (discardedBatch.sourceItemId) {
        this.updateSourceItemStatus(discardedBatch.sourceItemId, 'captured');
      }

      this.removePendingValidationBatch(discardedBatch.id);
      await this.persistRun();

      const remainingBatches = this.validationBatches.length;
      this.setFlash(
        remainingBatches
          ? `This candidate batch was discarded. ${remainingBatches} validation batch${remainingBatches === 1 ? '' : 'es'} still remain.`
          : 'Candidate transactions were discarded, but the source item remains in the prep run.'
      );

      if (remainingBatches) {
        this.moveToStep('validation', { keepMessages: true });
        return;
      }

      this.openReview({ keepMessages: true });
    },
    async confirmValidationDraft(options = {}) {
      if (!this.prepRun || !this.validationDraft) return;

      if (!options.skipUncheckedWarning && this.uncheckedValidationCandidateCount > 0) {
        this.validationConfirmGuard.open = true;
        return;
      }

      this.closeValidationConfirmGuard();
      const currentBatch = this.validationDraft;
      const cleanedCandidates = currentBatch.candidates
        .map((candidate) => {
          const normalizedHash = normalizeTransactionHash(candidate.txHash);
          if (!normalizedHash) return null;

          const rawToAddress = String(candidate.toAddress || '').trim();
          const normalizedToAddress = normalizeAddress(rawToAddress);
          const warnings = [...(candidate.warnings || [])];

          if (rawToAddress && !normalizedToAddress) {
            warnings.push('To Address format looks unusual; kept for manual review.');
          }

          return {
            ...candidate,
            txHash: normalizedHash,
            toAddress: normalizedToAddress || rawToAddress,
            network: String(candidate.network || '').trim(),
            validationState: candidate.validationState || 'unverified',
            lookupStatus: candidate.lookupStatus || 'not_attempted',
            requiresToAddress: candidate.requiresToAddress || 'unknown',
            toAddressSuggestions: Array.isArray(candidate.toAddressSuggestions) ? candidate.toAddressSuggestions : [],
            validationCheckedAt: candidate.validationCheckedAt || '',
            userConfirmed: true,
            warnings: Array.from(new Set(warnings)),
            updatedAt: new Date().toISOString()
          };
        })
        .filter(Boolean);

      if (!cleanedCandidates.length) {
        this.setError('There are no valid candidate transactions left to confirm.');
        return;
      }

      this.prepRun.stagedTransactions = mergeStagedTransactions(
        this.prepRun.stagedTransactions,
        cleanedCandidates
      );
      this.dismissedReturnToIntakePrompt = false;
      this.updateSourceItemStatus(currentBatch.sourceItemId, 'validated');
      this.removePendingValidationBatch(currentBatch.id);
      await this.persistRun();

      try {
        await logEvent('case_prep_transactions_confirmed', {
          count: cleanedCandidates.length,
          source_item_id: currentBatch.sourceItemId
        });
      } catch (error) {
        console.debug('CasePrepBuddy analytics failed for confirmValidationDraft', error);
      }

      const remainingBatches = this.validationBatches.length;
      this.setFlash(
        remainingBatches
          ? `${cleanedCandidates.length} candidate transaction${cleanedCandidates.length === 1 ? '' : 's'} confirmed. ${remainingBatches} validation batch${remainingBatches === 1 ? '' : 'es'} still remain.`
          : `${cleanedCandidates.length} candidate transaction${cleanedCandidates.length === 1 ? '' : 's'} confirmed and added to the prep run.`
      );

      if (remainingBatches) {
        this.moveToStep('validation', { keepMessages: true });
        return;
      }

      this.openReview({ keepMessages: true });
    },
    updateDraftTitle(title) {
      if (!this.prepRun) return;
      this.prepRun.draftTitle = title;
      this.queueSave();
    },
    async removeStagedTransaction(transactionId) {
      if (!this.prepRun) return;
      this.prepRun.stagedTransactions = (this.prepRun.stagedTransactions || []).filter(
        (transaction) => transaction.id !== transactionId
      );
      await this.persistRun();
      this.setFlash('Staged transaction removed from the prep run.');
    },
    async removeSuggestedLabel(labelId) {
      if (!this.prepRun) return;
      this.prepRun.suggestedLabels = (this.prepRun.suggestedLabels || []).filter(
        (entry) => entry.id !== labelId
      );
      await this.persistRun();
      this.setFlash('Suggested address label removed.');
    },
    normalizeLooseTransactionHashKey(value = '') {
      return (normalizeTransactionHash(value) || String(value || '').trim()).toLowerCase();
    },
    collectSourceTransactionHashKeys(source) {
      const payload = source?.payload || {};
      return new Set(
        dedupePlainStrings([
          ...((payload.selectedTransactionHashes) || []),
          ...((payload.extractedTransactionHashes) || []),
          ...((payload.addressLookupSelection) || [])
        ]).map((value) => this.normalizeLooseTransactionHashKey(value))
      );
    },
    async removeSourceItem(sourceItemId) {
      if (!this.prepRun) return;

      const sourceItem = (this.prepRun.sourceItems || []).find((entry) => entry.id === sourceItemId);
      if (!sourceItem) return;

      const confirmed = window.confirm(
        'Remove this source item? Any pending validation batches from it and any staged transactions that only came from it will also be removed.'
      );

      if (!confirmed) return;

      const remainingSources = (this.prepRun.sourceItems || []).filter((entry) => entry.id !== sourceItemId);
      const retainedHashKeys = new Set();
      remainingSources.forEach((entry) => {
        this.collectSourceTransactionHashKeys(entry).forEach((value) => retainedHashKeys.add(value));
      });

      const removedHashKeys = this.collectSourceTransactionHashKeys(sourceItem);
      let removedTransactionsCount = 0;

      this.prepRun.sourceItems = remainingSources;
      this.prepRun.pendingValidationBatches = (this.prepRun.pendingValidationBatches || []).filter((batch) => batch.sourceItemId !== sourceItemId);
      this.prepRun.suggestedLabels = (this.prepRun.suggestedLabels || []).filter((entry) => entry.sourceItemId !== sourceItemId);
      this.prepRun.stagedTransactions = (this.prepRun.stagedTransactions || []).flatMap((transaction) => {
        const provenanceSourceIds = Array.isArray(transaction.provenanceSourceIds)
          ? transaction.provenanceSourceIds.filter((id) => id !== sourceItemId)
          : [];
        const txHashKey = this.normalizeLooseTransactionHashKey(transaction.txHash);
        const ownedByRemovedSource = Array.isArray(transaction.provenanceSourceIds) && transaction.provenanceSourceIds.includes(sourceItemId);
        const shouldDropByProvenance = ownedByRemovedSource && !provenanceSourceIds.length;
        const shouldDropByHash = removedHashKeys.has(txHashKey) && !retainedHashKeys.has(txHashKey) && !provenanceSourceIds.length;

        if (shouldDropByProvenance || shouldDropByHash) {
          removedTransactionsCount += 1;
          return [];
        }

        return [{
          ...transaction,
          provenanceSourceIds,
          updatedAt: ownedByRemovedSource ? new Date().toISOString() : transaction.updatedAt
        }];
      });

      if (this.activeValidationBatchId && !(this.prepRun.pendingValidationBatches || []).some((batch) => batch.id === this.activeValidationBatchId)) {
        this.activeValidationBatchId = this.prepRun.pendingValidationBatches[0]?.id || null;
      }

      if (this.addressLookup.originSourceItemId === sourceItemId) {
        this.resetAddressLookupState();
      }

      await this.persistRun();
      this.setFlash(removedTransactionsCount
        ? `Source item removed. ${removedTransactionsCount} staged transaction${removedTransactionsCount === 1 ? '' : 's'} that only belonged to it were also removed.`
        : 'Source item removed. Related pending validation batches were cleaned up too.');
    },
    resumeStagedTransactionReview(transactionId = '') {
      if (!this.prepRun) return;

      const selected = this.stagedTransactionsNeedReview.filter((transaction) => !transactionId || transaction.id === transactionId);

      if (!selected.length) {
        this.setError('There are no staged transactions waiting for review.');
        return;
      }

      this.prepRun.pendingValidationBatches = (this.prepRun.pendingValidationBatches || []).filter(
        (batch) => batch.sourceType !== 'staged_transactions_review'
      );

      const batch = this.createPendingValidationBatch({
        sourceItemId: '',
        sourceLabel: 'Review staged transactions',
        sourceType: 'staged_transactions_review',
        invalidEntries: [],
        candidates: selected.map((transaction) => ({
          id: createClientId('stagedTxReview'),
          txHash: transaction.txHash,
          toAddress: transaction.toAddress || '',
          network: transaction.network || '',
          validationState: transaction.validationState || 'needs_review',
          lookupStatus: transaction.lookupStatus || 'not_attempted',
          requiresToAddress: transaction.requiresToAddress || 'unknown',
          provenanceSourceIds: Array.isArray(transaction.provenanceSourceIds) ? [...transaction.provenanceSourceIds] : [],
          userConfirmed: Boolean(transaction.userConfirmed),
          notes: transaction.notes || '',
          warnings: Array.isArray(transaction.warnings) ? [...transaction.warnings] : [],
          createdAt: transaction.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          validationCheckedAt: transaction.validationCheckedAt || ''
        }))
      });

      if (!batch) {
        this.setError('Could not reopen staged transactions for review.');
        return;
      }

      this.moveToStep('validation', { keepMessages: true });
      this.setFlash(
        `Reviewing ${selected.length} staged transaction${selected.length === 1 ? '' : 's'} in the shared validation step.`
      );
    },
    async copyText(text, label) {
      if (!text) {
        this.setError(`There is no ${label} to copy yet.`);
        return;
      }

      if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
        this.setError('Clipboard access is not available in this browser context.');
        return;
      }

      try {
        await navigator.clipboard.writeText(text);
        this.setFlash(`${label} copied to the clipboard.`);
      } catch (error) {
        console.debug('CasePrepBuddy copy failed', error);
        this.setError(`Could not copy the ${label}.`);
      }
    },
    sendTransactionToPathTracer(transaction) {
      const txRaw = String(transaction?.txHash || '').trim();
      const toRaw = transaction?.toAddress === '—' ? '' : String(transaction?.toAddress || '').trim();

      if (!txRaw) {
        this.setError('Choose a populated transaction row before opening PathTracer.');
        return;
      }

      const payload = {
        tx: this.sanitizeAnalysisPrefillValue(txRaw, 66),
        to: this.sanitizeAnalysisPrefillValue(toRaw, 64)
      };

      try {
        window.sessionStorage.setItem('pt:analysisPrefill', JSON.stringify(payload));
      } catch (error) {
        console.debug('CasePrepBuddy path tracer session handoff failed', error);
      }

      if (this.$router && typeof this.$router.push === 'function') {
        this.$router.push({ path: '/AnalysisTool' });
        return;
      }

      window.location.href = '/AnalysisTool';
    },
    buildCasePrepInvestigationPayload() {
      const transactions = this.stagedTransactions
        .filter((transaction) => String(transaction?.txHash || '').trim())
        .map((transaction, index) => ({
          rowNumber: index + 1,
          txHash: String(transaction.txHash || '').trim(),
          toAddress: String(transaction.toAddress || '').trim(),
          network: String(transaction.network || '').trim(),
          validationState: transaction.validationState || '',
          lookupStatus: transaction.lookupStatus || '',
          requiresToAddress: transaction.requiresToAddress || ''
        }));

      return {
        id: createClientId('investigationRequest'),
        source: 'case-prep',
        createdAt: new Date().toISOString(),
        prepRunId: this.prepRun?.id || '',
        prepRunTitle: this.prepRun?.draftTitle || '',
        transactionCount: transactions.length,
        transactions,
        tsv: this.handoffTsv,
        hashAndToAddressList: this.handoffHashAndToAddressList
      };
    },
    async openInvestigationRequestFromCasePrep() {
      if (!this.stagedTransactions.length || !String(this.handoffTsv || '').trim()) {
        this.setError('Add and confirm at least one staged transaction before requesting an investigation.');
        return;
      }

      const payload = this.buildCasePrepInvestigationPayload();

      try {
        window.sessionStorage.setItem('pt:casePrepInvestigationRequest', JSON.stringify(payload));
      } catch (error) {
        console.debug('CasePrepBuddy investigation handoff failed', error);
        this.setError('Could not prepare the investigation request page in this browser. Copy the TSV below first, then try again.');
        return;
      }

      try {
        await logEvent('case_prep_request_investigation_clicked', {
          prep_run_id: payload.prepRunId,
          transaction_count: payload.transactionCount
        });
      } catch (error) {
        console.debug('CasePrepBuddy analytics failed for investigation request', error);
      }

      const query = {
        source: 'case-prep',
        requestId: payload.id,
        txCount: String(payload.transactionCount)
      };

      if (this.$router && typeof this.$router.push === 'function') {
        this.$router.push({ name: 'RequestInvestigation', query });
        return;
      }

      const params = new URLSearchParams(query).toString();
      window.location.href = `/RequestInvestigation?${params}`;
    },
    sendPrepRunToCaseBuddy() {
      this.moveToStep('caseBuddyPreview', { keepMessages: true });
    },
    confirmCaseBuddyPlaceholderHandoff() {
      this.setFlash(
        'Direct Case Buddy handoff is coming soon. For now, keep the TSV ready and use PathTracer or the manual investigation request option from this final handoff page.'
      );
    },
    async resetRun() {
      if (!this.prepRun) return;

      const confirmed = window.confirm(
        'Reset this prep run? This will delete the local run and its staged data from the current browser.'
      );

      if (!confirmed) return;

      const currentRunId = this.prepRun.id;

   try {
  await deletePrepRun(currentRunId);
  this.prepRun = null;
  this.activeValidationBatchId = null;
  this.currentStep = 'launch';
  this.stepHistory = [];
  this.educationLevel = 0;
  this.dismissedReturnToIntakePrompt = false;
  this.resetForms();
  this.lastRunPreview = null;
  this.replaceBrowserWorkflowStep('launch');
  this.setFlash('Prep run reset. You can start a fresh run now.');
} catch (error) {
  console.debug('CasePrepBuddy reset failed', error);
  this.setError('Could not reset the prep run.');
}
    },
    prettyValidationState(value) {
      if (value === 'ready') return 'Ready';
      if (value === 'unverified') return 'Unverified';
      return 'Needs review';
    },
    prettySourceStatus(value) {
      if (value === 'validated') return 'Validated';
      if (value === 'pending_validation') return 'Pending validation';
      return 'Captured';
    },
    formatSourceType(value) {
const map = {
  education: 'Guided help',
  address_exposure: 'Address analysis review', // legacy saved runs
  exposure_review: 'Exposure review',
  address_forward: 'Address forward trace',
  transaction_hashes: 'Transaction intake',
  csv: 'CSV / TSV import',
  spreadsheet: 'Spreadsheet import',
  screenshots: 'Screenshot intake',
  document: 'Document placeholder'
};

      return map[value] || value;
    },
    normalizeExtractionSelectionItems(values = [], kind = 'transaction') {
      return (Array.isArray(values) ? values : [])
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .map((value) => ({
          id: createClientId(`extract_${kind}`),
          kind,
          value,
          selected: true
        }));
    },
    buildExtractionSelectionDraft({
      sourceItemId = '',
      sourceLabel = 'Extracted items',
      sourceType = '',
      transactions = [],
      invalidTransactionEntries = [],
      addresses = [],
      invalidAddressEntries = [],
      notes = '',
      warnings = []
    } = {}) {
      return {
        id: createClientId('extraction_review'),
        sourceItemId,
        sourceLabel,
        sourceType,
        notes: String(notes || '').trim(),
        warnings: dedupePlainStrings(warnings),
        invalidTransactionEntries: Array.isArray(invalidTransactionEntries) ? invalidTransactionEntries : [],
        invalidAddressEntries: Array.isArray(invalidAddressEntries) ? invalidAddressEntries : [],
        transactions: this.normalizeExtractionSelectionItems(transactions, 'transaction'),
        addresses: this.normalizeExtractionSelectionItems(addresses, 'address'),
        createdAt: new Date().toISOString()
      };
    },
    setExtractionSelectionAll(kind, selected) {
      if (!this.extractionSelectionDraft) return;

      const key = kind === 'address' ? 'addresses' : 'transactions';
      (this.extractionSelectionDraft[key] || []).forEach((item) => {
        item.selected = Boolean(selected);
      });
    },
    getSourceItemExtractedTransactions(source) {
      return dedupePlainStrings([
        ...((source?.payload?.selectedTransactionHashes) || []),
        ...((source?.payload?.extractedTransactionHashes) || [])
      ]);
    },
    getSourceItemExtractedAddresses(source) {
      return dedupePlainStrings([
        ...((source?.payload?.selectedAddresses) || []),
        ...((source?.payload?.extractedAddresses) || [])
      ]);
    },
    getSourceItemSelectedAddresses(source) {
      return dedupePlainStrings((source?.payload?.selectedAddresses) || []);
    },
    startAddressLookupFromSource(source) {
      if (!source) return;

      const addresses = this.getSourceItemSelectedAddresses(source);
      if (!addresses.length) {
        this.setError('There are no selected addresses on that source yet.');
        return;
      }

      this.forms.addressForward.addressesText = addresses.join('\n');
      this.forms.addressForward.note = this.forms.addressForward.note || String(source?.payload?.note || '').trim();
      this.addressLookup.originSourceItemId = source.id;
      this.addressLookup.originSourceLabel = source.title || '';
      this.addressLookup.direction = 'outgoing';
      this.syncAddressLookupActiveAddress();
      this.moveToStep('addressForward', { keepMessages: true });

      this.$nextTick(() => {
        this.lookupTransactionsForActiveAddress({ quiet: true });
      });
    },
    reopenSourcePromotion(source) {
      if (!source) return;

      const extractedTransactions = Array.isArray(source?.payload?.extractedTransactionHashes)
        ? source.payload.extractedTransactionHashes
        : [];
      const extractedAddresses = Array.isArray(source?.payload?.extractedAddresses)
        ? source.payload.extractedAddresses
        : [];

      if (!extractedTransactions.length && !extractedAddresses.length) {
        this.setError('There are no extracted items saved for that source yet.');
        return;
      }

      const selectedTransactions = new Set(source?.payload?.selectedTransactionHashes || []);
      const selectedAddresses = new Set(source?.payload?.selectedAddresses || []);
      const draft = this.buildExtractionSelectionDraft({
        sourceItemId: source.id,
        sourceLabel: source.title,
        sourceType: source.type,
        transactions: extractedTransactions,
        invalidTransactionEntries: source?.payload?.invalidTransactionEntries || [],
        addresses: extractedAddresses,
        invalidAddressEntries: source?.payload?.invalidAddressEntries || [],
        notes: source?.payload?.note || '',
        warnings: dedupePlainStrings([
          ...(source?.payload?.warnings || []),
          ...((source?.payload?.unusableFragments || []).length
            ? ['Some nearby values looked truncated or ambiguous and were not auto-added.']
            : [])
        ])
      });

      if (selectedTransactions.size) {
        draft.transactions.forEach((item) => {
          item.selected = selectedTransactions.has(item.value);
        });
      }

      if (selectedAddresses.size) {
        draft.addresses.forEach((item) => {
          item.selected = selectedAddresses.has(item.value);
        });
      }

      this.extractionSelectionDraft = draft;
      this.moveToStep('extractedSelection', { keepMessages: true });
    },
    async confirmExtractionSelection() {
      if (!this.prepRun || !this.extractionSelectionDraft) return;

      const draft = this.extractionSelectionDraft;
      const selectedTransactionHashes = draft.transactions.filter((item) => item.selected).map((item) => item.value);
      const selectedAddresses = draft.addresses.filter((item) => item.selected).map((item) => item.value);
      const sourceItem = (this.prepRun.sourceItems || []).find((entry) => entry.id === draft.sourceItemId) || null;

      if (sourceItem) {
        sourceItem.payload = {
          ...(sourceItem.payload || {}),
          extractedTransactionHashes: dedupePlainStrings([
            ...((sourceItem.payload?.extractedTransactionHashes) || []),
            ...draft.transactions.map((item) => item.value)
          ]),
          extractedAddresses: dedupePlainStrings([
            ...((sourceItem.payload?.extractedAddresses) || []),
            ...draft.addresses.map((item) => item.value)
          ]),
          selectedTransactionHashes,
          selectedAddresses,
          selectionConfirmedAt: new Date().toISOString()
        };
      }

      const existingBatchesForSource = (this.prepRun.pendingValidationBatches || []).filter(
        (batch) => batch.sourceItemId === draft.sourceItemId
      );

      if (existingBatchesForSource.length) {
        this.prepRun.pendingValidationBatches = (this.prepRun.pendingValidationBatches || []).filter(
          (batch) => batch.sourceItemId !== draft.sourceItemId
        );

        const nextBatch = this.prepRun.pendingValidationBatches[0] || null;
        this.activeValidationBatchId = nextBatch ? nextBatch.id : null;
      }

      if (!selectedTransactionHashes.length && sourceItem && sourceItem.status !== 'validated') {
        this.updateSourceItemStatus(sourceItem.id, 'captured');
      }

      let createdValidationBatch = false;

      if (selectedTransactionHashes.length) {
        if (sourceItem) {
          this.updateSourceItemStatus(sourceItem.id, 'pending_validation');
        }

        const batch = this.createPendingValidationBatch({
          sourceItemId: draft.sourceItemId,
          sourceLabel: draft.sourceLabel,
          sourceType: draft.sourceType,
          invalidEntries: draft.invalidTransactionEntries,
          candidates: createStagedTransactionCandidates({
            hashes: selectedTransactionHashes,
            sourceItemId: draft.sourceItemId,
            notes: draft.notes
          }).map((candidate) => ({
            ...candidate,
            warnings: dedupePlainStrings([
              ...(candidate.warnings || []),
              ...(draft.warnings || [])
            ])
          }))
        });

        createdValidationBatch = Boolean(batch);
      }

      await this.persistRun();
      this.extractionSelectionDraft = null;

      if (createdValidationBatch) {
        this.moveToStep('validation', { keepMessages: true });
        return;
      }

      if (selectedAddresses.length) {
        this.setFlash(
          `Saved ${selectedAddresses.length} extracted address${selectedAddresses.length === 1 ? '' : 'es'} and opened the address lookup step.`
        );
        this.forms.addressForward.addressesText = selectedAddresses.join('\n');
        this.forms.addressForward.note = draft.notes || this.forms.addressForward.note;
        this.addressLookup.originSourceItemId = draft.sourceItemId;
        this.addressLookup.originSourceLabel = draft.sourceLabel;
        this.addressLookup.direction = 'outgoing';
        this.syncAddressLookupActiveAddress();
        this.moveToStep('addressForward', { keepMessages: true });
        this.$nextTick(() => {
          this.lookupTransactionsForActiveAddress({ quiet: true });
        });
        return;
      }

      this.setFlash('Source saved. Nothing was promoted into the prep output yet.');
      this.goToRootIntake({ keepMessages: true });
    },
    skipExtractionSelection() {
      if (!this.extractionSelectionDraft) {
        this.goToRootIntake({ keepMessages: true });
        return;
      }

      this.extractionSelectionDraft = null;
      this.setFlash('Source saved. You can come back later and promote the extracted values when you are ready.');
      this.goToRootIntake({ keepMessages: true });
    },
    formatTimestamp(value) {
      if (!value) return 'Unknown';

      try {
        return new Intl.DateTimeFormat('en-CA', {
          dateStyle: 'medium',
          timeStyle: 'short'
        }).format(new Date(value));
      } catch (error) {
        return value;
      }
    },

















clearAddressForwardNarrowingFields() {
  this.forms.addressForward.dateFrom = '';
  this.forms.addressForward.dateTo = '';
  this.forms.addressForward.tokenType = '';
  this.forms.addressForward.amountMin = '';
  this.forms.addressForward.amountMax = '';
  this.forms.addressForward.candidateTransactionsText = '';
},

destroyAddressForwardDatePickers() {
  if (this.addressForwardFromDatePicker) {
    this.addressForwardFromDatePicker.destroy();
    this.addressForwardFromDatePicker = null;
  }

  if (this.addressForwardToDatePicker) {
    this.addressForwardToDatePicker.destroy();
    this.addressForwardToDatePicker = null;
  }
},

initAddressForwardDatePickers() {
  const fromInputRef = this.$refs.addressForwardFromDateInput;
  const toInputRef = this.$refs.addressForwardToDateInput;

  const fromEl = Array.isArray(fromInputRef) ? fromInputRef[0] : fromInputRef;
  const toEl = Array.isArray(toInputRef) ? toInputRef[0] : toInputRef;

  if (!fromEl || !toEl) {
    return;
  }

  this.destroyAddressForwardDatePickers();

  this.addressForwardFromDatePicker = flatpickr(fromEl, {
    enableTime: false,
    dateFormat: 'Y-m-d',
    maxDate: this.forms.addressForward.dateTo || 'today',
    minDate: '2009-01-03',
    allowInput: true,
    defaultDate: this.forms.addressForward.dateFrom || null,
    onChange: (selectedDates, dateStr) => {
      this.forms.addressForward.dateFrom = dateStr;
      if (this.addressForwardToDatePicker) {
        this.addressForwardToDatePicker.set('minDate', dateStr || '2009-01-03');
      }
    },
    onClose: (selectedDates, dateStr) => {
      this.forms.addressForward.dateFrom = dateStr || '';
    }
  });

  this.addressForwardToDatePicker = flatpickr(toEl, {
    enableTime: false,
    dateFormat: 'Y-m-d',
    maxDate: 'today',
    minDate: this.forms.addressForward.dateFrom || '2009-01-03',
    allowInput: true,
    defaultDate: this.forms.addressForward.dateTo || null,
    onChange: (selectedDates, dateStr) => {
      this.forms.addressForward.dateTo = dateStr;
      if (this.addressForwardFromDatePicker) {
        this.addressForwardFromDatePicker.set('maxDate', dateStr || 'today');
      }
    },
    onClose: (selectedDates, dateStr) => {
      this.forms.addressForward.dateTo = dateStr || '';
    }
  });
},
initializeBrowserWorkflowHistory() {
  const initialStep = this.currentStep || (this.prepRun ? 'intake' : 'launch');
  const existingState = window.history.state || {};

  // Mark the current entry as the first Case Prep Buddy workflow state.
  if (!existingState || !existingState.casePrepBuddyWorkflow) {
    window.history.replaceState(
      {
        ...existingState,
        casePrepBuddyWorkflow: true,
        workflowStep: initialStep
      },
      ''
    );
  }

  this.browserWorkflowHistoryReady = true;
},

pushBrowserWorkflowStep(step) {
  if (!this.browserWorkflowHistoryReady || this.isApplyingBrowserPopstate) {
    return;
  }

  const currentState = window.history.state || {};
  if (currentState.casePrepBuddyWorkflow && currentState.workflowStep === step) {
    return;
  }

  window.history.pushState(
    {
      ...currentState,
      casePrepBuddyWorkflow: true,
      workflowStep: step
    },
    ''
  );
},
replaceBrowserWorkflowStep(step) {
  const currentState = window.history.state || {};

  window.history.replaceState(
    {
      ...currentState,
      casePrepBuddyWorkflow: true,
      workflowStep: step
    },
    ''
  );
},
handleBrowserPopstate(event) {
  const state = event.state;

  if (!state || !state.casePrepBuddyWorkflow) {
    return;
  }

  this.isApplyingBrowserPopstate = true;

  const previousStep = this.currentStep;
  this.restoreWorkflowStepFromBrowserState(state.workflowStep, previousStep);

  this.$nextTick(() => {
    this.isApplyingBrowserPopstate = false;
  });
},

restoreWorkflowStepFromBrowserState(step, previousStep = null) {
  if (!step) {
    return;
  }

  if (
    previousStep &&
    this.stepHistory.length &&
    this.stepHistory[this.stepHistory.length - 1] === step
  ) {
    this.stepHistory.pop();
  }

  this.currentStep = step;

  if (step === 'intake') {
    this.clearMessages();
  }
},

isPlaceholderWorkflowStep(step) {
  return ['csv', 'spreadsheet', 'document'].includes(step);
},
handleKeepAddingSources() {
  this.dismissedReturnToIntakePrompt = true;
  this.clearMessages();
},

dismissFlash() {
  this.flashMessage = '';
},

dismissError() {
  this.errorMessage = '';
},
scheduleWorkflowStepScroll() {
  if (this.loading) {
    return;
  }

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
    //  this.scrollCurrentStepIntoView(this.isApplyingBrowserPopstate ? 'auto' : 'smooth');

    this.scrollCurrentStepIntoView('auto');
    });
  });
},

scrollCurrentStepIntoView(behavior = 'smooth') {
  const workspaceMain = this.$refs.workspaceMain;
  if (!workspaceMain) {
    return;
  }

  const target =
    workspaceMain.querySelector('.panel__header') ||
    workspaceMain.querySelector('section.panel') ||
    workspaceMain;

  if (!target) {
    return;
  }

  const topOffset = this.getWorkflowScrollOffset();
  const targetTop = window.scrollY + target.getBoundingClientRect().top - topOffset;

  window.scrollTo({
    top: Math.max(0, Math.round(targetTop)),
    behavior
  });
},

getWorkflowScrollOffset() {
  const navbarOffset = typeof this.noticeTopOffset === 'number'
    ? this.noticeTopOffset
    : 76;

  const extraClearance = (this.flashMessage || this.errorMessage) ? 72 : 24;

  return navbarOffset + extraClearance;
},
    requestStartNewRun() {
  this.confirmingFreshStart = true;
  this.clearMessages();
},

cancelFreshStart() {
  this.confirmingFreshStart = false;
},

async confirmStartNewRun() {
  this.confirmingFreshStart = false;
  await this.startNewRun();
},
async warmupDocumentReaders() {
  if (this.documentIntake.engineStatus === 'loading' || this.documentIntake.engineStatus === 'ready') {
    return;
  }

  this.documentIntake.engineStatus = 'loading';
  this.documentIntake.statusMessage = 'Preparing local document readers...';
  this.documentIntake.readProgress = 0;

  try {
    await warmupCasePrepDocumentReaders((progress) => {
      this.documentIntake.statusMessage = progress?.status || 'Preparing local document readers...';
      this.documentIntake.readProgress = Math.round((progress?.progress || 0) * 100);
    });

    this.documentIntake.engineStatus = 'ready';
    this.documentIntake.statusMessage = '';
    this.documentIntake.readProgress = 0;
  } catch (error) {
    const message = error?.message || 'Could not start the local document readers.';
    console.error('CasePrepBuddy warmupDocumentReaders failed', message, error);
    this.documentIntake.engineStatus = 'error';
    this.documentIntake.statusMessage = message;
    this.setError(message);
  }
},
handleDocumentDragEnter() {
  this.documentIntake.isDragging = true;
},
handleDocumentDragOver() {
  this.documentIntake.isDragging = true;
},
handleDocumentDragLeave() {
  this.documentIntake.isDragging = false;
},
handleDocumentDrop(event) {
  this.documentIntake.isDragging = false;
  const files = Array.from(event?.dataTransfer?.files || []);
  this.appendDocumentFiles(files);
},
handleDocumentFileSelection(event) {
  const files = Array.from(event?.target?.files || []);
  this.appendDocumentFiles(files);

  if (event?.target) {
    event.target.value = '';
  }
},
appendDocumentFiles(files) {
  if (!Array.isArray(files) || !files.length) return;

  const additions = [];
  const existingKeys = new Set(
    (this.documentIntake.files || []).map((item) => `${item.name}::${item.size}::${item.lastModified}`)
  );

  for (const file of files) {
    if (!isCasePrepDocumentSupported(file)) {
      continue;
    }

    const dedupeKey = `${file.name}::${file.size}::${file.lastModified}`;
    if (existingKeys.has(dedupeKey)) {
      continue;
    }

    existingKeys.add(dedupeKey);

    const kind = getCasePrepDocumentKind(file);

    additions.push({
      id: createClientId('document_file'),
      file,
      name: file.name,
      size: Number(file.size || 0),
      type: file.type || '',
      lastModified: file.lastModified || null,
      kind,
      kindLabel: kind.toUpperCase(),
      readStatus: 'queued',
      readStatusLabel: 'Queued for local read',
      lastExtractionSummary: ''
    });
  }

  if (!additions.length) {
    this.setError('Only PDF, DOCX, or text-based documents are supported in this path right now.');
    return;
  }

  this.documentIntake.files = [...this.documentIntake.files, ...additions];
  this.documentIntake.readState = 'idle';
  this.documentIntake.readProgress = 0;
  this.documentIntake.currentFileName = '';
  this.documentIntake.statusMessage = '';
  this.setFlash(`${additions.length} document${additions.length === 1 ? '' : 's'} attached locally.`);
},
removeDocumentFile(fileId) {
  this.documentIntake.files = (this.documentIntake.files || []).filter((file) => file.id !== fileId);
},
clearDocumentFiles() {
  this.documentIntake.files = [];
  this.documentIntake.isDragging = false;
  this.documentIntake.readState = 'idle';
  this.documentIntake.readProgress = 0;
  this.documentIntake.currentFileName = '';
  this.documentIntake.statusMessage = '';
},
resetDocumentIntakeSession({ clearForm = false } = {}) {
  this.clearDocumentFiles();
  this.documentIntake.warnings = [];
  this.documentIntake.unreadableFiles = [];
  this.documentIntake.engineStatus = 'unknown';

  if (clearForm) {
    this.placeholderForms.document = {
      note: '',
      knownTransactionsText: '',
      extractedAddressesText: '',
      manualExtractedText: '',
      rawExtractedText: ''
    };
  }
},
async readDocumentsLocally() {
  const files = this.documentIntake.files || [];
  if (!files.length) {
    this.setError('Attach at least one document first.');
    return;
  }

  this.documentIntake.readState = 'preparing';
  this.documentIntake.readProgress = 0;
  this.documentIntake.currentFileName = '';
  this.documentIntake.statusMessage = 'Preparing local document read...';
  this.documentIntake.warnings = [];
  this.documentIntake.unreadableFiles = [];

  try {
    await this.warmupDocumentReaders();

    let completed = 0;
    const total = files.length;

    for (const fileRecord of files) {
      this.documentIntake.readState = 'running';
      this.documentIntake.currentFileName = fileRecord.name;
      fileRecord.readStatus = 'running';
      fileRecord.readStatusLabel = 'Reading locally';

      const result = await extractCasePrepDocument(fileRecord.file, (progress) => {
        const fileProgress = Math.round((progress?.progress || 0) * 100);
        const overall = ((completed + (fileProgress / 100)) / Math.max(total, 1)) * 100;

        this.documentIntake.readProgress = Math.max(0, Math.min(100, Math.round(overall)));
        this.documentIntake.statusMessage = progress?.status || `Reading ${fileRecord.name}...`;
        this.documentIntake.currentFileName = fileRecord.name;
      });

      const extractedText = String(result?.text || '').trim();
      const parsedTransactions = parseTransactionInput(extractedText);
      const parsedAddresses = parseAddressInput(extractedText);

   this.placeholderForms.document.knownTransactionsText = mergeUniqueLines(
  this.placeholderForms.document.knownTransactionsText,
  parsedTransactions.valid,
  normalizeTransactionHash
);

this.placeholderForms.document.extractedAddressesText = mergeUniqueLines(
  this.placeholderForms.document.extractedAddressesText,
  parsedAddresses.valid,
  normalizeAddress
);

      this.placeholderForms.document.rawExtractedText = appendRawTextBlock(
        this.placeholderForms.document.rawExtractedText,
        `# ${fileRecord.name}\n${extractedText}`
      );

      this.documentIntake.warnings = dedupePlainStrings([
        ...this.documentIntake.warnings,
        ...(result?.warnings || [])
      ]);

      fileRecord.readStatus = 'complete';
      fileRecord.readStatusLabel = 'Local read complete';
      fileRecord.lastExtractionSummary =
        `${parsedTransactions.valid.length} transaction hashes, ${parsedAddresses.valid.length} addresses`;

      completed += 1;
      this.documentIntake.readProgress = Math.round((completed / total) * 100);
    }

    this.documentIntake.readState = 'complete';
    this.documentIntake.currentFileName = '';
    this.documentIntake.statusMessage = 'Document reading complete.';
    this.setFlash('Local document reading finished. Review the extracted hashes and addresses before saving this source.');
  } catch (error) {
    const message = error?.message || 'Could not finish reading the documents locally.';
    console.error('CasePrepBuddy document read failed', message, error);
    this.documentIntake.readState = 'error';
    this.documentIntake.statusMessage = message;
    this.documentIntake.engineStatus = 'error';
    this.setError(message);
  }
},
async handleDocumentSourceSubmit() {
  if (!this.prepRun) return;

  const form = this.placeholderForms.document;
  const hasMeaningfulInput = Boolean(
    form.note.trim()
    || form.knownTransactionsText.trim()
    || form.extractedAddressesText.trim()
    || form.manualExtractedText.trim()
    || this.documentIntake.files.length
  );

  if (!hasMeaningfulInput) {
    this.setError('Add a note, attach a document, or paste extracted text before saving this source.');
    return;
  }

  const combinedText = [form.manualExtractedText, form.rawExtractedText].filter(Boolean).join('\n\n');
  const parsedTransactions = parseTransactionInput(
    mergeUniqueLines(form.knownTransactionsText, combinedText)
  );
  const parsedAddresses = parseAddressInput(
    mergeUniqueLines(form.extractedAddressesText, combinedText)
  );

  const sourceItem = this.buildSourceItem({
    type: 'document',
    title: this.documentIntake.files.length
      ? `Document intake: ${this.documentIntake.files.length} file${this.documentIntake.files.length === 1 ? '' : 's'}`
      : 'Document intake',
    payload: {
      note: form.note.trim(),
      files: (this.documentIntake.files || []).map((file) => ({
        name: file.name,
        size: file.size,
        type: file.type,
        kind: file.kind,
        readStatus: file.readStatus,
        extractionSummary: file.lastExtractionSummary || ''
      })),
      extractedTransactionHashes: parsedTransactions.valid,
      invalidTransactionEntries: parsedTransactions.invalid,
      extractedAddresses: parsedAddresses.valid,
      invalidAddressEntries: parsedAddresses.invalid,
      warnings: this.documentIntake.warnings
    },
    placeholder: !parsedTransactions.valid.length,
    status: 'captured'
  });

  this.appendSourceItem(sourceItem);

  if (parsedTransactions.valid.length || parsedAddresses.valid.length) {
    await this.persistRun();

    this.extractionSelectionDraft = this.buildExtractionSelectionDraft({
      sourceItemId: sourceItem.id,
      sourceLabel: sourceItem.title,
      sourceType: 'document',
      transactions: parsedTransactions.valid,
      invalidTransactionEntries: parsedTransactions.invalid,
      addresses: parsedAddresses.valid,
      invalidAddressEntries: parsedAddresses.invalid,
      notes: form.note.trim(),
      warnings: this.documentIntake.warnings
    });

    this.resetDocumentIntakeSession({ clearForm: true });
    this.moveToStep('extractedSelection', { keepMessages: true });
    return;
  }

  await this.persistRun();
  this.resetDocumentIntakeSession({ clearForm: true });
  this.goToRootIntake({ keepMessages: true });
  this.setFlash('Document source saved. No transaction hashes or addresses were ready to promote yet.');
},
  } // end of methods
};
</script>

<style scoped>
.case-prep-buddy {
  max-width: 1260px;
  margin: 0 auto;
  padding: 1.5rem 1rem 3rem;
  display: grid;
  gap: 1rem;
}

.case-prep-buddy--exposure-review {
  max-width: min(1560px, calc(100vw - 2rem));
}

.case-prep-buddy--validation {
  max-width: min(1680px, calc(100vw - 2rem));
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  border-radius: 24px;
  background: linear-gradient(135deg, #eff6ff, #ffffff 55%, #f8fafc);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
}

.eyebrow {
  margin: 0 0 .35rem;
  color: #2563eb;
  font-size: .8rem;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.page-header h1,
.panel__header h2,
.launch-panel h2 {
  margin: 0;
  color: #0f172a;
}

.page-header__title-button {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  transition: color .18s ease, transform .18s ease;
}

.page-header__title-button:hover,
.page-header__title-button:focus-visible {
  color: #1d4ed8;
  outline: none;
}

.page-header__title-button:active {
  transform: translateY(1px);
}

.lede {
  margin: .6rem 0 0;
  max-width: 760px;
  color: #334155;
  line-height: 1.65;
}

.page-header__badge {
  display: inline-flex;
  align-items: center;
  padding: .55rem .85rem;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.1);
  color: #1d4ed8;
  font-weight: 700;
  white-space: nowrap;
}

.alert {
  padding: .95rem 1rem;
  border-radius: 16px;
  font-weight: 600;
}

.alert--success {
  background: #ecfdf5;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.alert--error {
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.panel {
  padding: 1.35rem;
  border-radius: 22px;
  background: #ffffff;
  box-shadow: 0 18px 38px rgba(15, 23, 42, 0.08);
  display: grid;
  gap: 1rem;
}

.panel--centered {
  text-align: center;
}

.panel__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.panel__header p,
.launch-panel p {
  margin: .45rem 0 0;
  color: #475569;
  line-height: 1.65;
}

.launch-panel {
  display: grid;
  gap: 1.25rem;
}

.launch-panel__copy {
  max-width: 840px;
}

.launch-panel__actions {
  display: flex;
  flex-wrap: wrap;
  gap: .85rem;
}

.resume-card {
  padding: 1rem;
  border-radius: 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.resume-card h3,
.review-card h3,
.education-card h3 {
  margin: 0;
}

.resume-card p {
  margin: .45rem 0 0;
}

.workspace {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(320px, 380px);
  gap: 1rem;
  align-items: start;
  min-width: 0;
}

.workspace__main {
  display: grid;
  gap: 1rem;
  min-width: 0;
}

.workspace-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.workspace-toolbar__left {
  display: flex;
  flex-wrap: wrap;
  gap: .75rem;
}

.workspace-toolbar__meta {
  color: #64748b;
  font-size: .92rem;
}

.intake-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 18px;
  background: linear-gradient(180deg, #eff6ff, #f8fafc);
  border: 1px solid #dbeafe;
}

.intake-banner p {
  margin: .35rem 0 0;
  color: #475569;
}

.intake-banner__actions {
  display: flex;
  flex-wrap: wrap;
  gap: .75rem;
}

.source-grid,
.intent-grid,
.review-grid,
.handoff-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.source-grid--intake {
  width: min(100%, 900px);
  justify-self: start;
  grid-auto-rows: 1fr;
  align-items: stretch;
}

.source-grid--intake .source-card {
  width: 100%;
  min-width: 0;
  height: 100%;
  box-sizing: border-box;
  align-content: start;
}

.review-stack {
  display: grid;
  gap: 1rem;
}

.review-card--transactions,
.review-card--sources,
.review-card .field,
.handoff-grid .field {
  min-width: 0;
}

.source-card,
.intent-card {
  display: grid;
  gap: .65rem;
  padding: 1rem;
  border-radius: 18px;
  border: 1px solid #dbeafe;
  background: linear-gradient(180deg, #ffffff, #f8fbff);
  text-align: left;
  cursor: pointer;
}

.source-card h3,
.intent-card h3 {
  margin: 0;
  color: #0f172a;
}

.source-card p,
.intent-card p {
  margin: 0;
  color: #475569;
  line-height: 1.55;
}

.source-card__badge {
  display: inline-flex;
  width: fit-content;
  padding: .25rem .55rem;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.08);
  color: #1d4ed8;
  font-size: .8rem;
  font-weight: 700;
}

.education-card {
  display: grid;
  gap: .75rem;
  padding: 1rem;
  border-radius: 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.education-card__level {
  margin: 0;
  color: #1d4ed8;
  font-weight: 700;
}

.education-card p,
.education-card ul {
  margin: 0;
  color: #475569;
  line-height: 1.6;
}

.education-card ul {
  padding-left: 1.1rem;
}

.inline-glossary {
  display: flex;
  flex-wrap: wrap;
  gap: .65rem;
}

.address-review-grid {
  display: grid;
  grid-template-columns: minmax(0, 420px) minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
}

.address-review-form,
.address-analysis-summary,
.analysis-wheel-card,
.analysis-stats-card {
  min-width: 0;
}

.address-review-form {
  padding: 1rem;
  border-radius: 20px;
  background: linear-gradient(180deg, #ffffff, #f8fafc);
  border: 1px solid #e2e8f0;
}

.address-review-form__intro h3,
.address-analysis-summary h3,
.analysis-wheel-card h3,
.analysis-stats-card h3,
.analysis-lifecycle__header h4,
.address-review-callout h4 {
  margin: 0;
}

.address-review-form__intro p,
.address-review-callout p,
.address-analysis-summary__description,
.analysis-mini-card p,
.analysis-wheel-legend p,
.analysis-stats-card__header p,
.analysis-stat-card p,
.analysis-lifecycle__header p,
.analysis-lifecycle-step p {
  margin: .45rem 0 0;
  color: #475569;
  line-height: 1.55;
}

.address-review-form__notes,
.address-analysis-summary__mini-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: .85rem;
}

.address-review-callout,
.analysis-mini-card {
  padding: .9rem;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid #dbeafe;
}

.address-review-callout h4,
.analysis-mini-card strong,
.analysis-stat-card strong,
.analysis-lifecycle-step strong {
  color: #0f172a;
}

.address-analysis-summary {
  display: grid;
  gap: 1rem;
  padding: 1rem;
  border-radius: 20px;
  background: linear-gradient(135deg, #eff6ff, #ffffff 55%, #f8fafc);
  border: 1px solid #dbeafe;
}

.address-analysis-summary__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.address-analysis-summary__eyebrow,
.analysis-section-eyebrow,
.analysis-mini-card__label,
.analysis-stat-card__label {
  display: block;
  margin: 0 0 .35rem;
  color: #2563eb;
  font-size: .78rem;
  font-weight: 800;
  letter-spacing: .06em;
  text-transform: uppercase;
}

.address-analysis-summary code {
  display: block;
  margin-top: .6rem;
  padding: .7rem .8rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  overflow-wrap: anywhere;
}

.address-analysis-summary__chips {
  display: flex;
  flex-wrap: wrap;
  gap: .65rem;
}

.analysis-chip {
  display: inline-flex;
  align-items: center;
  padding: .45rem .7rem;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.08);
  color: #1e40af;
  font-size: .85rem;
  font-weight: 700;
}

.address-analysis-preview-panels {
  grid-column: 1 / -1;
  display: grid;
  gap: 1rem;
}

.address-analysis-wheel-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.analysis-wheel-card,
.analysis-stats-card {
  display: grid;
  gap: 1rem;
  padding: 1rem;
  border-radius: 20px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.analysis-wheel-card__header,
.analysis-stats-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.analysis-wheel-card__state {
  display: inline-flex;
  align-items: center;
  padding: .3rem .6rem;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: .82rem;
  font-weight: 700;
  white-space: nowrap;
}

.analysis-wheel {
  display: grid;
  place-items: center;
}

.analysis-wheel__ring {
  position: relative;
  width: min(100%, 230px);
  aspect-ratio: 1;
  border-radius: 999px;
  display: grid;
  place-items: center;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.22);
}

.analysis-wheel__ring::after {
  content: '';
  position: absolute;
  inset: 24px;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: inset 0 0 0 1px rgba(226, 232, 240, 0.9);
}

.analysis-wheel__ring--outgoing {
  background: conic-gradient(#2563eb 0 28%, #60a5fa 28% 54%, #93c5fd 54% 78%, #dbeafe 78% 100%);
}

.analysis-wheel__ring--incoming {
  background: conic-gradient(#0f766e 0 24%, #14b8a6 24% 50%, #5eead4 50% 78%, #ccfbf1 78% 100%);
}

.analysis-wheel__center {
  position: relative;
  z-index: 1;
  display: grid;
  justify-items: center;
  gap: .35rem;
  padding: 0 1rem;
  text-align: center;
}

.analysis-wheel__center span {
  color: #64748b;
  font-size: .82rem;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
}

.analysis-wheel__center strong {
  color: #0f172a;
  overflow-wrap: anywhere;
}

.analysis-wheel-legend {
  list-style: none;
  display: grid;
  gap: .75rem;
  margin: 0;
  padding: 0;
}

.analysis-wheel-legend li {
  display: flex;
  align-items: flex-start;
  gap: .75rem;
}

.analysis-wheel-legend strong {
  display: block;
  color: #0f172a;
}

.legend-dot {
  flex: 0 0 auto;
  width: .75rem;
  height: .75rem;
  margin-top: .28rem;
  border-radius: 999px;
}

.legend-dot--1 { background: #2563eb; }
.legend-dot--2 { background: #60a5fa; }
.legend-dot--3 { background: #93c5fd; }
.legend-dot--4 { background: #dbeafe; }
.legend-dot--incoming-1 { background: #0f766e; }
.legend-dot--incoming-2 { background: #14b8a6; }
.legend-dot--incoming-3 { background: #5eead4; }
.legend-dot--incoming-4 { background: #ccfbf1; }

.analysis-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: .85rem;
}

.analysis-stat-card {
  padding: .9rem;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
}

.analysis-lifecycle {
  display: grid;
  gap: .85rem;
  padding-top: .25rem;
}

.analysis-lifecycle-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: .85rem;
}

.analysis-lifecycle-step {
  display: flex;
  gap: .75rem;
  padding: .9rem;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #dbeafe;
}

.analysis-lifecycle-step__number {
  flex: 0 0 auto;
  display: inline-grid;
  place-items: center;
  width: 1.85rem;
  height: 1.85rem;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  font-weight: 800;
}

.form-grid {
  display: grid;
  gap: 1rem;
}

.field {
  display: grid;
  gap: .45rem;
}

.field--full {
  grid-column: 1 / -1;
}

.field span,
.choice-group legend {
  font-weight: 700;
  color: #0f172a;
}

.field input,
.field textarea,
.tx-table input,
.tx-table select {
  width: 100%;
  padding: .85rem .95rem;
  border-radius: 14px;
  border: 1px solid #cbd5e1;
  background: #fff;
  font: inherit;
  color: #0f172a;
}

.field textarea,
.tx-table textarea {
  resize: vertical;
}

.choice-group {
  display: grid;
  gap: .65rem;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
}

.choice-group label {
  display: flex;
  align-items: center;
  gap: .5rem;
  color: #334155;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.validation-summary,
.validation-warning,
.empty-state {
  padding: 1rem;
  border-radius: 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.validation-summary p,
.validation-warning,
.empty-state p {
  margin: 0;
  color: #475569;
}

.validation-warning--strong {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: .75rem;
}

.table-scroll {
  overflow-x: auto;
}

.tx-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 760px;
}

.tx-table th,
.tx-table td {
  padding: .85rem;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
  vertical-align: top;
}

.tx-table th {
  color: #334155;
  font-size: .92rem;
}

.tx-table code,
.label-list code,
.source-item-list code {
  color: #1d4ed8;
  overflow-wrap: anywhere;
}

.warning-list {
  margin: .45rem 0 0;
  padding-left: 1rem;
  color: #b45309;
}

.review-card {
  display: grid;
  gap: .85rem;
  padding: 1rem;
  border-radius: 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  align-self: start;
  overflow: hidden;
}

.review-card__header {
  display: grid;
  gap: .5rem;
}

.review-card__header--split {
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 1rem;
}

.review-card__header h3 {
  margin: 0;
}

.review-card p {
  margin: 0;
  color: #475569;
  line-height: 1.55;
}

.handoff-card {
  margin-top: 1rem;
  overflow: hidden;
}

.handoff-grid {
  align-items: start;
}

.handoff-grid .field textarea {
  background: #ffffff;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  display: block;
}

.label-list,
.source-item-list {
  list-style: none;
  display: grid;
  gap: .75rem;
  margin: 0;
  padding: 0;
}

.label-list li,
.source-item-list li {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: stretch;
  gap: .85rem;
  padding: .85rem;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.source-item-list__content {
  min-width: 0;
  display: grid;
  gap: .2rem;
}

.label-list strong,
.source-item-list strong {
  display: block;
  margin-bottom: .25rem;
}

.source-item-list span {
  color: #64748b;
  font-size: .9rem;
}

.source-item-list__meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  gap: .5rem;
  min-width: max-content;
  text-align: right;
}

.source-item-list__meta span {
  white-space: nowrap;
}

.source-item-list p {
  margin: 0;
}

.source-item-inline-values {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: .4rem;
  margin-top: .35rem;
}

.source-item-inline-values code {
  display: inline-block;
  padding: .25rem .45rem;
  border-radius: 10px;
  background: #eff6ff;
}

.source-item-actions {
  display: flex;
  flex-wrap: wrap;
  gap: .5rem;
  margin-top: .55rem;
}

.selection-group {
  display: grid;
  gap: .85rem;
  padding: 1rem;
  border: 1px solid #dbeafe;
  border-radius: 18px;
  background: #f8fbff;
}

.selection-group__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: .85rem;
}

.selection-group__header h3 {
  margin: 0 0 .25rem;
}

.selection-group__header p {
  margin: 0;
}

.selection-list {
  list-style: none;
  display: grid;
  gap: .65rem;
  margin: 0;
  padding: 0;
}

.selection-list li {
  padding: .8rem .9rem;
  border-radius: 14px;
  border: 1px solid #dbeafe;
  background: #ffffff;
}

.selection-list__label {
  display: flex;
  align-items: flex-start;
  gap: .65rem;
}

.selection-list__label code {
  overflow-wrap: anywhere;
}

.table-actions {
  display: flex;
  flex-wrap: wrap;
  gap: .5rem;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: .25rem .55rem;
  border-radius: 999px;
  font-size: .82rem;
  font-weight: 700;
}

.status-pill--ready {
  background: #dcfce7;
  color: #166534;
}

.status-pill--unverified {
  background: #fef3c7;
  color: #92400e;
}

.status-pill--needs_review {
  background: #e0f2fe;
  color: #0369a1;
}

.status-pill--placeholder {
  background: #eff6ff;
  color: #1d4ed8;
}

.spreadsheet-preview-wrap {
  border-radius: 16px;
  border: 1px solid #dbe4f0;
  background: #ffffff;
}

.spreadsheet-preview-table {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  font-size: .92rem;
}

.spreadsheet-preview-table th,
.spreadsheet-preview-table td {
  border: 1px solid #e2e8f0;
  padding: .7rem .8rem;
  text-align: left;
  vertical-align: top;
}

.spreadsheet-preview-table thead th {
  background: #eff6ff;
  color: #1e3a8a;
  font-weight: 700;
  text-align: center;
}

.spreadsheet-preview-table__rowhead {
  width: 56px;
  background: #f8fafc;
  color: #64748b;
  text-align: center;
  font-weight: 700;
}

.spreadsheet-preview-table--with-actions {
  min-width: 840px;
}

.spreadsheet-preview-table__action-head,
.spreadsheet-preview-table__action-cell {
  width: 7.5rem;
  text-align: center;
  background: #f8fafc;
}

.spreadsheet-preview-table__action-cell {
  vertical-align: middle;
}

.spreadsheet-preview-table__action-button {
  white-space: nowrap;
}

.spreadsheet-preview-table__empty-action {
  color: #94a3b8;
}

.validation-summary--stacked {
  display: grid;
  gap: .35rem;
}

.panel__actions {
  display: flex;
  flex-wrap: wrap;
  gap: .75rem;
}

.btn {
  border: 0;
  border-radius: 14px;
  padding: .85rem 1rem;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.btn:disabled {
  opacity: .55;
  cursor: not-allowed;
}

.btn--primary {
  background: linear-gradient(180deg, #2563eb, #1d4ed8);
  color: #ffffff;
}

.btn--lookup-emphasis {
  box-shadow: 0 12px 24px rgba(37, 99, 235, 0.22);
}

.btn--ghost {
  background: #eff6ff;
  color: #1d4ed8;
}

.btn--yellow {
  background: linear-gradient(180deg, #ffe07a, #f3b61a);
  color: #111827;
  box-shadow: 0 10px 18px rgba(243, 182, 26, 0.22);
}

.btn--yellow:hover:not(:disabled) {
  background: linear-gradient(180deg, #ffe89a, #f6c233);
}

.btn--small {
  padding: .55rem .7rem;
  font-size: .9rem;
}

.btn--tiny {
  padding: .42rem .58rem;
  border-radius: 10px;
  font-size: .82rem;
}

.workspace--single-column {
  grid-template-columns: minmax(0, 1fr);
}

.workspace--exposure-review,
.workspace--validation {
  grid-template-columns: minmax(0, 1fr);
}

.workspace--exposure-review .workspace__main,
.workspace--validation .workspace__main {
  min-width: 0;
}

.panel--validation {
  gap: 1.15rem;
  overflow: visible;
}

.handoff-options {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.handoff-option {
  display: grid;
  align-content: start;
  gap: .65rem;
  padding: 1rem;
  border-radius: 18px;
  border: 1px solid #dbeafe;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.handoff-option h3 {
  margin: 0;
  color: #0f172a;
}

.handoff-option p {
  margin: 0;
  color: #475569;
  line-height: 1.5;
}

.handoff-option--investigation {
  border-color: rgba(214, 161, 10, 0.42);
  background: linear-gradient(180deg, #fffbeb, #ffffff);
}

.handoff-option--disabled {
  opacity: .72;
  background: #f8fafc;
}

.panel__actions--final-handoff {
  align-items: center;
}

.case-ready-panel {
  gap: 1rem;
}

.case-ready-banner {
  position: relative;
  overflow: hidden;
  display: grid;
  gap: 1rem;
  padding: 1.25rem;
  border-radius: 22px;
  border: 1px solid rgba(37, 99, 235, 0.18);
  background:
    radial-gradient(circle at 12% 18%, rgba(250, 204, 21, 0.32), transparent 18%),
    radial-gradient(circle at 86% 18%, rgba(236, 72, 153, 0.22), transparent 20%),
    radial-gradient(circle at 74% 82%, rgba(34, 197, 94, 0.18), transparent 22%),
    linear-gradient(135deg, #eff6ff 0%, #f8fafc 52%, #eef2ff 100%);
}

.case-ready-banner__copy h3 {
  margin: 0.2rem 0 0;
}

.case-ready-banner__copy p:last-child {
  margin-top: 0.5rem;
}

.case-ready-banner__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.case-ready-banner__chip {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(37, 99, 235, 0.12);
  color: #1e3a8a;
  font-size: 0.92rem;
  font-weight: 700;
}

.validation-status-select {
  min-width: 0;
  width: 100%;
}

.validation-network-input {
  max-width: none;
  width: 100%;
}

.table-scroll--validation {
  width: 100%;
  max-width: 100%;
  overflow-x: visible;
  overflow-y: visible;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: #ffffff;
}

.table-scroll--validation .tx-table {
  border-collapse: separate;
  border-spacing: 0;
}

.table-scroll--validation .tx-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f8fafc;
  box-shadow: 0 1px 0 #e2e8f0;
}

.table-scroll--validation .tx-table td {
  background: #ffffff;
}

.tx-table--validation {
  width: 100%;
  min-width: min(1080px, 100%);
  table-layout: fixed;
}

.validation-col--hash { width: 27%; }
.validation-col--to-address { width: 23%; }
.validation-col--network { width: 9%; }
.validation-col--status { width: 15%; }
.validation-col--notes { width: 18%; }
.validation-col--actions { width: 8%; }

.tx-table--validation th:nth-child(1),
.tx-table--validation td:nth-child(1) {
  width: 27%;
}

.tx-table--validation th:nth-child(2),
.tx-table--validation td:nth-child(2) {
  width: 23%;
}

.tx-table--validation td:nth-child(3),
.tx-table--validation th:nth-child(3) {
  width: 9%;
}

.tx-table--validation td:nth-child(4),
.tx-table--validation th:nth-child(4) {
  width: 15%;
}

.tx-table--validation th:nth-child(5),
.tx-table--validation td:nth-child(5) {
  width: 18%;
}

.tx-table--validation th:nth-child(6),
.tx-table--validation td:nth-child(6) {
  width: 8%;
}

.tx-table--validation td:first-child code {
  display: block;
  max-width: 100%;
}

.tx-table--validation input,
.tx-table--validation select {
  min-width: 0;
  box-sizing: border-box;
}

.tx-table--validation .validation-cell--actions .btn {
  justify-content: center;
  width: 100%;
}

.validation-cell--hash {
  overflow-wrap: anywhere;
}

.panel__actions--validation-footer {
  align-items: center;
  padding-top: 0.5rem;
}

.warning-list li.candidate-message--positive {
  color: #166534;
  font-weight: 700;
}

.warning-list li.candidate-message--negative {
  color: #b91c1c;
  font-weight: 700;
}

.warning-list li.candidate-message--warning {
  color: #b45309;
}

.field-help--success {
  color: #166534;
  font-weight: 700;
}

.case-modal--compact {
  max-width: 640px;
}

.spreadsheet-preview-wrap {
  user-select: text;
}

.spreadsheet-preview-table {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  background: #ffffff;
}

.spreadsheet-preview-table th,
.spreadsheet-preview-table td {
  border: 1px solid #dbe5f0;
  padding: 0.6rem 0.7rem;
  text-align: left;
  vertical-align: top;
}

.spreadsheet-preview-table__noselect {
  user-select: none;
  -webkit-user-select: none;
}

.spreadsheet-preview-table__rowhead {
  width: 3.25rem;
  background: #f8fafc;
  color: #475569;
  text-align: center;
}

.spreadsheet-preview-table__celltext {
  display: block;
  min-height: 1.25rem;
  user-select: text;
  -webkit-user-select: text;
  overflow-wrap: anywhere;
}

@media (max-width: 1100px) {
  .workspace {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1250px) {
  .address-review-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .source-grid,
  .intent-grid,
  .review-grid,
  .review-stack,
  .handoff-grid,
  .filters-grid,
  .address-analysis-wheel-grid,
  .analysis-lifecycle-grid {
    grid-template-columns: 1fr;
  }

  .source-grid--intake {
    width: 100%;
    grid-auto-rows: auto;
  }

  .analysis-stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .intake-banner,
  .workspace-toolbar,
  .address-analysis-summary__top,
  .analysis-wheel-card__header,
  .analysis-stats-card__header {
    flex-direction: column;
    align-items: stretch;
  }

  .review-card__header--split,
  .handoff-options,
  .label-list li,
  .source-item-list li {
    grid-template-columns: 1fr;
  }

  .source-item-list__meta {
    align-items: flex-start;
    min-width: 0;
    text-align: left;
  }
}

@media (max-width: 700px) {
  .address-review-form__notes,
  .address-analysis-summary__mini-grid,
  .analysis-stats-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .table-scroll--validation {
    max-height: none;
    overflow: visible;
    border: 0;
    background: transparent;
  }

  .tx-table--validation,
  .tx-table--validation colgroup,
  .tx-table--validation thead,
  .tx-table--validation tbody,
  .tx-table--validation tr,
  .tx-table--validation th,
  .tx-table--validation td {
    display: block;
  }

  .tx-table--validation colgroup,
  .tx-table--validation thead {
    display: none;
  }

  .tx-table--validation {
    min-width: 0;
  }

  .tx-table--validation tbody {
    display: grid;
    gap: 0.85rem;
  }

  .tx-table--validation tr {
    padding: 0.9rem;
    border: 1px solid #e2e8f0;
    border-radius: 18px;
    background: #ffffff;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
  }

  .table-scroll--validation .tx-table td,
  .table-scroll--validation .tx-table td:first-child {
    position: static;
    display: grid;
    grid-template-columns: minmax(8rem, 32%) minmax(0, 1fr);
    gap: 0.75rem;
    align-items: start;
    padding: 0.65rem 0;
    border-bottom: 1px solid #eef2f7;
    box-shadow: none;
  }

  .tx-table--validation td:last-child {
    border-bottom: 0;
  }

  .tx-table--validation td::before {
    content: attr(data-label);
    color: #475569;
    font-size: 0.82rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .tx-table--validation .validation-cell--actions .btn {
    justify-self: start;
    width: auto;
  }
}

@media (max-width: 640px) {
  .page-header {
    flex-direction: column;
  }

  .launch-panel__actions,
  .panel__actions,
  .workspace-toolbar__left,
  .intake-banner__actions,
  .table-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }
}


.local-processing-banner {
  padding: 1rem;
  border-radius: 18px;
  background: linear-gradient(180deg, #eff6ff, #f8fafc);
  border: 1px solid #bfdbfe;
  color: #334155;
  line-height: 1.55;
}

.local-processing-banner code {
  color: #1d4ed8;
}

.screenshot-dropzone {
  position: relative;
  padding: 1.15rem;
  border-radius: 20px;
  border: 1.5px dashed #93c5fd;
  background: linear-gradient(180deg, #ffffff, #f8fbff);
}

.screenshot-dropzone--dragging {
  border-color: #2563eb;
  background: #eff6ff;
}

.screenshot-dropzone__content {
  display: grid;
  gap: 0.75rem;
}

.screenshot-dropzone__content h3 {
  margin: 0;
  color: #0f172a;
}

.screenshot-dropzone__content p {
  margin: 0;
  color: #475569;
}

.screenshot-file-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
}

.screenshot-file-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.screenshot-file-card {
  display: grid;
  gap: 0.75rem;
  padding: 0.9rem;
  border-radius: 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.screenshot-file-card__preview {
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 14px;
  border: 1px solid #dbeafe;
  background: #ffffff;
}

.screenshot-file-card__meta {
  display: grid;
  gap: 0.25rem;
}

.screenshot-file-card__meta strong {
  color: #0f172a;
}

.screenshot-file-card__meta p,
.ocr-progress-card p {
  margin: 0;
  color: #475569;
}

.screenshot-file-card__summary {
  font-size: 0.92rem;
}

.ocr-progress-card {
  display: grid;
  gap: 0.65rem;
  padding: 1rem;
  border-radius: 18px;
  background: #f8fafc;
  border: 1px solid #dbeafe;
}

.ocr-progress-card--error {
  border-color: #fecaca;
  background: #fef2f2;
}

.ocr-progress-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.ocr-progress-card progress {
  width: 100%;
  height: 14px;
}

.ocr-raw-text {
  display: grid;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.ocr-raw-text summary {
  cursor: pointer;
  font-weight: 700;
  color: #0f172a;
}

.field-help {
  margin: 0;
  color: #475569;
  font-size: 0.95rem;
  line-height: 1.45;
}

.datepicker-input {
  cursor: pointer;
}

.notice-stack {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  width: min(680px, calc(100vw - 24px));
  display: grid;
  gap: 0.75rem;
  z-index: 2300;
  pointer-events: none;
}

.alert--floating {
  pointer-events: auto;
  box-sizing: border-box;
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: start;
  padding: 0.95rem 1rem;
  border-radius: 14px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
}

.alert__text {
  min-width: 0;
  overflow-wrap: anywhere;
}

.alert__close {
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  padding: 0.1rem 0.2rem;
  opacity: 0.75;
}

.alert__close:hover {
  opacity: 1;
}

.notice-stack-enter-active,
.notice-stack-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.notice-stack-enter-from,
.notice-stack-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.workspace__main .panel__header {
  scroll-margin-top: 120px;
}

.resume-card--featured {
  border: 1px solid rgba(37, 99, 235, 0.18);
  background: rgba(239, 246, 255, 0.9);
}

.launch-panel__actions--stacked {
  display: grid;
  gap: 0.75rem;
}

.launch-warning {
  display: grid;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 14px;
  border: 1px solid rgba(220, 38, 38, 0.18);
  background: rgba(254, 242, 242, 0.92);
}

.launch-warning h3 {
  margin: 0;
}

.launch-warning p {
  margin: 0;
}

.btn--danger {
  background: #b91c1c;
  color: #fff;
  border-color: #b91c1c;
}


.panel--flush {
  padding: 1.5rem;
}

.validation-batches {
  display: grid;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.validation-batches__label {
  margin: 0;
  font-size: 0.92rem;
  color: rgba(15, 23, 42, 0.72);
}

.validation-batches__list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.validation-batch-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 0.8rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.32);
  background: rgba(255, 255, 255, 0.92);
  color: inherit;
  cursor: pointer;
}

.validation-batch-chip strong {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.8rem;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  background: rgba(226, 232, 240, 0.9);
  font-size: 0.82rem;
}

.validation-batch-chip--active {
  border-color: rgba(37, 99, 235, 0.4);
  background: rgba(239, 246, 255, 0.96);
}

.address-lookup-panel {
  display: grid;
  gap: 1rem;
  padding: 1rem;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: rgba(248, 250, 252, 0.92);
}

.address-lookup-results {
  display: grid;
  gap: 1rem;
}

.address-lookup-panel__actions {
  justify-content: flex-start;
}

.tx-table--lookup td code {
  display: inline-block;
  max-width: 220px;
  overflow-wrap: anywhere;
}



.validation-toolbar {
  align-items: center;
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin: 1rem 0;
  padding: 1rem;
}

.validation-toolbar p {
  margin: 0.25rem 0 0;
}

.validation-toolbar__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-end;
}

.validation-row-action {
  margin-top: 0.45rem;
}

.validation-state-hints {
  color: #64748b;
  display: grid;
  font-size: 0.76rem;
  gap: 0.15rem;
  margin-top: 0.35rem;
}

.field-help--warning {
  color: #b45309;
  font-weight: 700;
}

.case-modal-backdrop {
  align-items: center;
  background: rgba(15, 23, 42, 0.56);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1rem;
  position: fixed;
  z-index: 1000;
}

.case-modal {
  background: #ffffff;
  border-radius: 22px;
  box-shadow: 0 30px 90px rgba(15, 23, 42, 0.35);
  max-height: min(720px, 92vh);
  max-width: 760px;
  overflow: auto;
  padding: 1.25rem;
  width: min(760px, 96vw);
}

.case-modal__head {
  align-items: flex-start;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.case-modal__head h3 {
  margin: 0.15rem 0 0;
}

.case-modal__body p {
  margin-top: 0;
}

.to-address-choice-list {
  display: grid;
  gap: 0.65rem;
}

.to-address-choice {
  background: #f8fafc;
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 14px;
  cursor: pointer;
  display: grid;
  gap: 0.35rem;
  padding: 0.85rem;
  text-align: left;
}

.to-address-choice:hover,
.to-address-choice:focus-visible {
  border-color: #2563eb;
  outline: none;
}

.to-address-choice--known {
  background: #eff6ff;
  border-color: #2563eb;
}

.to-address-choice span,
.to-address-choice strong {
  color: #475569;
  font-size: 0.82rem;
}

.to-address-choice strong {
  color: #1d4ed8;
}

</style>
